import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ekipAdaylariSchema,
  gozlemListesiSchema,
  gozlemSchema,
  sahaTuruDetaySchema,
  sahaTuruListesiSchema,
  sahaTuruSecenekleriSchema,
  gundemSecenekleriSchema,
  kurulDetaySchema,
  kurulListesiSchema,
  kurulSecenekleriSchema,
  toplantiDetaySchema,
  toplantiListesiSchema,
} from '@isg/validation';
import { acilDurumListesiSchema, acilDurumOzetiSchema, acilIletisimSchema } from '@isg/validation';
import { kisiKkdZimmetleriSchema, kkdZimmetListesiSchema, kkdZimmetSchema } from '@isg/validation';
import { rafBelgesiSchema, rafListesiSchema } from '@isg/validation';
import { sahaGunuYanitiSchema } from '@isg/validation';
import {
  evrakBeklentisiSchema,
  evrakPaketiMaliyetOzetiSchema,
  evrakPaketleriSchema,
} from '@isg/validation';
import type {
  FormTemplateExcelIceAktarSonucu,
  EvrakBeklentisi,
  EvrakPaketiMaliyetOzeti,
} from '@isg/validation';
import type { RafBelgesi, RafListesi, RafSekmesi } from '@isg/validation';
import { kapiDurumuSchema, listeKapilariSchema } from '@isg/validation';
import type { GorevHedefDurumu, KapiDurumu, ListeKapilari } from '@isg/validation';
import type { FormTemplateListesi, FormTemplateSurumGecmisi } from '@isg/validation';
import { ptwAileMaliyetiSchema, tenantSettingLogListesiSchema } from '@isg/validation';
import { ptwBriefingListSchema } from '@isg/validation';
import type { PtwBriefingCreateInput, PtwBriefingList, PtwKunyeSetGirdi } from '@isg/validation';
import { ISLERIM_HEDEF_HARITASI } from '@isg/validation';
import type { IslerimKaynak } from '@isg/validation';
import type {
  KisiEksik,
  PtwAileMaliyeti,
  PtwKontrolMatrisYazma,
  PtwMaliyetAilesi,
  TenantSettingLogListesi,
} from '@isg/validation';
import type {
  KisiKkdZimmetleri,
  KkdZimmet,
  KkdZimmetListesi,
  KkdZimmetOlustur,
} from '@isg/validation';
import type {
  AcilDurumListeSuzgeci,
  AcilDurumListesi,
  AcilDurumOzeti,
  AcilIletisim,
  AcilIletisimGuncelle,
  AcilIletisimOlustur,
  KontrolluBelgeOlustur,
  ToplanmaNoktasiGirdi,
} from '@isg/validation';
import type {
  EkipAdayi,
  Gozlem,
  GozlemListesi,
  GozlemOlustur,
  SahaTuruDetay,
  SahaTuruKapat,
  SahaTuruListesi,
  SahaTuruOlustur,
  SahaTuruSecenekleri,
  GundemMaddesiOlustur,
  GundemSecenekleri,
  KararDofAc,
  KararDuzelt,
  KararOlustur,
  KurulDetay,
  KurulGuncelle,
  KurulListeSuzgeci,
  KurulListesi,
  KurulOlustur,
  KurulSecenekleri,
  ToplantiDetay,
  ToplantiKapat,
  ToplantiListeSuzgeci,
  ToplantiListesi,
  ToplantiPlanla,
} from '@isg/validation';
import type {
  NonconformityCreateInput,
  NonconformityBes5Input,
  NonconformityMoveInput,
  PtwIzinSuzgeci,
  personWorkProfileSchema,
  riskBandWriteSchema,
} from '@isg/validation';
import type {
  BildirimSonucu,
  NonconformityDetail,
  NonconformityList,
  NonconformityTransitionRow,
  ActionTransitionRow,
  ActionAttachmentDetail,
  ApprovalChainDetail,
  PtwApprovalDecisionResult,
  PtwPendingApproval,
  PtwKatilimciKararBekleyen,
  PtwPermitDetail,
  PtwPermitFilterOptions,
  PtwPermitListResponse,
  PtwPermitOptions,
  PtwPermitSummary,
  PtwEquipmentScanSonucu,
  PtwQrDurum,
  PtwUygunlukSonucu,
  PtwViolationSummary,
  ActionDetail,
  ActionList,
  AppUserSummary,
  AssignableUser,
  CompanyDetail,
  ContractorDetail,
  ContractorDocumentAttachmentDetail,
  ContractorDocumentDetail,
  ContractorEkipmanSatiri,
  ContractorList,
  ContractorPersonelSatiri,
  EmploymentSummary,
  EquipmentDetail,
  EquipmentList,
  EquipmentInspectionDetail,
  FormSubmissionAttachmentDetail,
  FormSubmissionDetail,
  FormDoldurmaHedefleri,
  FormSubmissionList,
  FormTemplateSummary,
  FormTemplateVersionDetail,
  KvkkAydinlatma,
  KvkkConsentResult,
  LocationNodeSummary,
  LookupItemDetail,
  LookupListeKodu,
  MyAuthorization,
  NotificationBell,
  NotificationReadResult,
  PersonCompetencySummary,
  PendingUserSummary,
  PersonDetail,
  PersonList,
  PersonImportReport,
  PersonKimlikFull,
  RepresentativeAccountInvite,
  RoleAssignmentDetail,
  RoleType,
  TenantOverview,
  TenantSettings,
  WorkplaceSummary,
  PtwConfigOverview,
  PtwTypeForm,
  IncidentAttachmentDetail,
  IncidentDetail,
  IncidentOlayTuruUpdateResponse,
  IncidentOptions,
  IncidentSeverityConfig,
  IncidentSummary,
  WorkHoursImportReport,
  WorkHoursRow,
} from '@isg/shared-types';
import type {
  ActionCreateInput,
  ApprovalChainCreateInput,
  ApprovalChainUpdateInput,
  PtwApprovalDecisionInput,
  PtwCancelInput,
  PtwExtensionRequestInput,
  PtwPermitCreateInput,
  PtwViolationDecisionInput,
  PtwWithdrawInput,
  FormSemaTaslak,
  FormSubmissionActionCreateInput,
  FormSubmissionCreateInput,
  FormSubmissionUpdateInput,
  RepresentativeAccountCreateInput,
  IncidentAffectedUpdateInput,
  IncidentCauseUpdateInput,
  IncidentCreateInput,
  IncidentEkTipi,
  IncidentKanitKategorisi,
  SeverityLevelUpdateInput,
  SeverityRulesUpdateInput,
  WorkHoursUpsertInput,
  // F4·S5·B2 — beşinci motorun yüzeyi.
  EquipmentDurumGecisInput,
  EquipmentInspectionCreateInput,
  EquipmentDurumGecmisi,
  TaskAssignmentCreateInput,
  TaskAssignmentSummary,
  TaskAssignmentUpdateInput,
  TaskOccurrenceDetail,
  TaskOccurrenceList,
  TaskOptions,
  TaskOverdueSummary,
  // TUR 12 — dashboard.
  DashboardDonemi,
  DashboardKapsamTipi,
  DashboardKiyas,
  DashboardKpi,
  DashboardOperasyonel,
  DashboardSecenekleri,
  KpiCarpan,
  // F4·S11·B — yasal uygunluk takvimi.
  YukumlulukKalem,
  YukumlulukKalemGuncelle,
  YukumlulukKalemOlustur,
  YukumlulukTakvim,
} from '@isg/validation';
import type {
  // Tur 4½ / Aşama B — görevlendirme (§25).
  Gorevlendirme,
  GorevlendirmeListesi,
  GorevlendirmeOlustur,
  IsyeriZorunluGorevlendirmeler,
  KisiUygunlukPaneli,
  EkipmanKontrolDurumu,
  EkipmanPeriyotConfig,
  EkipmanPeriyotOnBilgi,
  EkipmanPeriyotYazInput,
  EkipmanSahipligi,
  EmploymentCloseInput,
  EmploymentCreateInput,
  EmploymentUpdateInput,
  EquipmentCreateInput,
  LocationNodeCreateInput,
  PersonCreateInput,
  PersonUpdateInput,
  WorkplaceCreateInput,
  EquipmentImportReport,
  EquipmentUpdateInput,
  GuncellemeSonucu,
  RoleScopeCreateInput,
  SilmeSonucu,
  TenantSettingAnahtar,
} from '@isg/validation';
import {
  // Tur 4½ / Aşama B — görevlendirme (§25).
  gorevlendirmeListesiSchema,
  gorevlendirmeSchema,
  isyeriZorunluGorevlendirmelerSchema,
  kisiUygunlukPaneliSchema,
  appUserSummaryListSchema,
  equipmentDurumGecmisiSchema,
  equipmentInspectionSchema,
  guncellemeSonucuSchema,
  silmeSonucuSchema,
  gorevHedefDurumuSchema,
  taskAssignmentSummarySchema,
  taskMineListSchema,
  taskOccurrenceDetailSchema,
  taskOccurrenceListSchema,
  taskOptionsSchema,
  roleAssignmentDetailSchema,
  taskOverdueSummarySchema,
  dashboardKiyasSchema,
  dashboardKpiSchema,
  dashboardOperasyonelSchema,
  dashboardSecenekleriSchema,
  yukumlulukKalemSchema,
  yukumlulukTakvimSchema,
} from '@isg/validation';
import type {
  digerKuyrukKalemSchema,
  incidentQrUretSchema,
  healthOpinionCreateSchema,
  trainingCreateSchema,
  EgitimSuzgeci,
  TrainingImportReport,
  TrainingImportPreview,
  TrainingList,
} from '@isg/validation';
import {
  actionAttachmentSchema,
  approvalChainDetailSchema,
  healthOpinionDetailSchema,
  healthOpinionOptionsSchema,
  eksikIsyeriSchema,
  eksikKisiSchema,
  healthOpinionImportReportSchema,
  healthOpinionListSchema,
  healthOpinionSummarySchema,
  kendiSaglikVadesiSchema,
  personWorkProfileDetailSchema,
  riskAssessmentDetailSchema,
  riskAssessmentListSchema,
  riskAssessmentOptionsSchema,
  riskBandListSchema,
  riskEslesmeSchema,
  ptwApprovalDecisionResultSchema,
  ptwPendingApprovalSchema,
  ptwIzinSuzgecSorgusu,
  ptwPermitDetailSchema,
  ptwPermitFilterOptionsSchema,
  ptwPermitListResponseSchema,
  ptwPermitOptionsSchema,
  ptwPermitSummarySchema,
  ptwKatilimciKararBekleyenSchema,
  ptwEquipmentScanSonucuSchema,
  ptwQrDurumSchema,
  ptwOnayUygunluguSchema,
  ptwUygunlukSonucuSchema,
  ptwViolationSummarySchema,
  actionDetailSchema,
  bildirimSonucuSchema,
  nonconformityDetailSchema,
  nonconformityListSchema,
  nonconformityTransitionSchema,
  actionTransitionSchema,
  actionListSchema,
  assignableUserSchema,
  companyDetailSchema,
  contractorDetailSchema,
  contractorDocumentAttachmentSchema,
  contractorDocumentDetailSchema,
  contractorEkipmanSatiriSchema,
  contractorListSchema,
  contractorPersonelSatiriSchema,
  employmentSummarySchema,
  equipmentDetailSchema,
  equipmentImportReportSchema,
  equipmentListSchema,
  formSubmissionAttachmentSchema,
  formSubmissionDetailSchema,
  formDoldurmaHedefleriSchema,
  formSubmissionListSchema,
  formTemplateListSchema,
  formTemplateExcelIceAktarSonucSchema,
  formTemplateSummarySchema,
  formTemplateSurumGecmisiSchema,
  formTemplateVersionDetailSchema,
  kvkkAydinlatmaSchema,
  kvkkConsentResultSchema,
  locationNodeSummarySchema,
  ekipmanPeriyotConfigSchema,
  ekipmanPeriyotOnBilgiSchema,
  lookupItemDetailSchema,
  myAuthorizationSchema,
  notificationBellSchema,
  notificationReadResultSchema,
  personCompetencySummarySchema,
  pendingUserSummarySchema,
  personDetailSchema,
  personListSchema,
  personImportPreviewSchema,
  riskItemImportReportSchema,
  personImportReportSchema,
  actionImportReportSchema,
  personKimlikFullSchema,
  representativeAccountInviteSchema,
  tenantOverviewSchema,
  tenantSettingsSchema,
  workplaceSummarySchema,
  ptwConfigOverviewSchema,
  ptwTypeFormSchema,
  incidentDetailSchema,
  incidentOlayTuruUpdateResponseSchema,
  incidentOptionsSchema,
  incidentQrSonucSchema,
  trainingDetailSchema,
  trainingImportReportSchema,
  trainingImportPreviewSchema,
  trainingListSchema,
  trainingOptionsSchema,
  trainingQrSonucSchema,
  egitimSuzgecSorgusu,
  digerKuyruguSatirSchema,
  incidentSeverityConfigSchema,
  incidentSummarySchema,
  incidentAttachmentSchema,
  workHoursImportReportSchema,
  workHoursRowSchema,
} from '@isg/validation';
import type {
  ActionImportReport,
  PersonImportPreview,
  PtwOnayUygunlugu,
  RiskItemImportReport,
} from '@isg/validation';
import { useAccessToken } from '../auth/token-context';
import {
  catismalariOku,
  catismalariTemizle,
  kuyrugaAbone,
  kuyrugaAl,
  kuyrukIstegi,
  kuyruguOku,
  senkronEt,
} from '../offline/saha-kuyrugu';
import type {
  KuyrukKaydi,
  SahaCatismasi,
  SahaKuyrukIslem,
  SenkronSonucu,
} from '../offline/saha-kuyrugu';
import {
  ApiError,
  apiDelete,
  apiDownload,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  apiUpload,
} from './client';

/**
 * Sunucu verisi kancaları (F1·S10). Tek desen: TanStack Query + şema-parse'lı
 * GET. Ekranlar API'nin bahçe filtresine GÜVENİR — burada ikinci bir
 * filtre/yetki kontrolü YAZILMAZ (Kapsam §20; karar B3: boş bahçe = boş
 * ekran + açıklayıcı mesaj). Liste şemaları z.array ile diziye sarılır.
 */

const companiesSchema = z.array(companyDetailSchema);
const workplacesSchema = z.array(workplaceSummarySchema);
const locationsSchema = z.array(locationNodeSummarySchema);
const personsSchema = z.array(personDetailSchema);
const employmentsSchema = z.array(employmentSummarySchema);
const competenciesSchema = z.array(personCompetencySummarySchema);
const equipmentDizisiSchema = z.array(equipmentDetailSchema);
const contractorsSchema = z.array(contractorDetailSchema);
const contractorDocumentsSchema = z.array(contractorDocumentDetailSchema);
const lookupListSchema = z.array(lookupItemDetailSchema);

export function useTenant(): UseQueryResult<TenantOverview> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['tenant'],
    queryFn: ({ signal }) => apiGet('/api/tenant', token, tenantOverviewSchema, signal),
  });
}

export function useMyAuthorization(): UseQueryResult<MyAuthorization> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['my-authorization'],
    queryFn: ({ signal }) => apiGet('/api/my-authorization', token, myAuthorizationSchema, signal),
  });
}

export function useCompanies(): UseQueryResult<CompanyDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['companies'],
    queryFn: ({ signal }) => apiGet('/api/companies', token, companiesSchema, signal),
  });
}

/**
 * **BAHÇEDEKİ TÜM LOKASYON DÜĞÜMLERİ — TEK İSTEK (TUR 9 / B4; T9-19).**
 * Org ağacının üçüncü kademesindeki fan-out'un yerine geçer.
 */
export function useLocationsTumu(options?: {
  enabled?: boolean;
}): UseQueryResult<LocationNodeSummary[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['locations-tumu'],
    queryFn: ({ signal }) => apiGet('/api/locations', token, locationsSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

/** Tek işyerinin lokasyon ağacı (TUR 9 / B1 — işyeri kartı). */
export function useLocations(
  workplaceId: string,
  options?: { enabled?: boolean },
): UseQueryResult<LocationNodeSummary[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['locations', workplaceId],
    queryFn: ({ signal }) =>
      apiGet(`/api/workplaces/${workplaceId}/locations`, token, locationsSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

/**
 * P13 · B1/1 — LOKASYON YAZMA KAPISI DURUMU (bayrak + sebep). Cevap uçların
 * KENDİ adım listesidir; ekran şapka listesi yazmaz, arşiv türetmez.
 */
/**
 * P13-b (madde 225) — LİSTE DÜZEYİ KAPILAR TEK UÇTAN. Kaydı olmayan görünürlük
 * koşulları (*"Yeni aksiyon"* · *"Şirket ekle"* · *"Toplu yükle"* · bölüm
 * başlıkları) buradan okunur; ekran şapka dizgesi yazmaz. Her alan ilgili
 * yazma ucunun kendi sabitinden sunucuda türetilir.
 */
export function useKapilar(): UseQueryResult<ListeKapilari> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['kapilar'],
    queryFn: ({ signal }) => apiGet('/api/kapilar', token, listeKapilariSchema, signal),
  });
}

/**
 * İKİNCİL OKUMA BAYRAĞI (sessiz 403 paketi, 29.09.2026 — madde 283).
 *
 * > **SESSİZ 403 YASAK.** Ekranın bir parçası bu şapkaya gösterilmeyecekse
 * > ekran o parçayı HİÇ ÇAĞIRMAZ ve parça çizilmez.
 *
 * Eskiden ekran ucu çağırıp 403'ü yutuyordu (*"blok sessizce çizilmez"*);
 * kullanıcı *"veri yok"* ile *"görme yetkin yok"*u ayırt edemiyordu. Bayrak
 * sunucudan gelir (`GET kapilar` — ilgili okuma ucunun kendi sabiti); ekran
 * şapka listesi yazmaz.
 *
 * `acik`: sorgu gönderilsin mi · parça çizilsin mi (İKİSİ AYNI CEVAP).
 * `bekliyor`: bayrağın kendisi henüz yüklenmedi — sayaç gibi *"her DOM anında
 * aynı sayı"* isteyen yerler bunu yükleme hâline katar.
 */
export function useIkincilOkuma(parca: keyof ListeKapilari['ikincilOkuma']): {
  acik: boolean;
  bekliyor: boolean;
} {
  const kapilar = useKapilar();
  return { acik: kapilar.data?.ikincilOkuma[parca] ?? false, bekliyor: kapilar.isPending };
}

/**
 * MADDE 226 — kişi kartı görevlendirme bölümünün kapısı: şapka → kişi bahçesi
 * → arşiv (seçili istihdamın işyeri). `POST persons/:id/designations` AYNI
 * adımları zorlar.
 */
export function useGorevlendirmeKapisi(
  personId: string,
  employmentId: string | null,
): UseQueryResult<KapiDurumu> {
  const token = useAccessToken();
  // MADDE 291 — kapı, bölümün okuma listesindeki şapkalara açıktır; bölümü
  // görmeyen şapkada (alan sorumlusu · hekim · YSP · çalışan) ÇAĞRILMAZ.
  const kapi = useIkincilOkuma('gorevlendirme');
  return useQuery({
    queryKey: ['gorevlendirme-gate', personId, employmentId],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/persons/${personId}/designations/gate${employmentId ? `?employmentId=${employmentId}` : ''}`,
        token,
        kapiDurumuSchema,
        signal,
      ),
    enabled: kapi.acik,
  });
}

export function useLokasyonKapisi(
  workplaceId: string,
  options?: { enabled?: boolean },
): UseQueryResult<KapiDurumu> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['location-gate', workplaceId],
    queryFn: ({ signal }) =>
      apiGet(`/api/workplaces/${workplaceId}/location-gate`, token, kapiDurumuSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

/**
 * P13 · B3 — EKİPMAN YAZMA KAPISI DURUMU (künye + kontrol geçmişi). Cevap
 * `PATCH equipment/:id` ve kontrol uçlarının adım listesidir.
 */
export function useEkipmanKapisi(equipmentId: string | undefined): UseQueryResult<KapiDurumu> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['equipment-gate', equipmentId],
    queryFn: ({ signal }) =>
      apiGet(`/api/equipment/${equipmentId}/write-gate`, token, kapiDurumuSchema, signal),
    enabled: Boolean(equipmentId),
  });
}

/**
 * Y3 — envanter süzgeci. `null` = "tümü"; ekran hiçbir eksende varsayılan
 * seçim taşımaz.
 */
export interface EkipmanSuzgeci {
  workplaceId: string | null;
  durum: 'aktif' | 'pasif' | null;
  tipId: string | null;
  kontrolDurumu: EkipmanKontrolDurumu | null;
  sahiplik: EkipmanSahipligi | null;
  /** ACİL DURUM / E3 (Kapsam §29): yalnız acil durum ekipmanı — tipin işareti; ikinci envanter yok. */
  acilDurum: boolean | null;
  sayfa: number;
}

export const BOS_EKIPMAN_SUZGECI: EkipmanSuzgeci = {
  workplaceId: null,
  durum: null,
  tipId: null,
  kontrolDurumu: null,
  sahiplik: null,
  acilDurum: null,
  sayfa: 1,
};

export function etkinEkipmanSuzgeciSayisi(suzgec: EkipmanSuzgeci): number {
  return [
    suzgec.workplaceId,
    suzgec.durum,
    suzgec.tipId,
    suzgec.kontrolDurumu,
    suzgec.sahiplik,
    suzgec.acilDurum,
  ].filter((deger) => deger !== null).length;
}

/**
 * ⚠️ **LİSTE SORGUSU İLE ÇIKTI SORGUSU AYNI YERDEN KURULUR (P4).** Ayrı ayrı
 * yazılsalardı ekrandaki süzgeçle dosyadaki süzgeç zamanla ayrışır ve fark
 * *"ekranda 12 vardı, dosyada 9 çıktı"* olarak, kimse bakmadan yaşardı.
 * `sayfali` yalnız listede true'dur: dosya süzgecin TAMAMINI taşır.
 */
export function ekipmanSuzgecSorgusu(suzgec: EkipmanSuzgeci, sayfali: boolean): string {
  const parametreler = new URLSearchParams();
  if (suzgec.workplaceId) parametreler.set('workplaceId', suzgec.workplaceId);
  if (suzgec.durum) parametreler.set('durum', suzgec.durum);
  if (suzgec.tipId) parametreler.set('tipId', suzgec.tipId);
  if (suzgec.kontrolDurumu) parametreler.set('kontrolDurumu', suzgec.kontrolDurumu);
  if (suzgec.sahiplik) parametreler.set('sahiplik', suzgec.sahiplik);
  if (suzgec.acilDurum) parametreler.set('acilDurum', 'true');
  if (sayfali) parametreler.set('sayfa', String(suzgec.sayfa));
  const sorgu = parametreler.toString();
  return sorgu ? `?${sorgu}` : '';
}

/**
 * **ENVANTER LİSTESİ — SUNUCUDAN SÜZÜLÜR VE SAYFALANIR (Y3, tur 4 artçısı).**
 *
 * ✏️ `useEquipmentOf` (işyeri başına paralel sorgu + istemci sayfalaması)
 * **KALKTI**: bahçenin tamamını tarayıcıya indirmek, süzgeci ve sayfalamayı
 * *"her şey yüklendikten sonra"* çalışan bir süse çeviriyordu ve envanter
 * modülün en kalabalık listesidir.
 */
export function useEkipmanEnvanteri(suzgec: EkipmanSuzgeci): UseQueryResult<EquipmentList> {
  const token = useAccessToken();
  return useQuery({
    queryKey: [
      'equipment',
      'envanter',
      suzgec.workplaceId,
      suzgec.durum,
      suzgec.tipId,
      suzgec.kontrolDurumu,
      suzgec.sahiplik,
      suzgec.acilDurum,
      suzgec.sayfa,
    ],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/equipment${ekipmanSuzgecSorgusu(suzgec, true)}`,
        token,
        equipmentListSchema,
        signal,
      ),
  });
}

/** Y1 — künye sayfasının kaynağı. */
export function useEkipmanKunyesi(equipmentId: string): UseQueryResult<EquipmentDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['equipment', 'kunye', equipmentId],
    enabled: equipmentId !== '',
    queryFn: ({ signal }) =>
      apiGet(`/api/equipment/${equipmentId}`, token, equipmentDetailSchema, signal),
  });
}

/**
 * TEK İŞYERİNİN EKİPMANLARI (Tur 3 / F1 — form doldurma ekranının nesne
 * seçicisi). Bahçe kanıt yüzeyi olan işyeri kapsamlı uçtan okur; burada tek
 * işyeri vardır ve seçim yapılmadan sorgu KOŞMAZ.
 */
export function useWorkplaceEquipment(
  workplaceId: string | null,
): UseQueryResult<EquipmentDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['equipment', workplaceId],
    enabled: workplaceId !== null,
    queryFn: ({ signal }) =>
      apiGet(`/api/workplaces/${workplaceId}/equipment`, token, equipmentDizisiSchema, signal),
  });
}

/**
 * ÇALIŞANLAR LİSTESİ (F4·S5·B1 — IA §3/1).
 *
 * `istihdam` süzgeci SUNUCUDA uygulanır: ekran kendi filtresini yazmaz
 * (§2.16 — "ekran API'ye güvenir").
 *
 * ⚠️ **VARSAYILAN BURADA, UÇTA DEĞİL:** uç süzgeçsiz döner (`hepsi`), çünkü
 * genel bir KİŞİ ucudur ve başka tüketicileri var (kişi kartı · geri getirme
 * akışı). *Süzgeci isteyen şey ekranın ADIDIR* — "Çalışanlar" başlığı aktif
 * istihdamı varsayılan yapar (IA §3/1).
 */
export function usePersons(istihdam: 'aktif' | 'hepsi' = 'aktif'): UseQueryResult<PersonDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['persons', istihdam],
    queryFn: ({ signal }) =>
      apiGet(`/api/persons?istihdam=${istihdam}`, token, personsSchema, signal),
  });
}

/**
 * **SAYFALI ÇALIŞAN LİSTESİ (TUR 9 / B4 — T9-04).** Süzgeç · arama · sayfa
 * SUNUCUDA; ekran ikinci filtre yazmaz (§2.16). Toplam GÖRÜNÜR (P4).
 */
export function usePersonsSayfali(params: {
  istihdam: 'aktif' | 'hepsi';
  /** Madde 248 / K-248 — eksik süzgeci; verilmez/null = süzgeçsiz (yalnız Çalışanlar ekranı kullanır). */
  eksik?: KisiEksik | null;
  q: string;
  sayfa: number;
  sayfaBoyutu: number;
}): UseQueryResult<PersonList> {
  const token = useAccessToken();
  const { istihdam, eksik, q, sayfa, sayfaBoyutu } = params;
  return useQuery({
    queryKey: ['persons-sayfali', istihdam, eksik ?? null, q, sayfa, sayfaBoyutu],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/persons/sayfali?istihdam=${istihdam}&sayfa=${sayfa}&sayfaBoyutu=${sayfaBoyutu}` +
          (eksik ? `&eksik=${eksik}` : '') +
          (q ? `&q=${encodeURIComponent(q)}` : ''),
        token,
        personListSchema,
        signal,
      ),
    placeholderData: (onceki) => onceki,
  });
}

/**
 * **TEKİL KİŞİ (TUR 9 / B4 — T9-03).** Kişi kartı listeyi indirip içinden
 * kendi kaydını ayıklıyordu; 400 çalışanlı bir tenant'ta bir kartı açmak 400
 * satır demekti.
 */
export function usePerson(personId: string): UseQueryResult<PersonDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['person', personId],
    queryFn: ({ signal }) => apiGet(`/api/persons/${personId}`, token, personDetailSchema, signal),
  });
}

/**
 * **BAHÇEDEKİ TÜM İŞYERLERİ — TEK İSTEK (TUR 9 / B4; T9-18·T9-19).**
 * `useWorkplacesOf`un şirket başına fan-out'unun yerine geçer: on bir ekran
 * aynı listeyi N istekle kuruyordu ve **bir isteğin sessizce boş dönmesi
 * hiçbir yerde görünmüyordu** (madde 180'in sınıfı).
 *
 * `durum: 'aktif'` — K12: SEÇİCİ arşivlileri göstermez (çalışamayacak
 * kontrol çizilmez); liste ekranı `hepsi` der.
 */
export function useWorkplacesTumu(options?: {
  companyId?: string;
  durum?: 'aktif' | 'hepsi';
}): UseQueryResult<WorkplaceSummary[]> {
  const token = useAccessToken();
  const companyId = options?.companyId;
  const durum = options?.durum ?? 'hepsi';
  const sorgu = new URLSearchParams({ durum, ...(companyId ? { companyId } : {}) });
  return useQuery({
    queryKey: ['workplaces-tumu', companyId ?? 'hepsi', durum],
    queryFn: ({ signal }) =>
      apiGet(`/api/workplaces?${sorgu.toString()}`, token, workplacesSchema, signal),
  });
}

/**
 * Tam kimlik no — YALNIZ kullanıcı "Tam göster"e bastığında çekilir (madde 37):
 * her istek sunucuda gösterim log'u yazar; o yüzden önbelleğe ALINMAZ
 * (gcTime 0 — karttan çıkınca değer düşer, yeni gösterim yeni log satırıdır).
 */
export function usePersonKimlikFull(
  personId: string,
  options: { enabled: boolean },
): UseQueryResult<PersonKimlikFull> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['kimlik-full', personId],
    queryFn: ({ signal }) =>
      apiGet(`/api/persons/${personId}/kimlik-full`, token, personKimlikFullSchema, signal),
    enabled: options.enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
}

export function useEmployments(
  personId: string,
  // ✏️ Gezinti artçısı 2 / B1 — Görevlendirmeler sekmesinin soluk satırı kişi
  // SEÇİLMEDEN istek atmaz (boş kimlikle `/persons//employments` 404'ü).
  options?: { enabled?: boolean },
): UseQueryResult<EmploymentSummary[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['employments', personId],
    queryFn: ({ signal }) =>
      apiGet(`/api/persons/${personId}/employments`, token, employmentsSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Listedeki HER kişinin istihdamları (F1.5·S12 — portal personel listesinin
 * görev kolonu). Temsilcide API satırları zaten kendi firmasıyla sınırlar —
 * ekran ikinci filtre yazmaz.
 */
export function useEmploymentsOf(
  persons: PersonDetail[] | undefined,
): UseQueryResult<EmploymentSummary[]>[] {
  const token = useAccessToken();
  return useQueries({
    queries: (persons ?? []).map((person) => ({
      queryKey: ['employments', person.id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiGet(`/api/persons/${person.id}/employments`, token, employmentsSchema, signal),
    })),
  });
}

/**
 * Toplu personel yükleme (F1.5·S12): Excel tarayıcıda satırlara çevrilir,
 * uca YALNIZ JSON satır dizisi gider (S9 sözleşmesi — dosya sunucuya gitmez).
 * Başarıda kişi/istihdam listeleri tazelenir; rapor önbelleğe alınmaz
 * (mutation zaten cache'lemez — her yükleme yeni rapor).
 */
/**
 * ✏️ **F4·S9: DOSYA ARTIK SUNUCUYA GİDİYOR** (içe aktarma motoru — S6.5).
 * Önceden Excel TARAYICIDA satırlara çevriliyor ve uca JSON satır dizisi
 * gidiyordu (madde 20 öncesi geçici karar; `lib/person-import-file.ts`
 * kendi notunda *"dosya altyapısı gelince bu modül düşer"* diyordu — düştü).
 * **Satır sözleşmesi DEĞİŞMEDİ:** JSON ucu yerinde duruyor ve dosyalı uç
 * aynı gövdeden geçiyor.
 */
export function usePersonelYukle(): UseMutationResult<
  PersonImportReport,
  Error,
  { dosya: File; contractorId: string | null }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dosya, contractorId }) =>
      apiUpload(
        '/api/import/persons/file',
        token,
        dosya,
        personImportReportSchema,
        // İşveren seçimi ŞABLONDA değil İSTEKTE taşınır (§18.2 kararı).
        contractorId ? { contractorId } : undefined,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['persons'] });
      void queryClient.invalidateQueries({ queryKey: ['employments'] });
      // TUR 6 / P1: portal personel listesi `contractors/:id/personel`den okur —
      // yükleme o listeyi de tazeler (30 sn'lik genel staleTime bayat bırakırdı).
      void queryClient.invalidateQueries({ queryKey: ['contractors'] });
    },
  });
}

/**
 * ÖNİZLEME — hiçbir şey yazmaz; kullanıcı yükleyeceği listeyi onaylamadan
 * önce görür. Parse SUNUCUDA (kolon sırasının tek yeri motordur); kimlik no
 * yanıtta zaten MASKELİ gelir — ekran maskelemez.
 */
export function usePersonelOnizleme(): UseMutationResult<
  PersonImportPreview,
  Error,
  { dosya: File }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({ dosya }) =>
      apiUpload('/api/import/persons/preview', token, dosya, personImportPreviewSchema),
  });
}

/**
 * ÇIKTI / ŞABLON İNDİRME — TEK YOL (F4·S9).
 *
 * Excel ve PDF çıktıları ile boş içe-aktarma şablonları aynı kapıdan iner:
 * bayt akışı API üzerinden gelir (public URL/SAS YOK) ve dosya adı sunucunun
 * `Content-Disposition` başlığından okunur. Ekran dosya adı UYDURMAZ —
 * uydurursa antetteki kapsam ile dosya adı ayrışır.
 */
/**
 * RİSK MADDELERİ TOPLU YÜKLEME (F4·S9 — içe aktarma motorunun ilk YENİ
 * müşterisi). Dosya sunucuda satırlara çevrilir; her satır tekil madde
 * ucuyla AYNI kapıdan (`upsertRiskItem`) geçer. Rapor kısmi başarıyı satır
 * numarası + sebeple taşır; **arşivlenmiş değerlendirmede istek 409'lar ve
 * hiçbir satır işlenmez.**
 */
export function useRiskMaddeYukle(): UseMutationResult<
  RiskItemImportReport,
  Error,
  { assessmentId: string; dosya: File }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assessmentId, dosya }) =>
      apiUpload(
        `/api/risk-assessments/${assessmentId}/items/import`,
        token,
        dosya,
        riskItemImportReportSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['risk-assessments'] });
    },
  });
}

/**
 * SİSTEM ÖNCESİ DÖF KAYITLARININ TOPLU DEVRİ (2½-sonrası — A1). Dosya
 * sunucuda satırlara çevrilir; her satır tekil aksiyon açma gövdesinden
 * (`createActionTx`) geçer. Önizleme YOKTUR — devir satırı kişi dosyası
 * açmaz ve yanlış satır tek tıkla iptal edilir (risk aktarma deseni).
 */
export function useDevirYukle(): UseMutationResult<ActionImportReport, Error, { dosya: File }> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dosya }) =>
      apiUpload('/api/actions/devir/import', token, dosya, actionImportReportSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });
}

export function useCiktiIndir(): UseMutationResult<void, Error, { yol: string; yedekAd: string }> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async ({ yol, yedekAd }) => {
      const { blob, dosyaAdi } = await apiDownload(yol, token);
      const url = URL.createObjectURL(blob);
      const bag = document.createElement('a');
      bag.href = url;
      bag.download = dosyaAdi ?? yedekAd;
      document.body.appendChild(bag);
      bag.click();
      bag.remove();
      URL.revokeObjectURL(url);
    },
  });
}

export function useCompetencies(personId: string): UseQueryResult<PersonCompetencySummary[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['competencies', personId],
    queryFn: ({ signal }) =>
      apiGet(`/api/persons/${personId}/competencies`, token, competenciesSchema, signal),
  });
}

/** Kişi kartının SOLUK SATIRI (P1-a — F4·S5·B4): yetkinlik satır içi girilir. */
export function useCreateCompetency(
  personId: string,
): UseMutationResult<
  PersonCompetencySummary,
  Error,
  { yetkinlikTipiId: string; gecerlilikBaslangic: string; gecerlilikBitis: string }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiPost(`/api/persons/${personId}/competencies`, token, input, personCompetencySummarySchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['competencies', personId] });
    },
  });
}

/**
 * KULLANICI DİZİNİ — **İKİ EKRANIN TEK KAPISI** (F4·B2, karar **Y1**:
 * Kapsam §20.7). Kişi kartındaki *"şapka ve bahçe"* bölümü (`personId` ile)
 * ve Yönetim grubundaki **Kullanıcılar** ekranı (süzgeçsiz) AYNI ucu okur.
 *
 * ⚠️ **Aşağıdaki DÖRT MUTASYON da ortaktır ve başka yerde tanımlanmaz.**
 * Rol ekranına ayrı bir yazma yolu açan kod Y1'i ihlal eder — kayıt her
 * hâlükârda KİŞİYE yazılır (`role_assignment` + `role_scope`). Bu kural
 * `apps/web/src/screens/yetki-parts.spec.ts` kod denetimiyle korunur.
 */
export function useAppUsers(options?: {
  personId?: string;
  enabled?: boolean;
}): UseQueryResult<AppUserSummary[]> {
  const token = useAccessToken();
  const personId = options?.personId;
  return useQuery({
    queryKey: ['app-users', personId ?? 'hepsi'],
    queryFn: ({ signal }) =>
      apiGet(
        personId ? `/api/app-users?personId=${personId}` : '/api/app-users',
        token,
        appUserSummaryListSchema,
        signal,
      ),
    enabled: options?.enabled ?? true,
  });
}

/** Dizin sorgularının TAMAMINI tazeler (kişi kartı + Kullanıcılar ekranı). */
function useDizinTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['app-users'] });
    // Şapka değişimi kullanıcının KENDİ menüsünü de etkileyebilir.
    void queryClient.invalidateQueries({ queryKey: ['my-authorization'] });
    void queryClient.invalidateQueries({ queryKey: ['kapilar'] });
  };
}

// ── TUR 9 / B1·B2·B3·B5 — ORGANİZASYON + ÇALIŞAN YAZMA KANCALARI ──────────
//
// ⚠️ **BU KANCALARI YALNIZ İKİ PAYLAŞILAN BİLEŞEN ÇAĞIRIR** —
// `screens/organizasyon-parts.tsx` ve `screens/calisan-parts.tsx`. Kural
// `yetki-parts`ın (Y1) aynısıdır ve **kod denetimiyle** kilitlidir
// (`e2e/organizasyon-yonetim.spec.ts`): ikinci bir yazma yolu, ikinci
// doğrulama kümesi ve zamanla ayrışan iki davranış demektir.

/** Org ağacını + listeleri tazeler (şirket · işyeri · lokasyon değişimi). */
function useOrgTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['companies'] });
    void queryClient.invalidateQueries({ queryKey: ['workplaces'] });
    void queryClient.invalidateQueries({ queryKey: ['workplaces-tumu'] });
    void queryClient.invalidateQueries({ queryKey: ['locations'] });
    void queryClient.invalidateQueries({ queryKey: ['locations-tumu'] });
  };
}

/** Kişi/istihdam listelerini tazeler (kart + liste + sayfalı liste). */
function useKisiTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['persons'] });
    void queryClient.invalidateQueries({ queryKey: ['persons-sayfali'] });
    void queryClient.invalidateQueries({ queryKey: ['person'] });
    void queryClient.invalidateQueries({ queryKey: ['employments'] });
    void queryClient.invalidateQueries({ queryKey: ['app-users'] });
    // Çalışan sayısı işyeri künyesinde TÜRETİLİR — istihdam değişimi onu da
    // değiştirir (sayaç yok, her okumada hesaplanır).
    void queryClient.invalidateQueries({ queryKey: ['workplaces-tumu'] });
    void queryClient.invalidateQueries({ queryKey: ['workplaces'] });
  };
}

export function useSirketAc(): UseMutationResult<CompanyDetail, Error, { ad: string }> {
  const token = useAccessToken();
  const tazele = useOrgTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/companies', token, input, companyDetailSchema),
    onSuccess: tazele,
  });
}

export function useSirketGuncelle(): UseMutationResult<
  CompanyDetail,
  Error,
  { id: string; ad: string }
> {
  const token = useAccessToken();
  const tazele = useOrgTazele();
  return useMutation({
    mutationFn: ({ id, ...govde }) =>
      apiPatch(`/api/companies/${id}`, token, govde, companyDetailSchema),
    onSuccess: tazele,
  });
}

export function useSirketSil(): UseMutationResult<SilmeSonucu, Error, { id: string }> {
  const token = useAccessToken();
  const tazele = useOrgTazele();
  return useMutation({
    mutationFn: ({ id }) => apiDelete(`/api/companies/${id}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

export function useIsyeriAc(): UseMutationResult<
  WorkplaceSummary,
  Error,
  { companyId: string } & WorkplaceCreateInput
> {
  const token = useAccessToken();
  const tazele = useOrgTazele();
  return useMutation({
    mutationFn: ({ companyId, ...govde }) =>
      apiPost(`/api/companies/${companyId}/workplaces`, token, govde, workplaceSummarySchema),
    onSuccess: tazele,
  });
}

export function useLokasyonAc(): UseMutationResult<
  LocationNodeSummary,
  Error,
  { workplaceId: string } & LocationNodeCreateInput
> {
  const token = useAccessToken();
  const tazele = useOrgTazele();
  return useMutation({
    mutationFn: ({ workplaceId, ...govde }) =>
      apiPost(`/api/workplaces/${workplaceId}/locations`, token, govde, locationNodeSummarySchema),
    onSuccess: tazele,
  });
}

/** ACİL DURUM / E4 (Kapsam §29): toplanma noktası işareti — lokasyon ekleme kapısıyla aynı. */
export function useLokasyonToplanmaNoktasi(): UseMutationResult<
  LocationNodeSummary,
  Error,
  { workplaceId: string; locationId: string } & ToplanmaNoktasiGirdi
> {
  const token = useAccessToken();
  const tazele = useOrgTazele();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, locationId, ...govde }) =>
      apiPatch(
        `/api/workplaces/${workplaceId}/locations/${locationId}`,
        token,
        govde,
        locationNodeSummarySchema,
      ),
    onSuccess: () => {
      tazele();
      void queryClient.invalidateQueries({ queryKey: ['acil-durum'] });
    },
  });
}

/** Arşivle / geri aç — yalnız holding yöneticisi (§20.5/A3, karar guard'da). */
export function useIsyeriDurumGecisi(): UseMutationResult<
  WorkplaceSummary,
  Error,
  { workplaceId: string; hedef: 'archive' | 'activate' }
> {
  const token = useAccessToken();
  const tazele = useOrgTazele();
  return useMutation({
    mutationFn: ({ workplaceId, hedef }) =>
      apiPost(`/api/workplaces/${workplaceId}/${hedef}`, token, {}, workplaceSummarySchema),
    onSuccess: tazele,
  });
}

export function useKisiAc(): UseMutationResult<PersonDetail, Error, PersonCreateInput> {
  const token = useAccessToken();
  const tazele = useKisiTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/persons', token, input, personDetailSchema),
    onSuccess: tazele,
  });
}

/** K11 — künye düzeltmesi. Kimlik no/tipi ŞEMADA YOK (eşleştirmenin çapası). */
export function useKisiGuncelle(): UseMutationResult<
  PersonDetail,
  Error,
  { personId: string } & PersonUpdateInput
> {
  const token = useAccessToken();
  const tazele = useKisiTazele();
  return useMutation({
    mutationFn: ({ personId, ...govde }) =>
      apiPatch(`/api/persons/${personId}`, token, govde, personDetailSchema),
    onSuccess: tazele,
  });
}

export function useIstihdamAc(): UseMutationResult<
  EmploymentSummary,
  Error,
  { personId: string } & EmploymentCreateInput
> {
  const token = useAccessToken();
  const tazele = useKisiTazele();
  return useMutation({
    mutationFn: ({ personId, ...govde }) =>
      apiPost(`/api/persons/${personId}/employments`, token, govde, employmentSummarySchema),
    onSuccess: tazele,
  });
}

export function useIstihdamKapat(): UseMutationResult<
  EmploymentSummary,
  Error,
  { personId: string; employmentId: string } & EmploymentCloseInput
> {
  const token = useAccessToken();
  const tazele = useKisiTazele();
  return useMutation({
    mutationFn: ({ personId, employmentId, ...govde }) =>
      apiPost(
        `/api/persons/${personId}/employments/${employmentId}/close`,
        token,
        govde,
        employmentSummarySchema,
      ),
    onSuccess: tazele,
  });
}

/** K11 / T9-13 — rol + başlangıç düzeltmesi. İşyeri buradan DEĞİŞMEZ (nakil). */
export function useIstihdamGuncelle(): UseMutationResult<
  EmploymentSummary,
  Error,
  { personId: string; employmentId: string } & EmploymentUpdateInput
> {
  const token = useAccessToken();
  const tazele = useKisiTazele();
  return useMutation({
    mutationFn: ({ personId, employmentId, ...govde }) =>
      apiPatch(
        `/api/persons/${personId}/employments/${employmentId}`,
        token,
        govde,
        employmentSummarySchema,
      ),
    onSuccess: tazele,
  });
}

/**
 * **NAKİL — TEK ADIM, İKİ UÇ (TUR 9 / B2 · T9-14).** Kişi kartında tek
 * düğmedir ama **yeni bir mekanizma DEĞİLDİR**: mevcut iki ucu arka arkaya
 * çağırır (kapanış `sebep=nakil` + yeni istihdam).
 *
 * ⚠️ **MADDE 80 KORUNUR:** geçmiş **eski işyerinde kalır** — istihdam
 * taşınmaz, kapanır ve yenisi açılır. Sunucuya "nakil" diye bir uç eklemek,
 * o kuralı tek bir yerde bozmayı mümkün kılardı.
 * ⚠️ **KAPANIŞ BAŞARILI, AÇILIŞ BAŞARISIZ OLABİLİR** ve bu hâl kullanıcıya
 * SÖYLENİR (kişi kartında ayrılmış bir istihdam kalır) — sessiz yutma yok.
 */
export function useNakil(): UseMutationResult<
  EmploymentSummary,
  Error,
  {
    personId: string;
    employmentId: string;
    bitis: string;
    yeni: EmploymentCreateInput;
  }
> {
  const token = useAccessToken();
  const tazele = useKisiTazele();
  return useMutation({
    mutationFn: async ({ personId, employmentId, bitis, yeni }) => {
      await apiPost(
        `/api/persons/${personId}/employments/${employmentId}/close`,
        token,
        { bitis, bitisSebebi: 'nakil' },
        employmentSummarySchema,
      );
      return apiPost(`/api/persons/${personId}/employments`, token, yeni, employmentSummarySchema);
    },
    onSuccess: tazele,
  });
}

/** B3 — bekleyen hesabı kişiye bağlama (§18/3 karma eşleşmenin elle kolu). */
export function useHesabiKisiyeBagla(): UseMutationResult<
  PendingUserSummary,
  Error,
  { appUserId: string; personId: string }
> {
  const token = useAccessToken();
  const tazele = useKisiTazele();
  return useMutation({
    mutationFn: ({ appUserId, personId }) =>
      apiPost(`/api/app-users/${appUserId}/person`, token, { personId }, pendingUserSummarySchema),
    onSuccess: tazele,
  });
}

/**
 * B3 — bağ çözme (§18/6). ⚠️ Çözme `person.bag_cozuldu_at`i doldurur ve dosya
 * **otomatik eşleşmeye kapanır** (e-posta devri koruması); geri bağlama ELLE
 * yapılır ve alanı SIFIRLAMAZ.
 */
export function useHesapBaginiCoz(): UseMutationResult<SilmeSonucu, Error, { appUserId: string }> {
  const token = useAccessToken();
  const tazele = useKisiTazele();
  return useMutation({
    mutationFn: ({ appUserId }) =>
      apiDelete(`/api/app-users/${appUserId}/person`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

/** Şapka verme — yalnız holding yöneticisi (§20.5/A4; karar guard'da). */
export function useSapkaVer(): UseMutationResult<
  RoleAssignmentDetail,
  Error,
  { appUserId: string; sapka: RoleType }
> {
  const token = useAccessToken();
  const tazele = useDizinTazele();
  return useMutation({
    mutationFn: (input) =>
      apiPost('/api/role-assignments', token, input, roleAssignmentDetailSchema),
    onSuccess: tazele,
  });
}

/** Şapka geri alma — bahçe kayıtları BİRLİKTE iner (sunucuda, tek transaction). */
export function useSapkaGeriAl(): UseMutationResult<SilmeSonucu, Error, { assignmentId: string }> {
  const token = useAccessToken();
  const tazele = useDizinTazele();
  return useMutation({
    mutationFn: ({ assignmentId }) =>
      apiDelete(`/api/role-assignments/${assignmentId}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

/**
 * Bahçe satırı ekleme. ⚠️ **DERİNLİK KURALI SUNUCUDA** (`bahceDerinligiIhlali`
 * → 422): ekran seçim ağacını `izinliBahceTurleri`den daraltır ama kapı
 * yine de kanıtlanır — ekran ikinci kural yazmaz, aynı kaynaktan okur.
 */
export function useBahceEkle(): UseMutationResult<
  RoleAssignmentDetail,
  Error,
  { assignmentId: string } & RoleScopeCreateInput
> {
  const token = useAccessToken();
  const tazele = useDizinTazele();
  return useMutation({
    mutationFn: ({ assignmentId, ...govde }) =>
      apiPost(
        `/api/role-assignments/${assignmentId}/scopes`,
        token,
        govde,
        roleAssignmentDetailSchema,
      ),
    onSuccess: tazele,
  });
}

export function useBahceCikar(): UseMutationResult<
  SilmeSonucu,
  Error,
  { assignmentId: string; scopeId: string }
> {
  const token = useAccessToken();
  const tazele = useDizinTazele();
  return useMutation({
    mutationFn: ({ assignmentId, scopeId }) =>
      apiDelete(
        `/api/role-assignments/${assignmentId}/scopes/${scopeId}`,
        token,
        silmeSonucuSchema,
      ),
    onSuccess: tazele,
  });
}

export function useContractors(options?: {
  enabled?: boolean;
}): UseQueryResult<ContractorDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['contractors'],
    queryFn: ({ signal }) => apiGet('/api/contractors', token, contractorsSchema, signal),
    // enabled=false: şapkası bu listeyi okuyamayan ekran sorguyu HİÇ atmaz
    // (403 üretip yutmak yerine — istek atılmayan yetki denenmemiş yetkidir).
    enabled: options?.enabled ?? true,
  });
}

export function useContractorDocumentsOf(
  contractors: ContractorDetail[] | undefined,
): UseQueryResult<ContractorDocumentDetail[]>[] {
  const token = useAccessToken();
  return useQueries({
    queries: (contractors ?? []).map((contractor) => ({
      queryKey: ['contractor-documents', contractor.id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiGet(
          `/api/contractors/${contractor.id}/documents`,
          token,
          contractorDocumentsSchema,
          signal,
        ),
    })),
  });
}

/**
 * KVKK aydınlatma metni + onay durumu (F1.5·S11). Uç KVKK kapısından
 * MUAFTIR — onaysız temsilci de okuyabilir (onay ekranı buna dayanır).
 */
export function useKvkkAydinlatma(): UseQueryResult<KvkkAydinlatma> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['kvkk-aydinlatma'],
    queryFn: ({ signal }) => apiGet('/api/kvkk/aydinlatma', token, kvkkAydinlatmaSchema, signal),
  });
}

/**
 * KVKK onayı. Başarıda my-authorization yenilenir — kapı açılır, kabuk
 * normal akışa döner (karar sunucuda; ekran yalnız sonucu izler).
 */
export function useKvkkOnayla(): UseMutationResult<KvkkConsentResult, Error, { surum: string }> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => apiPost('/api/kvkk/consent', token, input, kvkkConsentResultSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-authorization'] });
      void queryClient.invalidateQueries({ queryKey: ['kvkk-aydinlatma'] });
    },
  });
}

/**
 * Temsilci hesabı + davet paketi (F1.5·S11). Yanıttaki geçici parola YALNIZ
 * bu yanıtta görünür — önbelleğe alınmaz (mutation zaten cache'lemez).
 */
export function useTemsilciHesabiAc(): UseMutationResult<
  RepresentativeAccountInvite,
  Error,
  { contractorId: string; input: RepresentativeAccountCreateInput }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({ contractorId, input }) =>
      apiPost(
        `/api/contractors/${contractorId}/representative-account`,
        token,
        input,
        representativeAccountInviteSchema,
      ),
  });
}

/**
 * ✏️ 5½ / A1: liste kodu artık `LookupListeKodu`nun TAMAMIDIR — genel sözlük
 * yönetimi ekranı her listeyi okur (envanter `SOZLUK_LISTE_ENVANTERI`).
 */
export function useLookup(
  listeKodu: LookupListeKodu,
  options?: { enabled?: boolean },
): UseQueryResult<LookupItemDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['lookup', listeKodu],
    queryFn: ({ signal }) => apiGet(`/api/lookup/${listeKodu}`, token, lookupListSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

// ═══ Aksiyon/DÖF motoru (F2·S2 — Kapsam §22.3) ═══

const actionsSchema = z.array(actionDetailSchema);
const uygunsuzlukGecmisiSchema = z.array(nonconformityTransitionSchema);
const aksiyonIziSchema = z.array(actionTransitionSchema);
const assignablesSchema = z.array(assignableUserSchema);

/** "Bana atananlar" — her şapkanın kendi kayıtları (dar bahçenin ana ekranı). */
export function useBanaAtananlar(): UseQueryResult<ActionDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['actions', 'bana-atananlar'],
    queryFn: ({ signal }) => apiGet('/api/actions/assigned-to-me', token, actionsSchema, signal),
  });
}

/**
 * AKSİYON SÜZGECİ (T5 — P4). ⚠️ Süzgeç SUNUCUDA koşar: liste sayfalanıyorsa
 * istemcide süzmek yalnız GEÇERLİ SAYFAYI süzerdi ve toplam sayı yalan
 * söylerdi.
 */
export interface AksiyonSuzgeci {
  durum: 'hepsi' | 'open' | 'completed_reported' | 'closed';
  workplaceId: string | null;
  atananAppUserId: string | null;
  kaynakTipiId: string | null;
  gecikmis: boolean;
  sayfa: number;
}

export const AKSIYON_SUZGEC_BOS: AksiyonSuzgeci = {
  durum: 'hepsi',
  workplaceId: null,
  atananAppUserId: null,
  kaynakTipiId: null,
  gecikmis: false,
  sayfa: 1,
};

/** Görünür süzgeç göstergesinin saydığı kol sayısı (sayfa bir süzgeç değildir). */
export function aksiyonSuzgecSayisi(suzgec: AksiyonSuzgeci): number {
  return [
    suzgec.durum !== 'hepsi',
    suzgec.workplaceId !== null,
    suzgec.atananAppUserId !== null,
    suzgec.kaynakTipiId !== null,
    suzgec.gecikmis,
  ].filter(Boolean).length;
}

/** Süzgeci sorgu dizesine çevirir — çıktı ucu (T7) de AYNI dizeyi kullanır. */
export function aksiyonSuzgecSorgusu(suzgec: AksiyonSuzgeci): URLSearchParams {
  const params = new URLSearchParams();
  if (suzgec.durum !== 'hepsi') params.set('durum', suzgec.durum);
  if (suzgec.workplaceId) params.set('workplaceId', suzgec.workplaceId);
  if (suzgec.atananAppUserId) params.set('atananAppUserId', suzgec.atananAppUserId);
  if (suzgec.kaynakTipiId) params.set('kaynakTipiId', suzgec.kaynakTipiId);
  if (suzgec.gecikmis) params.set('gecikmis', 'true');
  return params;
}

export function useAksiyonlar(
  suzgec: AksiyonSuzgeci = AKSIYON_SUZGEC_BOS,
  options?: { enabled?: boolean },
): UseQueryResult<ActionList> {
  const token = useAccessToken();
  return useQuery({
    queryKey: [
      'actions',
      'liste',
      suzgec.durum,
      suzgec.workplaceId,
      suzgec.atananAppUserId,
      suzgec.kaynakTipiId,
      suzgec.gecikmis,
      suzgec.sayfa,
    ],
    queryFn: ({ signal }) => {
      const params = aksiyonSuzgecSorgusu(suzgec);
      params.set('sayfa', String(suzgec.sayfa));
      return apiGet(`/api/actions?${params.toString()}`, token, actionListSchema, signal);
    },
    enabled: options?.enabled ?? true,
  });
}

export function useAtanabilirler(options?: {
  enabled?: boolean;
}): UseQueryResult<AssignableUser[]> {
  const token = useAccessToken();
  // Atama yapamayan şapkada (izleyici) uç ÇAĞRILMAZ — kapı sunucudan.
  const kapi = useIkincilOkuma('atanabilir');
  return useQuery({
    queryKey: ['actions', 'atanabilirler'],
    queryFn: ({ signal }) =>
      apiGet('/api/actions/assignable-users', token, assignablesSchema, signal),
    enabled: (options?.enabled ?? true) && kapi.acik,
  });
}

// ═══ UYGUNSUZLUK (Tur 2½a — Kapsam §24 + §24.1) ═══════════════════════════
//
// ✏️ `useTalepler` / `useTalepAc` / `useTalepDonustur` SÖKÜLDÜ: "talep"
// kavramı öldü. Uygunsuzluktan DÖF açmak ayrı bir hook DEĞİL — `useAksiyonAc`
// gövdesine `nonconformityId` alanı geçilir (§24.1/9).

export function useUygunsuzluklar(params?: {
  durum?: string;
  workplaceId?: string;
  sayfa?: number;
  sayfaBoyutu?: number;
}): UseQueryResult<NonconformityList> {
  const token = useAccessToken();
  const sorgu = new URLSearchParams();
  if (params?.durum) sorgu.set('durum', params.durum);
  if (params?.workplaceId) sorgu.set('workplaceId', params.workplaceId);
  sorgu.set('sayfa', String(params?.sayfa ?? 1));
  sorgu.set('sayfaBoyutu', String(params?.sayfaBoyutu ?? 25));
  const qs = sorgu.toString();
  return useQuery({
    queryKey: ['nonconformities', qs],
    queryFn: ({ signal }) =>
      apiGet(`/api/nonconformities?${qs}`, token, nonconformityListSchema, signal),
  });
}

export function useUygunsuzluk(id: string | undefined): UseQueryResult<NonconformityDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['nonconformities', 'detay', id],
    queryFn: ({ signal }) =>
      apiGet(`/api/nonconformities/${id}`, token, nonconformityDetailSchema, signal),
    enabled: Boolean(id),
  });
}

export function useUygunsuzlukGecmisi(
  id: string | undefined,
): UseQueryResult<NonconformityTransitionRow[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['nonconformities', 'gecmis', id],
    queryFn: ({ signal }) =>
      apiGet(`/api/nonconformities/${id}/transitions`, token, uygunsuzlukGecmisiSchema, signal),
    enabled: Boolean(id),
  });
}

function useUygunsuzlukTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['nonconformities'] });
    void queryClient.invalidateQueries({ queryKey: ['actions'] });
    void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    void queryClient.invalidateQueries({ queryKey: ['zil'] });
  };
}

export function useUygunsuzlukAc(): UseMutationResult<
  NonconformityDetail,
  Error,
  NonconformityCreateInput
> {
  const token = useAccessToken();
  const tazele = useUygunsuzlukTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/nonconformities', token, input, nonconformityDetailSchema),
    onSuccess: tazele,
  });
}

export function useUygunsuzlukReddet(): UseMutationResult<
  NonconformityDetail,
  Error,
  { id: string; gerekce: string }
> {
  const token = useAccessToken();
  const tazele = useUygunsuzlukTazele();
  return useMutation({
    mutationFn: ({ id, gerekce }) =>
      apiPost(`/api/nonconformities/${id}/reject`, token, { gerekce }, nonconformityDetailSchema),
    onSuccess: tazele,
  });
}

/**
 * U13 (tur 11 / R3-c) — 5x5 DEĞERLENDİRME. Olayın sökülen `bes5` eyleminin
 * doğru kaptaki hâli; puan SUNUCUDA türetilir, gövde yalnız iki ekseni
 * taşır.
 */
export function useUygunsuzlukBes5(): UseMutationResult<
  NonconformityDetail,
  Error,
  { id: string; input: NonconformityBes5Input }
> {
  const token = useAccessToken();
  const tazele = useUygunsuzlukTazele();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiPost(`/api/nonconformities/${id}/bes5`, token, input, nonconformityDetailSchema),
    onSuccess: tazele,
  });
}

/** U7 — uygunsuzluk → olay (tür AKIŞI belirler; gerekçe zorunlu). */
export function useUygunsuzlukOlayaTasi(): UseMutationResult<
  NonconformityDetail,
  Error,
  { id: string; input: NonconformityMoveInput }
> {
  const token = useAccessToken();
  const tazele = useUygunsuzlukTazele();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiPost(
        `/api/nonconformities/${id}/move-to-incident`,
        token,
        input,
        nonconformityDetailSchema,
      ),
    onSuccess: tazele,
  });
}

/** U7'nin diğer yönü — olay → uygunsuzluk (tür sorusu YOK). */
export function useOlayUygunsuzlugaTasi(): UseMutationResult<
  BildirimSonucu,
  Error,
  { id: string; gerekce: string }
> {
  const token = useAccessToken();
  const tazele = useUygunsuzlukTazele();
  return useMutation({
    mutationFn: ({ id, gerekce }) =>
      apiPost(
        `/api/incidents/${id}/move-to-nonconformity`,
        token,
        { gerekce },
        bildirimSonucuSchema,
      ),
    onSuccess: tazele,
  });
}

/**
 * T9 — KAPANIŞ ONAYIM BEKLEYENLER. `useBanaAtananlar`ın aynası: orada
 * *"yapacağım işler"*, burada *"onaylayacağım işler"*.
 */
export function useKapanisOnayimBekleyenler(): UseQueryResult<ActionDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['actions', 'onayim-bekleyenler'],
    queryFn: ({ signal }) =>
      apiGet('/api/actions/awaiting-my-approval', token, actionsSchema, signal),
  });
}

/**
 * T4 — AKSİYON DETAYI. `GET /api/actions/:id` ucu F2·S2'den beri VARDI ama
 * hiçbir ekran ÇAĞIRMIYORDU: aksiyonun kendi sayfası yoktu, bilgisi liste
 * satırına sıkışmıştı (§2.40'ın "belgede ✅ kodda yok" kolu — Yönerge §6/9-h).
 */
export function useAksiyon(id: string | undefined): UseQueryResult<ActionDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['actions', 'detay', id],
    queryFn: ({ signal }) => apiGet(`/api/actions/${id}`, token, actionDetailSchema, signal),
    enabled: Boolean(id),
  });
}

function useAksiyonTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['actions'] });
    // ✏️ Tur 2½a: DÖF açmak/kapatmak uygunsuzluğun TÜRETİLMİŞ durumunu
    // değiştirir (kapanış bir kolon değil, DÖF satırlarından bir türetme) —
    // aksiyon yazan her yol uygunsuzluk sorgularını da tazelemek ZORUNDADIR,
    // yoksa ekran "Bildirildi" derken sunucu "İşlemde" der.
    void queryClient.invalidateQueries({ queryKey: ['nonconformities'] });
    void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    void queryClient.invalidateQueries({ queryKey: ['islerim'] });
    void queryClient.invalidateQueries({ queryKey: ['zil'] });
  };
}

export function useAksiyonAc(): UseMutationResult<ActionDetail, Error, ActionCreateInput> {
  const token = useAccessToken();
  const tazele = useAksiyonTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/actions', token, input, actionDetailSchema),
    onSuccess: tazele,
  });
}

// ═══ DÜZELTME PAKETİ (Tur 2½b — V2-V6) ═══════════════════════════════════
//
// ⚠️ Dördü de AYNI tazeleyiciden geçer (`useAksiyonTazele`): düzeltme
// aksiyonun durumunu ya da sorumlusunu değiştirir ve olay/uygunsuzluk
// TÜRETİLMİŞ kapanışını etkiler (iptal DÖF sayaçtan düşer).

export function useAksiyonIzi(id: string | undefined): UseQueryResult<ActionTransitionRow[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['actions', 'iz', id],
    queryFn: ({ signal }) =>
      apiGet(`/api/actions/${id}/transitions`, token, aksiyonIziSchema, signal),
    enabled: Boolean(id),
  });
}

export function useAksiyonDuzeltme(): UseMutationResult<
  ActionDetail,
  Error,
  { id: string; islem: 'reassign' | 'cancel' | 'change-deadline' | 'edit'; govde: unknown }
> {
  const token = useAccessToken();
  const tazele = useAksiyonTazele();
  return useMutation({
    mutationFn: ({ id, islem, govde }) =>
      apiPost(`/api/actions/${id}/${islem}`, token, govde, actionDetailSchema),
    onSuccess: tazele,
  });
}

/** Durum geçişleri: tamamla-bildir (atanan) · onayla-kapat / geri aç (atayan). */
export function useAksiyonGecis(): UseMutationResult<
  ActionDetail,
  Error,
  { id: string; gecis: 'report-done' | 'approve-close' | 'reopen'; aciklama?: string | null }
> {
  const token = useAccessToken();
  const tazele = useAksiyonTazele();
  return useMutation({
    mutationFn: ({ id, gecis, aciklama }) =>
      apiPost(
        `/api/actions/${id}/${gecis}`,
        token,
        gecis === 'report-done' ? { aciklama: aciklama ?? null } : {},
        actionDetailSchema,
      ),
    onSuccess: tazele,
  });
}

// ── Aksiyon kanıt ekleri (F2·S3 — Kapsam §22.4) ────────────────────────────

const attachmentsSchema = z.array(actionAttachmentSchema);

/** Ek listesi: aksiyonu görebilen görür (filtre sunucuda — ikinci kural yok). */
export function useAksiyonEkleri(
  actionId: string | null,
): UseQueryResult<ActionAttachmentDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['actions', 'ekler', actionId],
    enabled: actionId !== null,
    queryFn: ({ signal }) =>
      apiGet(`/api/actions/${actionId}/attachments`, token, attachmentsSchema, signal),
  });
}

function useEkTazele(): (actionId: string) => void {
  const queryClient = useQueryClient();
  return (actionId) => {
    void queryClient.invalidateQueries({ queryKey: ['actions', 'ekler', actionId] });
  };
}

export function useEkYukle(): UseMutationResult<
  ActionAttachmentDetail,
  Error,
  { actionId: string; dosya: File }
> {
  const token = useAccessToken();
  const tazele = useEkTazele();
  return useMutation({
    mutationFn: ({ actionId, dosya }) =>
      apiUpload(`/api/actions/${actionId}/attachments`, token, dosya, actionAttachmentSchema),
    onSuccess: (_sonuc, { actionId }) => tazele(actionId),
  });
}

export function useEkSil(): UseMutationResult<
  { silindi: true },
  Error,
  { actionId: string; attachmentId: string }
> {
  const token = useAccessToken();
  const tazele = useEkTazele();
  return useMutation({
    mutationFn: ({ actionId, attachmentId }) =>
      apiDelete(`/api/actions/${actionId}/attachments/${attachmentId}`, token, silmeSonucuSchema),
    onSuccess: (_sonuc, { actionId }) => tazele(actionId),
  });
}

/**
 * İndirme: bayt akışı API'den gelir (public URL/SAS YOK — Kapsam §22.4/6),
 * tarayıcıya geçici bir object URL ile sunulur.
 */
export function useEkIndir(): UseMutationResult<
  void,
  Error,
  { actionId: string; attachmentId: string; dosyaAdi: string }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async ({ actionId, attachmentId, dosyaAdi }) => {
      const { blob, dosyaAdi: sunucuAdi } = await apiDownload(
        `/api/actions/${actionId}/attachments/${attachmentId}/download`,
        token,
      );
      const url = URL.createObjectURL(blob);
      const bag = document.createElement('a');
      bag.href = url;
      bag.download = sunucuAdi ?? dosyaAdi;
      document.body.appendChild(bag);
      bag.click();
      bag.remove();
      URL.revokeObjectURL(url);
    },
  });
}

/** Tenant ayarları (F2·S2 — ilk anahtar uzman_aksiyon_atama). */
export function useTenantSettings(options?: { enabled?: boolean }): UseQueryResult<TenantSettings> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['tenant-settings'],
    queryFn: ({ signal }) => apiGet('/api/tenant-settings', token, tenantSettingsSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Ayar yazma — anahtar TİPİ KÜTÜKTEN gelir (F4·S2): `TenantSettingAnahtar`.
 * Eskiden tek anahtara ('uzman_aksiyon_atama') sabitliydi; yapılandırma
 * ekranı 14'ünü de aynı uçtan yazar — **ikinci mutation açma.**
 */
export function useTenantSettingYaz(): UseMutationResult<
  TenantSettings,
  Error,
  {
    anahtar: TenantSettingAnahtar;
    // ✏️ TUR 11: `risk_yenileme_periyot_ay` bir HARİTA taşır ({az, tehlikeli,
    // cok}) — birlik bu yüzden nesneyi de kapsar; anahtara göre doğrulama
    // SUNUCUDADIR (`tenantSettingDegerIhlali`) ve 422 verir.
    deger: boolean | number | string | null | RoleType[] | Record<string, number>;
  }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ anahtar, deger }) =>
      apiPut(`/api/tenant-settings/${anahtar}`, token, { deger }, tenantSettingsSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      // Dilim 2 (K-73g): her yazma deftere satır düşer.
      void queryClient.invalidateQueries({ queryKey: ['ayar-defteri'] });
    },
  });
}

/**
 * EKİPMAN KONTROL PERİYOTLARI (F4·S10 ARTÇI — madde 91/91-2 revizyonu).
 *
 * ✏️ **İKİ KATMAN, TEK YAZMA MUTATION'I.** ~~Holding katmanı (tipin kuralı)
 * sözlük ucundan yazılırdı~~ — ürün sahibi 26.08.2026'da o katmanı kaldırdı
 * (*"işyerleri kendileri yükler; yüklerken periyodu da belirlesinler"*).
 * Geriye **işyeri satırı** kaldı; ikinci katman KİLİTLİ kütüphane önerisidir
 * ve yazma yolu YOKTUR.
 */
export function useEkipmanPeriyotConfig(options?: {
  enabled?: boolean;
}): UseQueryResult<EkipmanPeriyotConfig> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['equipment-config-periods'],
    queryFn: ({ signal }) =>
      apiGet('/api/equipment-config/periods', token, ekipmanPeriyotConfigSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

/** İşyeri × tip satırı. `kontrolPeriyoduAy: null` satırı kaldırır → tip
 *  kütüphane ÖNERİSİNE döner. */
export function useEkipmanIsyeriPeriyoduYaz(): UseMutationResult<
  EkipmanPeriyotConfig,
  Error,
  EkipmanPeriyotYazInput
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (girdi) =>
      apiPut('/api/equipment-config/periods', token, girdi, ekipmanPeriyotConfigSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['equipment-config-periods'] });
      // Periyot değişince `sonraki_kontrol` sunucuda TAZELENİR — ekipman
      // listesindeki vade rozetleri de bayatlar.
      void queryClient.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
}

/**
 * **EKLEME AKIŞININ ÖN BİLGİSİ (F4·S10 ARTÇI — 1c).** Tip seçilince çağrılır:
 * bu işyerinde bu tipin süresi var mı, yoksa ne önerelim, kullanıcı yazabilir mi.
 *
 * ⚠️ Karar SUNUCUDA (§2.16) — ekran `yazabilir`i kendi hesaplamaz.
 */
export function useEkipmanPeriyotOnBilgi(
  workplaceId: string | null,
  tipId: string | null,
): UseQueryResult<EkipmanPeriyotOnBilgi> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['equipment-period-info', workplaceId, tipId],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/workplaces/${workplaceId}/equipment-period-info?tipId=${tipId}`,
        token,
        ekipmanPeriyotOnBilgiSchema,
        signal,
      ),
    enabled: workplaceId !== null && tipId !== null,
  });
}

/**
 * Ekipman ekleme. ⚠️ `kontrolPeriyoduAy` **KAYDA YAZILMAZ** — işyeri × tip
 * satırına gider (91-1: kayıt süre taşımaz). Satır zaten varsa alan
 * GÖNDERİLMEZ; farklı değerle gönderilirse uç 409 verir.
 */
export function useEkipmanEkle(): UseMutationResult<
  EquipmentDetail,
  Error,
  { workplaceId: string } & EquipmentCreateInput
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, ...govde }) =>
      apiPost(`/api/workplaces/${workplaceId}/equipment`, token, govde, equipmentDetailSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['equipment'] });
      void queryClient.invalidateQueries({ queryKey: ['equipment-period-info'] });
      void queryClient.invalidateQueries({ queryKey: ['equipment-config-periods'] });
    },
  });
}

/**
 * **Y1 — KÜNYE GÜNCELLEME.** ⚠️ `sonKontrol` bu gövdeye KONULAMAZ: uç onu
 * 422 ile reddeder (tur 4 / K1 — tarih kontrol geçmişinden türetilir).
 */
export function useEkipmanGuncelle(): UseMutationResult<
  EquipmentDetail,
  Error,
  { id: string; input: EquipmentUpdateInput }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiPatch(`/api/equipment/${id}`, token, input, equipmentDetailSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
}

/**
 * **Y4 — ENVANTERİN TOPLU YÜKLENMESİ.** Kısmi başarı satır düzeyindedir;
 * rapor satır satır döner (devir yüklemesi deseni — önizleme YOK).
 */
export function useEkipmanYukle(): UseMutationResult<
  EquipmentImportReport,
  Error,
  { dosya: File }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dosya }) =>
      apiUpload('/api/equipment/import', token, dosya, equipmentImportReportSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['equipment'] });
      void queryClient.invalidateQueries({ queryKey: ['equipment-period-info'] });
      void queryClient.invalidateQueries({ queryKey: ['equipment-config-periods'] });
    },
  });
}

/**
 * Zil (F2·S2 — Kapsam §22.2/8): kullanıcının kendi in_app teslimatları.
 * 60 sn'de bir tazelenir — gerçek zamanlılık v1 hedefi değil (push/websocket
 * ürün kararıyla gelir); sekme odağa dönünce de tazelenir (TanStack varsayılanı).
 */
export function useZil(): UseQueryResult<NotificationBell> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['zil'],
    queryFn: ({ signal }) => apiGet('/api/notifications', token, notificationBellSchema, signal),
    refetchInterval: 60_000,
  });
}

export function useZilOkundu(): UseMutationResult<
  NotificationReadResult,
  Error,
  { deliveryIds?: string[] }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiPost('/api/notifications/read', token, input, notificationReadResultSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zil'] });
    },
  });
}

// ═══ Form / şablon motoru (F2·S4 — Kapsam §22.5) ═══
//
// TİP KÖPRÜSÜ aynen (§2.16): her yanıt @isg/validation şemasından geçer.
// Ekran API'ye GÜVENİR — bahçe/sürüm kararları sunucudadır, burada ikinci
// filtre yoktur. Kayıt detayı KENDİ sürümünün şemasını taşır; doldurma ve
// görüntüleme ekranı alanları o şemadan üretir, "güncel şablon"a bakmaz.

// ✏️ F4·S6 (Y1): liste SUNUCUDA sayfalanır/süzülür — dizi şeması DÜŞTÜ.
const scopeSecenekleriSchema = z.object({
  isyerleri: z.array(z.object({ id: z.uuid(), ad: z.string(), companyAd: z.string() })),
  seciliIsyeriIdleri: z.array(z.uuid()),
});
export type FormSablonSuzgeci = {
  ara?: string;
  durum: 'hepsi' | 'active' | 'archived';
  workplaceId?: string | null;
  sayfa: number;
  sayfaBoyutu: number;
};
const formAttachmentsSchema = z.array(formSubmissionAttachmentSchema);

export function useFormSablonlar(
  suzgec?: FormSablonSuzgeci,
  options?: { enabled?: boolean },
): UseQueryResult<FormTemplateListesi> {
  const token = useAccessToken();
  const q = new URLSearchParams();
  if (suzgec?.ara) q.set('ara', suzgec.ara);
  if (suzgec?.durum && suzgec.durum !== 'hepsi') q.set('durum', suzgec.durum);
  if (suzgec?.workplaceId) q.set('workplaceId', suzgec.workplaceId);
  q.set('sayfa', String(suzgec?.sayfa ?? 1));
  q.set('sayfaBoyutu', String(suzgec?.sayfaBoyutu ?? 25));
  const qs = q.toString();
  return useQuery({
    queryKey: ['form-templates', qs],
    queryFn: ({ signal }) =>
      apiGet(`/api/form-templates?${qs}`, token, formTemplateListSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

/**
 * TEKİL ŞABLON (F4·S6 · Y1'in ön koşulu). ⚠️ Liste sayfalandığı için
 * *"listeden bul"* deseni artık YANLIŞ CEVAP VERİR — aranan şablon ikinci
 * sayfadaysa ekran onu bulamaz ve "yok" der (tur 9 / T9-03'ün form kolu).
 */
export function useFormSablon(templateId: string | null): UseQueryResult<FormTemplateSummary> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['form-template', templateId],
    enabled: templateId !== null,
    queryFn: ({ signal }) =>
      apiGet(`/api/form-templates/${templateId}`, token, formTemplateSummarySchema, signal),
  });
}

/** Y4 — SÜRÜM GEÇMİŞİ (şema taşımaz; içerik tekil sürüm ucundan). */
export function useFormSurumGecmisi(
  templateId: string | null,
): UseQueryResult<FormTemplateSurumGecmisi> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['form-template-versions-list', templateId],
    enabled: templateId !== null,
    queryFn: ({ signal }) =>
      apiGet(
        `/api/form-templates/${templateId}/versions`,
        token,
        formTemplateSurumGecmisiSchema,
        signal,
      ),
  });
}

/** Y2 — KAPSAM SEÇME HAVUZU: bahçedeki işyerleri + şablonun güncel kapsamı. */
export function useFormSablonKapsamSecenekleri(
  templateId: string | null,
): UseQueryResult<z.infer<typeof scopeSecenekleriSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['form-template-scope-options', templateId],
    enabled: templateId !== null,
    queryFn: ({ signal }) =>
      apiGet(
        `/api/form-templates/${templateId}/scope-options`,
        token,
        scopeSecenekleriSchema,
        signal,
      ),
  });
}

/** Y2/T4 — KAPSAM YAZMA (tam küme). Yeni sürüm DOĞURMAZ (T3). */
export function useFormSablonKapsam(): UseMutationResult<
  FormTemplateSummary,
  Error,
  { templateId: string; kapsamIsyeriIdleri: string[] }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ templateId, kapsamIsyeriIdleri }) =>
      apiPut(
        `/api/form-templates/${templateId}/scope`,
        token,
        { kapsamIsyeriIdleri },
        formTemplateSummarySchema,
      ),
    onSuccess: tazele,
  });
}

/** Sürüm detayı — ŞEMAYI taşır (düzenleyici ve doldurma ekranının kaynağı). */
export function useFormSurum(versionId: string | null): UseQueryResult<FormTemplateVersionDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['form-template-versions', versionId],
    enabled: versionId !== null,
    queryFn: ({ signal }) =>
      apiGet(
        `/api/form-template-versions/${versionId}`,
        token,
        formTemplateVersionDetailSchema,
        signal,
      ),
  });
}

/**
 * P4 — SAYFALANMIŞ + SÜZGEÇLİ kayıt listesi (Tur 3 / F3). Süzgeç SUNUCUDA
 * uygulanır: checklist en hızlı büyüyen tablodur ve "hepsini indir, tarayıcıda
 * süz" bir yıl sonra ekranı kilitler. Süzgeç anahtarın PARÇASIDIR — süzgeç
 * değişince yeni sorgu koşar (aksi hâlde eski sayfa gösterilirdi).
 */
export interface FormKayitSuzgeci {
  formTemplateId?: string | null;
  workplaceId?: string | null;
  durum?: 'draft' | 'submitted' | 'hepsi';
  baslangic?: string | null;
  bitis?: string | null;
  sayfa?: number;
  sayfaBoyutu?: number;
}

export function formKayitSorgusu(suzgec: FormKayitSuzgeci): string {
  const parametreler = new URLSearchParams();
  if (suzgec.formTemplateId) parametreler.set('formTemplateId', suzgec.formTemplateId);
  if (suzgec.workplaceId) parametreler.set('workplaceId', suzgec.workplaceId);
  if (suzgec.durum && suzgec.durum !== 'hepsi') parametreler.set('durum', suzgec.durum);
  if (suzgec.baslangic) parametreler.set('baslangic', suzgec.baslangic);
  if (suzgec.bitis) parametreler.set('bitis', suzgec.bitis);
  parametreler.set('sayfa', String(suzgec.sayfa ?? 1));
  parametreler.set('sayfaBoyutu', String(suzgec.sayfaBoyutu ?? 25));
  return parametreler.toString();
}

export function useFormKayitlar(
  suzgec: FormKayitSuzgeci = {},
  options?: { enabled?: boolean },
): UseQueryResult<FormSubmissionList> {
  const token = useAccessToken();
  const sorgu = formKayitSorgusu(suzgec);
  return useQuery({
    queryKey: ['form-submissions', 'liste', sorgu],
    queryFn: ({ signal }) =>
      apiGet(`/api/form-submissions?${sorgu}`, token, formSubmissionListSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Doldurma havuzu (Tur 3 / F1) — İKİ KAYNAK (bahçe + atama) SUNUCUDA
 * birleşir; ekran şirket/işyeri listesini kendi çıkarmaz. Şapkasız çalışanın
 * şirket listesi bahçe filtresinden BOŞ döner ve bu uç olmasaydı ona
 * verilmiş görev ekranda HİÇ GÖRÜNMEZDİ.
 */
export function useFormDoldurmaHedefleri(
  formTemplateId: string | null,
): UseQueryResult<FormDoldurmaHedefleri> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['form-templates', formTemplateId, 'doldurma-hedefleri'],
    enabled: formTemplateId !== null,
    queryFn: ({ signal }) =>
      apiGet(
        `/api/form-templates/${formTemplateId}/doldurma-hedefleri`,
        token,
        formDoldurmaHedefleriSchema,
        signal,
      ),
  });
}

export function useFormKayit(id: string | null): UseQueryResult<FormSubmissionDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['form-submissions', id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      apiGet(`/api/form-submissions/${id}`, token, formSubmissionDetailSchema, signal),
  });
}

function useFormTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['form-submissions'] });
    // Saha turu / S2: kap içindeki form kaydı turun detayında okunur (denetim
    // formu bölümü) — gönderim tur sayfasına döner, bayat detay göstermez.
    void queryClient.invalidateQueries({ queryKey: ['saha-turu'] });
    void queryClient.invalidateQueries({ queryKey: ['form-templates'] });
    // ⚠️ **TEKİL ANAHTARLAR DA TAZELENİR (F4·S6 · Y1).** Liste sayfalanınca
    // şablon sayfası ve doldurma sayfası TEKİL uca geçti (`['form-template',
    // id]` · `['form-template-versions-list', id]` · kapsam havuzu); bunlar
    // ÇOĞUL anahtarın (`['form-templates']`) ön eki DEĞİLDİR ve invalidate
    // onları görmez. ⚠️ Görülen arıza tam §2.40'tı: *"Düzenlemeye başla"*ya
    // basılıyor, taslak sunucuda DOĞUYOR, ekran hiç değişmiyordu — kullanıcı
    // düğmenin çalışmadığını sanardı. Bu satırları silme.
    void queryClient.invalidateQueries({ queryKey: ['form-template'] });
    void queryClient.invalidateQueries({ queryKey: ['form-template-versions-list'] });
    void queryClient.invalidateQueries({ queryKey: ['form-template-scope-options'] });
    void queryClient.invalidateQueries({ queryKey: ['form-template-versions'] });
    // S8 (F4·S8): izin içi form kaydı izin kartında yaşar — kayıt yazınca
    // iznin tür-form listesi de tazelenir (taslak/gönderildi rozetleri).
    void queryClient.invalidateQueries({ queryKey: ['ptw-izin-formlari'] });
    // T3 — görevden açılan form gönderilince görev dönemi kapanır (aynı transaction).
    void queryClient.invalidateQueries({ queryKey: ['gorevler'] });
    // T4 — tur içindeki form: tur detayı formun durumunu taşır.
    void queryClient.invalidateQueries({ queryKey: ['saha-turu'] });
    void queryClient.invalidateQueries({ queryKey: ['actions'] });
    void queryClient.invalidateQueries({ queryKey: ['nonconformities'] });
    void queryClient.invalidateQueries({ queryKey: ['zil'] });
  };
}

export function useFormKayitAc(): UseMutationResult<
  FormSubmissionDetail,
  Error,
  FormSubmissionCreateInput
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: (input) =>
      apiPost('/api/form-submissions', token, input, formSubmissionDetailSchema),
    onSuccess: tazele,
  });
}

export function useFormKayitGuncelle(): UseMutationResult<
  FormSubmissionDetail,
  Error,
  { id: string; girdi: FormSubmissionUpdateInput }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ id, girdi }) =>
      apiPatch(`/api/form-submissions/${id}`, token, girdi, formSubmissionDetailSchema),
    onSuccess: tazele,
  });
}

/** Gönderim: zorunluluk denetimi SUNUCUDA koşar (422 mesajı ekrana taşınır). */
export function useFormGonder(): UseMutationResult<FormSubmissionDetail, Error, { id: string }> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ id }) =>
      apiPost(`/api/form-submissions/${id}/submit`, token, {}, formSubmissionDetailSchema),
    onSuccess: tazele,
  });
}

/** Kayıttan AKSİYON (§22.5/7): kaynak + işyeri sunucuda kayıttan türetilir. */
export function useFormKayittanAksiyon(): UseMutationResult<
  ActionDetail,
  Error,
  { id: string; girdi: FormSubmissionActionCreateInput }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ id, girdi }) =>
      apiPost(`/api/form-submissions/${id}/actions`, token, girdi, actionDetailSchema),
    onSuccess: tazele,
  });
}

// ✏️ SÖKÜLDÜ (Tur 2½a — U10): `useFormKayittanTalep`. Formdan-uygunsuzluk
// ucu çelişkiliydi (§24/5 KPI kararı); form → DÖF hook'u yerinde durur.

export function useFormEkleri(
  submissionId: string | null,
): UseQueryResult<FormSubmissionAttachmentDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['form-submissions', 'ekler', submissionId],
    enabled: submissionId !== null,
    queryFn: ({ signal }) =>
      apiGet(
        `/api/form-submissions/${submissionId}/attachments`,
        token,
        formAttachmentsSchema,
        signal,
      ),
  });
}

export function useFormEkYukle(): UseMutationResult<
  FormSubmissionAttachmentDetail,
  Error,
  { submissionId: string; alanKodu: string; dosya: File }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionId, alanKodu, dosya }) =>
      apiUpload(
        `/api/form-submissions/${submissionId}/attachments?alan=${encodeURIComponent(alanKodu)}`,
        token,
        dosya,
        formSubmissionAttachmentSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['form-submissions'] });
    },
  });
}

export function useFormEkSil(): UseMutationResult<
  { silindi: true },
  Error,
  { submissionId: string; attachmentId: string }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionId, attachmentId }) =>
      apiDelete(
        `/api/form-submissions/${submissionId}/attachments/${attachmentId}`,
        token,
        silmeSonucuSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['form-submissions'] });
    },
  });
}

/** Ek indirme — public URL YOK; baytlar API'den gelir (S3 sözleşmesi). */
export function useFormEkIndir(): (
  submissionId: string,
  attachmentId: string,
) => Promise<{ blob: Blob; dosyaAdi: string | null }> {
  const token = useAccessToken();
  return (submissionId, attachmentId) =>
    apiDownload(
      `/api/form-submissions/${submissionId}/attachments/${attachmentId}/download`,
      token,
    );
}

// ── Şablon düzenleyici (yayınlanmış sürüm değişmez — §22.5/3) ──────────────

export function useFormSurumAc(): UseMutationResult<
  FormTemplateVersionDetail,
  Error,
  { templateId: string }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ templateId }) =>
      apiPost(
        `/api/form-templates/${templateId}/versions`,
        token,
        {},
        formTemplateVersionDetailSchema,
      ),
    onSuccess: tazele,
  });
}

/**
 * Şablon şemasını yazar.
 *
 * ⚠️ **DÖNEN ŞEMA ÖNBELLEĞE DOĞRUDAN YAZILIR — `invalidateQueries` TEK
 * BAŞINA YETMEZ.** Geçersiz kılma bir YENİDEN ÇEKME başlatır; o çekme
 * dönene kadar düzenleyici hâlâ **ESKİ** şemayı elinde tutar ve her yazma
 * ŞEMANIN TAMAMINI gönderdiği için, o aralıkta yapılan ikinci kayıt
 * birincisini **sessizce siler** (ölçüldü: iki bölüm arka arkaya eklenince
 * ilki kayboluyordu). Kaydet düğmesi istek boyunca pasiftir ama istek
 * dönünce **çekme bitmeden önce** yeniden açılır — kapı tam o aralıkta
 * açıktır. Sunucunun döndürdüğü sürümü önbelleğe koymak aralığı kapatır.
 */
export function useFormSurumGuncelle(): UseMutationResult<
  FormTemplateVersionDetail,
  Error,
  { versionId: string; sema: FormSemaTaslak }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ versionId, sema }) =>
      apiPatch(
        `/api/form-template-versions/${versionId}`,
        token,
        { sema },
        formTemplateVersionDetailSchema,
      ),
    onSuccess: (surum) => {
      queryClient.setQueryData(['form-template-versions', surum.id], surum);
      tazele();
    },
  });
}

export function useFormSurumYayinla(): UseMutationResult<
  FormTemplateVersionDetail,
  Error,
  { versionId: string }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ versionId }) =>
      apiPost(
        `/api/form-template-versions/${versionId}/publish`,
        token,
        {},
        formTemplateVersionDetailSchema,
      ),
    onSuccess: tazele,
  });
}

export function useFormSablonDurum(): UseMutationResult<
  FormTemplateSummary,
  Error,
  { templateId: string; durum: 'active' | 'archived' }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ templateId, durum }) =>
      apiPatch(`/api/form-templates/${templateId}`, token, { durum }, formTemplateSummarySchema),
    onSuccess: tazele,
  });
}

// ── Şablon yönetimi (F4·S7 — Kapsam §22.5a: çoğaltma + boş tuval + Ş5) ──────

/** Ş5 — NULL-ref şablonda ad değişikliği (kütüphane nüshasında sunucu 422). */
export function useFormSablonAd(): UseMutationResult<
  FormTemplateSummary,
  Error,
  { templateId: string; ad: string }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ templateId, ad }) =>
      apiPatch(`/api/form-templates/${templateId}`, token, { ad }, formTemplateSummarySchema),
    onSuccess: tazele,
  });
}

/**
 * 42.8 — kayıttan aksiyonun KAYNAĞI: sözlük kalemi seçimi (null = kaldır).
 * Kütüphane nüshasında ürün verisidir — sunucu 422 verir (ekran alanı da
 * çizmez); serbest kural/metin yazımı YOKTUR.
 */
export function useFormSablonAksiyonKaynagi(): UseMutationResult<
  FormTemplateSummary,
  Error,
  { templateId: string; aksiyonKaynagiId: string | null }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ templateId, aksiyonKaynagiId }) =>
      apiPatch(
        `/api/form-templates/${templateId}`,
        token,
        { aksiyonKaynagiId },
        formTemplateSummarySchema,
      ),
    onSuccess: tazele,
  });
}

/** Ş2 — çoğaltma: ad ZORUNLU (otomatik "(kopya)" üretilmez); kapsam açanın
 *  bahçesine bağlanır, kopya kaynaktan BAĞIMSIZ yaşar. */
export function useFormSablonCogalt(): UseMutationResult<
  FormTemplateSummary,
  Error,
  { templateId: string; ad: string; kapsamIsyeriIdleri: string[] }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ templateId, ad, kapsamIsyeriIdleri }) =>
      apiPost(
        `/api/form-templates/${templateId}/duplicate`,
        token,
        { ad, kapsamIsyeriIdleri },
        formTemplateSummarySchema,
      ),
    onSuccess: tazele,
  });
}

/** Ş2 — boş tuval (kaçış kapısı): bahçeli şablonu sıfırdan açar. Aksiyon
 *  kaynağı (42.8) opsiyonel — kurucu SÖZLÜKTEN seçer, boşsa kayıttan aksiyon
 *  açılamaz (422). */
export function useFormSablonBosTuval(): UseMutationResult<
  FormTemplateSummary,
  Error,
  { ad: string; kapsamIsyeriIdleri: string[]; aksiyonKaynagiId?: string | null }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ ad, kapsamIsyeriIdleri, aksiyonKaynagiId }) =>
      apiPost(
        '/api/form-templates',
        token,
        { ad, kapsamIsyeriIdleri, aksiyonKaynagiId: aksiyonKaynagiId ?? null },
        formTemplateSummarySchema,
      ),
    onSuccess: tazele,
  });
}

/**
 * EXCEL'DEN ŞABLON (P1 — Namet grubu, 01.11.2026): sabit şablonu indir ·
 * doldurulmuşunu yükle. Yükleme BOŞ TUVALİN ikizidir (aynı uç gövdesi):
 * ad + kapsam metin alanı olarak, dosya `dosya` olarak gider; yanıt açılan
 * şablonun özeti + tanınmayan değer uyarıları.
 */
export function useFormSablonExcelIndir(): UseMutationResult<void, Error, void> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async () => {
      const { blob, dosyaAdi } = await apiDownload('/api/form-templates/import-template', token);
      const url = URL.createObjectURL(blob);
      const bag = document.createElement('a');
      bag.href = url;
      bag.download = dosyaAdi ?? 'form-sablonu-excel.xlsx';
      document.body.appendChild(bag);
      bag.click();
      bag.remove();
      URL.revokeObjectURL(url);
    },
  });
}

export function useFormSablonExcelYukle(): UseMutationResult<
  FormTemplateExcelIceAktarSonucu,
  Error,
  { dosya: File; ad: string; kapsamIsyeriIdleri: string[] }
> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ dosya, ad, kapsamIsyeriIdleri }) =>
      apiUpload('/api/form-templates/import', token, dosya, formTemplateExcelIceAktarSonucSchema, {
        ad,
        kapsamIsyeriIdleri: JSON.stringify(kapsamIsyeriIdleri),
      }),
    onSuccess: tazele,
  });
}

/** BAHÇELİ şablon silme (soft) — matbu/merkezî şablonda sunucu 422 (Ş4). */
export function useFormSablonSil(): UseMutationResult<SilmeSonucu, Error, { templateId: string }> {
  const token = useAccessToken();
  const tazele = useFormTazele();
  return useMutation({
    mutationFn: ({ templateId }) =>
      apiDelete(`/api/form-templates/${templateId}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

// ── PTW onay zinciri (F3·S2 — Kapsam §16.7) ─────────────────────────────────
//
// Tanım yüzeyi yalnız holding yöneticisinindir (§20.5); ekran bunu MENÜ
// görünürlüğüyle sadeleştirir, kararı her istekte guard verir (§2.16).

const onayZincirleriSchema = z.array(approvalChainDetailSchema);
const bekleyenOnaylarSchema = z.array(ptwPendingApprovalSchema);
const zincirListesiSchema = z.array(approvalChainDetailSchema);

function useZincirTazele(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['ptw-onay-zincirleri'] });
  };
}

export function useOnayZincirleri(options?: {
  enabled?: boolean;
}): UseQueryResult<ApprovalChainDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-onay-zincirleri'],
    queryFn: ({ signal }) =>
      apiGet('/api/ptw/approval-chains', token, onayZincirleriSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

export function useZincirOlustur(): UseMutationResult<
  ApprovalChainDetail[],
  Error,
  ApprovalChainCreateInput
> {
  const token = useAccessToken();
  const tazele = useZincirTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/ptw/approval-chains', token, input, zincirListesiSchema),
    onSuccess: tazele,
  });
}

/** Başlangıç şablonu (K4): sistem ÖNERİR — tuşa müşteri basar. */
export function useZincirSablonu(): UseMutationResult<
  ApprovalChainDetail[],
  Error,
  { workplaceIds: string[] }
> {
  const token = useAccessToken();
  const tazele = useZincirTazele();
  return useMutation({
    mutationFn: (input) =>
      apiPost('/api/ptw/approval-chains/starter', token, input, zincirListesiSchema),
    onSuccess: tazele,
  });
}

export function useZincirGuncelle(): UseMutationResult<
  ApprovalChainDetail,
  Error,
  { id: string; input: ApprovalChainUpdateInput }
> {
  const token = useAccessToken();
  const tazele = useZincirTazele();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiPatch(`/api/ptw/approval-chains/${id}`, token, input, approvalChainDetailSchema),
    onSuccess: tazele,
  });
}

export function useZincirSil(): UseMutationResult<{ silindi: true }, Error, { id: string }> {
  const token = useAccessToken();
  const tazele = useZincirTazele();
  return useMutation({
    mutationFn: ({ id }) => apiDelete(`/api/ptw/approval-chains/${id}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

/** Bekleyen onaylarım — F3·S4'ten beri ÜÇ KAPI (açılış · uzatma · kapanış);
 *  kapı satırın kendi alanında gelir, ekran ayrı liste tutmaz. */
/**
 * ✏️ K-98 (bulgu 98): AKTİF İZNE EKLENEN KATILIMCI KARARLARI — izin veren
 * basamağının havuzundaki kullanıcının kuyruğu (İşlerim'in beşinci kolu).
 */
export function useKatilimciKararBekleyenler(): UseQueryResult<PtwKatilimciKararBekleyen[]> {
  const token = useAccessToken();
  // Karar veremeyen şapkada (izleyici) kabuk bu ucu ÇAĞIRMAZ — kapı sunucudan.
  const kapi = useIkincilOkuma('katilimciKarari');
  return useQuery({
    queryKey: ['ptw-katilimci-kararlari'],
    queryFn: ({ signal }) =>
      apiGet(
        '/api/ptw/participant-decisions/pending',
        token,
        z.array(ptwKatilimciKararBekleyenSchema),
        signal,
      ),
    enabled: kapi.acik,
  });
}

/** ✏️ K-98: aktif izne katılımcı ekleme — kişi `bekliyor` doğar. */
export function useAktifKatilimciEkle(): UseMutationResult<
  PtwPermitSummary,
  Error,
  { permitId: string; personId: string; gorevRefId: string }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ permitId, personId, gorevRefId }) =>
      apiPost(
        `/api/ptw/permits/${permitId}/participant-requests`,
        token,
        { personId, gorevRefId },
        ptwPermitSummarySchema,
      ),
    onSuccess: (_ozet, { permitId }) => tazele(permitId),
  });
}

/** ✏️ K-98: kişi bazında karar (izin veren basamağı; ret gerekçeli). */
export function useKatilimciKarari(): UseMutationResult<
  PtwPermitSummary,
  Error,
  { permitId: string; participantId: string; sonuc: 'onaylandi' | 'reddedildi'; gerekce?: string }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ participantId, sonuc, gerekce }) =>
      apiPost(
        `/api/ptw/participants/${participantId}/decision`,
        token,
        { sonuc, gerekce },
        ptwPermitSummarySchema,
      ),
    onSuccess: (_ozet, { permitId }) => tazele(permitId),
  });
}

export function useBekleyenOnaylar(): UseQueryResult<PtwPendingApproval[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-bekleyen-onaylar'],
    queryFn: ({ signal }) =>
      apiGet('/api/ptw/approvals/pending', token, bekleyenOnaylarSchema, signal),
  });
}

/**
 * İŞLERİM — KİŞİSEL KUYRUĞUN **TEK YÜKLEMİ** (F4·S5·B1 — T1b, IA §3/2).
 *
 * ⚠️ **ÜST BAR SAYACI VE DASHBOARD BLOĞU AYNI YÜKLEMDEN OKUR.** Üst bar "3"
 * derken dashboard 5 satır gösterirse kullanıcı **ikisine de güvenmez**;
 * ayrı iki sorgu yazılırsa sapma KAÇINILMAZDIR. Bu yüzden sayı burada
 * türetilir (`toplam = kalemler.length`), iki yerde ayrı sayılmaz.
 *
 * ⚠️ İşlerim bir **KUYRUKTUR** ve KİMLİK ekseninde konuşur (*"bunlar senin
 * işin"*); dashboard'ın *"süresi geçenler"* bloğu (B2) bir **DURUM ÖZETİDİR**
 * ve KONU ekseninde konuşur. İkisi birleştirilmez (IA §3/8 — birleşirse ya
 * blok kişiselleşir [G7 ihlali] ya İşlerim kişisel olmaktan çıkar).
 *
 * Kapsam bugün iki kuyruk: üzerime atanmış AÇIK aksiyonlar + onayımı
 * bekleyen izinler. Periyodik görevler B2'de AYNI listeye girer (T9).
 */
export interface IslerimKalemi {
  /** ✏️ T9: dördüncü kaynak `onayim` — kapanış onayı bekleyen aksiyonlar.
   *  ✏️ Bulgu 103: kaynak listesi `@isg/validation ISLERIM_KAYNAKLARI` —
   *  yolu `ISLERIM_HEDEF_HARITASI`ndan (K-103b); yeni kaynak hedefsiz derlenmez. */
  readonly kaynak: IslerimKaynak;
  readonly id: string;
  readonly baslik: string;
  readonly yol: string;
}

/**
 * Sayacın EKRANA YAZILAN METNİ — iki gösterim yeri de bunu çağırır.
 *
 * ⚠️ Yüklenirken sayı yazılmaz: bir kuyruk gelmiş diğeri gelmemişken yazılan
 * KISMİ sayı, dashboard bloğu henüz çizilmemişken üst barda görünür ve ikisi
 * o an farklı konuşur (F4·S5·B1'de e2e yakaladı). Ortak metin fonksiyonu,
 * "her DOM anında aynı sayı" değişmezini kod düzeyinde garanti eder.
 */
export function islerimSayaci(islerim: { toplam: number; yukleniyor: boolean }): string {
  if (islerim.yukleniyor) return '…';
  return islerim.toplam > 99 ? '99+' : String(islerim.toplam);
}

export function useIslerim(): {
  kalemler: IslerimKalemi[];
  toplam: number;
  yukleniyor: boolean;
} {
  const aksiyonlar = useBanaAtananlar();
  const onaylar = useBekleyenOnaylar();
  const gorevler = useBenimGorevlerim();
  // T9 — DÖRDÜNCÜ KUYRUK. ⚠️ 0d dersi: sayaç ne sayıyorsa sayfa onu
  // GÖSTERMEK ZORUNDA; bu kuyruğun bloğu IslerimScreen'e birlikte eklendi.
  const onayimBekleyenler = useKapanisOnayimBekleyenler();
  // ✏️ K-98 — BEŞİNCİ KUYRUK: aktif izne eklenen kişinin kararı (izin veren).
  const katilimciKararlari = useKatilimciKararBekleyenler();
  const katilimciOkuma = useIkincilOkuma('katilimciKarari');
  // Kapalı aksiyon bir "iş" değildir — kuyruk tanımı TEK yerde, burada.
  const acikAksiyonlar = (aksiyonlar.data ?? []).filter((a) => a.durum !== 'closed');
  const kalemler: IslerimKalemi[] = [
    ...acikAksiyonlar.map((a) => ({
      kaynak: 'aksiyon' as const,
      id: a.id,
      baslik: a.baslik,
      // ⚠️ Bulgu 103: bu satır '/islerim'e gidiyordu — kayda değil listeye.
      yol: ISLERIM_HEDEF_HARITASI.aksiyon(a.id),
    })),
    ...(onaylar.data ?? []).map((o) => ({
      kaynak: 'onay' as const,
      id: `${o.permit.id}-${o.chainStepId}-${o.turNo}`,
      baslik: `${o.permit.workplaceAd} — ${o.permit.locationAd}`,
      yol: ISLERIM_HEDEF_HARITASI.onay(o.permit.id),
    })),
    ...(katilimciKararlari.data ?? []).map((k) => ({
      kaynak: 'katilimci' as const,
      id: k.participantId,
      baslik: `${k.personAd} — ${k.permit.workplaceAd} · ${k.permit.locationAd}`,
      yol: ISLERIM_HEDEF_HARITASI.katilimci(k.permit.id),
    })),
    ...(onayimBekleyenler.data ?? []).map((a) => ({
      kaynak: 'onayim' as const,
      id: a.id,
      baslik: a.baslik,
      yol: ISLERIM_HEDEF_HARITASI.onayim(a.id),
    })),
    // T9 — KİŞİ HEDEFLİ VE KÜME HEDEFLİ GÖREVLER TEK LİSTEDE, AYRILMADAN.
    // Ayırt edici alan YOKTUR: *tatbikat gerçekten uzmanın işidir; küme
    // hedefini ayırmak uzmanın kendi işini kendi listesinde görmemesine yol
    // açardı.* Sayaç bu diziden türediği için ÜÇÜNCÜ KUYRUK SAYACI DA
    // KENDİLİĞİNDEN DOĞRULAR — ikinci sayım yolu açılmadı.
    ...(gorevler.data ?? []).map((g) => ({
      kaynak: 'gorev' as const,
      id: g.id,
      baslik: `${g.nesneAd} — ${g.kalemAd}`,
      yol: ISLERIM_HEDEF_HARITASI.gorev(g.id),
    })),
  ];
  return {
    kalemler,
    toplam: kalemler.length,
    yukleniyor:
      aksiyonlar.isPending ||
      onaylar.isPending ||
      gorevler.isPending ||
      onayimBekleyenler.isPending ||
      // Kapısı kapalı kuyruk HİÇ gönderilmez (izleyici) — onu beklemek sayacı
      // sonsuza dek "…"da bırakırdı. Kapının kendisi yüklenene dek sayı yazılmaz.
      katilimciOkuma.bekliyor ||
      (katilimciOkuma.acik && katilimciKararlari.isPending),
  };
}

/**
 * İşlerim'in görev kolu (T9). Ayrı bir ekranı yoktur — `useIslerim` bunu
 * kendi listesine katar; sayaç ve blok o listeden okur.
 */
export function useBenimGorevlerim(): UseQueryResult<z.output<typeof taskMineListSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['gorevler', 'benim'],
    queryFn: ({ signal }) =>
      apiGet('/api/tasks/occurrences/mine', token, taskMineListSchema, signal),
  });
}

/**
 * PTW yazma işlemlerinden sonra tazelenen anahtarlar. TEK yerde durur: bir
 * geçiş listeyi, detayı, bekleyen onayları ve zili aynı anda eskitir — her
 * mutasyonda ayrı ayrı sayılsaydı biri unutulur ve ekran bayat veriyle
 * kalırdı.
 */
function usePtwTazele(): (permitId?: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (permitId?: string) => {
    await queryClient.invalidateQueries({ queryKey: ['ptw-izinler'] });
    await queryClient.invalidateQueries({ queryKey: ['ptw-bekleyen-onaylar'] });
    await queryClient.invalidateQueries({ queryKey: ['ptw-katilimci-kararlari'] });
    await queryClient.invalidateQueries({ queryKey: ['ptw-ihlaller'] });
    await queryClient.invalidateQueries({ queryKey: ['zil'] });
    if (permitId) {
      await queryClient.invalidateQueries({ queryKey: ['ptw-izin', permitId] });
      await queryClient.invalidateQueries({ queryKey: ['ptw-uygunluk', permitId] });
      // S8: talep gönderimi/revizyonu izin içi form kayıtlarının durumunu da
      // değiştirir (damga/taslağa dönüş) — form görünümleri birlikte tazelenir.
      await queryClient.invalidateQueries({ queryKey: ['ptw-izin-formlari', permitId] });
      await queryClient.invalidateQueries({ queryKey: ['form-submissions'] });
      // Brifing bölümünün yazma kapısı iznin durumuna ve güncel sorumlusuna
      // bağlıdır (onay · devir kabulü · askı) — her izin işlemiyle tazelenir.
      await queryClient.invalidateQueries({ queryKey: ['ptw-brifingler', permitId] });
    }
  };
}

export function useOnayKarari(): UseMutationResult<
  PtwApprovalDecisionResult,
  Error,
  { permitId: string; input: PtwApprovalDecisionInput }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ permitId, input }) =>
      apiPost(
        `/api/ptw/permits/${permitId}/approvals`,
        token,
        input,
        ptwApprovalDecisionResultSchema,
      ),
    onSuccess: (_sonuc, { permitId }) => tazele(permitId),
  });
}

// ── PTW ofis yüzeyi (F3·S4 — Kapsam §16.7, ERD §5.5) ────────────────────────
//
// Ekran hiçbir uygunluk/geçiş kuralı HESAPLAMAZ: liste sayaçları, izin
// detayının `yetkiler` alanı ve uygunluk kalemleri sunucudan gelir (§2.16).

const ihlallerSchema = z.array(ptwViolationSummarySchema);

/**
 * ✏️ **K5 (tur 10) — LİSTE SUNUCUDA SÜZÜLÜR VE SAYFALANIR** (eğitim Y1 /
 * ekipman Y3 ikizi). Süzgeç dizgisi `ptwIzinSuzgecSorgusu`dan gelir ve **çıktı
 * ucu aynı dizgiyi kurar** — ikisi ayrı kurulsaydı dosya ekrandan farklı bir
 * küme taşırdı.
 */
export function useIzinler(suzgec: PtwIzinSuzgeci): UseQueryResult<PtwPermitListResponse> {
  const token = useAccessToken();
  const sorgu = ptwIzinSuzgecSorgusu(suzgec, true);
  return useQuery({
    queryKey: ['ptw-izinler', sorgu],
    queryFn: ({ signal }) =>
      apiGet(`/api/ptw/permits${sorgu}`, token, ptwPermitListResponseSchema, signal),
  });
}

/** K5 — liste süzgecinin havuzları (talep formunun havuzundan AYRI uç). */
export function useIzinSuzgecSecenekleri(): UseQueryResult<PtwPermitFilterOptions> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-izin-suzgecleri'],
    queryFn: ({ signal }) =>
      apiGet('/api/ptw/permits/filters', token, ptwPermitFilterOptionsSchema, signal),
  });
}

/**
 * K4 (tur 10) — İZİN LİSTESİ EXCEL YOLU. ⚠️ **EKRANIN SÜZGECİYLE,
 * SAYFALAMASIZ:** dosya *"şu anki süzgeçle N kayıt"*ın tamamını taşır.
 *
 * ⚠️ İndirmenin kendisi `useCiktiIndir`dedir — bu yalnız YOLU kurar. İkinci
 * bir indirme mutasyonu yazmak, `content-disposition` okuma ve blob temizleme
 * mantığını çoğaltmak olurdu.
 */
export function izinListesiCiktiYolu(suzgec: PtwIzinSuzgeci): string {
  return `/api/ptw/permits/export${ptwIzinSuzgecSorgusu(suzgec, false)}`;
}

export function useIzinDetay(permitId: string | null): UseQueryResult<PtwPermitDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-izin', permitId],
    queryFn: ({ signal }) =>
      apiGet(`/api/ptw/permits/${permitId!}`, token, ptwPermitDetailSchema, signal),
    enabled: permitId !== null,
  });
}

/** ANLIK uygunluk — canlı kontrolle AYNI fonksiyondur (§2.5); ekran yalnız
 *  sonucu gösterir, kalem üretmez. */
export function useIzinUygunluk(permitId: string | null): UseQueryResult<PtwUygunlukSonucu> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-uygunluk', permitId],
    queryFn: ({ signal }) =>
      apiGet(`/api/ptw/permits/${permitId!}/eligibility`, token, ptwUygunlukSonucuSchema, signal),
    enabled: permitId !== null,
  });
}

/**
 * ONAY PENCERESİNİN UYGUNLUK OKUMASI (P-2/P-5). İki bölüm ayrı gelir ve
 * `engeller` düğmenin pasifliğini belirler — ekran ikinci kural yazmaz;
 * karar ucu AYNI listeyle 422 verir. Bekleyen basamağı olmayanda `null`.
 */
export function useOnayUygunlugu(permitId: string | null): UseQueryResult<PtwOnayUygunlugu | null> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-onay-uygunluk', permitId],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/ptw/permits/${permitId!}/approval-eligibility`,
        token,
        ptwOnayUygunluguSchema.nullable(),
        signal,
      ),
    enabled: permitId !== null,
  });
}

/**
 * KATILIMCI LİSTESİNİ YAZ (P-1) — gönderilen liste TAM listedir. Taslakta
 * katılımcı eklenebilmesinin tek yolu budur: gönderim kapısı katılımcısız
 * izne kapalı, talep formu ise katılımcıyı yalnız açılışta soruyor.
 */
export function useIzinKatilimcilari(): UseMutationResult<
  PtwPermitSummary,
  Error,
  { permitId: string; katilimcilar: { personId: string; gorevRefId: string }[] }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ permitId, katilimcilar }) =>
      apiPut(
        `/api/ptw/permits/${permitId}/participants`,
        token,
        { katilimcilar },
        ptwPermitSummarySchema,
      ),
    onSuccess: (_ozet, { permitId }) => tazele(permitId),
  });
}

/**
 * TASLAKTA GÜNCEL SORUMLU (K-95c — bulgu 95). Sorumlu gönderimde zorunlu;
 * talep formunda boş bırakılan taslak burada tamamlanır.
 */
export function useIzinSorumlusu(): UseMutationResult<
  PtwPermitSummary,
  Error,
  { permitId: string; guncelSorumluEmploymentId: string }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ permitId, guncelSorumluEmploymentId }) =>
      apiPut(
        `/api/ptw/permits/${permitId}/sorumlu`,
        token,
        { guncelSorumluEmploymentId },
        ptwPermitSummarySchema,
      ),
    onSuccess: (_ozet, { permitId }) => tazele(permitId),
  });
}

/**
 * TASLAK KÜNYESİ (28.09.2026 — K-T1 · K-T2): izin türleri · lokasyon ·
 * geçerlilik · iş ekipmanları. Gönderilen listeler TAM listedir; işyeri
 * gövdede YOKTUR (değişmez).
 *
 * ⚠️ Tür çıkarmada elle girilen içerik varsa sunucu **409 + düşecek içerik
 * listesi** döner (`ApiError.govde` → `ptwKunyeOnayGerekliSchema`); ekran
 * listeyi gösterir ve `elleIcerikOnayi: true` ile yeniden gönderir.
 */
export function useIzinKunyesi(): UseMutationResult<
  PtwPermitSummary,
  Error,
  { permitId: string; girdi: PtwKunyeSetGirdi }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ permitId, girdi }) =>
      apiPut(`/api/ptw/permits/${permitId}/kunye`, token, girdi, ptwPermitSummarySchema),
    onSuccess: (_ozet, { permitId }) => tazele(permitId),
  });
}

// ── İŞ ÖNCESİ BRİFİNG (28.09.2026 — K-B1…B4) ────────────────────────────────

/**
 * Brifing bölümünün okuması — konular · katılımcı adayları · brifingler +
 * yazma kapısı (P13). ⚠️ İzin detayı ucu brifingi TAŞIMAZ (K-B2): brifing
 * yalnız kendi ucundan okunur.
 */
export function useIzinBrifingleri(permitId: string | null): UseQueryResult<PtwBriefingList> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-brifingler', permitId],
    queryFn: ({ signal }) =>
      apiGet(`/api/ptw/permits/${permitId!}/briefings`, token, ptwBriefingListSchema, signal),
    enabled: permitId !== null,
  });
}

/** Üç yazma (gir · kaldır · katılım satırı kaldır) AYNI yanıtı döner: bölümün güncel hâli. */
function useBrifingYazildi(): (permitId: string, veri: PtwBriefingList) => void {
  const queryClient = useQueryClient();
  return (permitId, veri) => {
    queryClient.setQueryData(['ptw-brifingler', permitId], veri);
  };
}

export function useIzinBrifingEkle(): UseMutationResult<
  PtwBriefingList,
  Error,
  { permitId: string; girdi: PtwBriefingCreateInput }
> {
  const token = useAccessToken();
  const yazildi = useBrifingYazildi();
  return useMutation({
    mutationFn: ({ permitId, girdi }) =>
      apiPost(`/api/ptw/permits/${permitId}/briefings`, token, girdi, ptwBriefingListSchema),
    onSuccess: (veri, { permitId }) => yazildi(permitId, veri),
  });
}

export function useIzinBrifingSil(): UseMutationResult<
  PtwBriefingList,
  Error,
  { permitId: string; hedef: { briefingId: string } | { attendanceId: string } }
> {
  const token = useAccessToken();
  const yazildi = useBrifingYazildi();
  return useMutation({
    mutationFn: ({ hedef }) =>
      apiDelete(
        'briefingId' in hedef
          ? `/api/ptw/briefings/${hedef.briefingId}`
          : `/api/ptw/briefing-attendances/${hedef.attendanceId}`,
        token,
        ptwBriefingListSchema,
      ),
    onSuccess: (veri, { permitId }) => yazildi(permitId, veri),
  });
}
/**
 * İTA SATIRLARINI YAZ (tur 11 / R6) — gönderilen liste TAM listedir ve
 * **SIRA DİZİNİN KENDİSİDİR** (ayrı `sira` alanı gönderilmez).
 *
 * ⚠️ Boş liste geçerlidir: İTA zorunlu değildir, boş İTA onaya gider ve
 * onaycı reddeder (§16.7.4/6-3).
 */
export function useIzinJsa(): UseMutationResult<
  PtwPermitSummary,
  Error,
  { permitId: string; satirlar: { isAdimi: string; tehlike: string; onlem: string }[] }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ permitId, satirlar }) =>
      apiPut(`/api/ptw/permits/${permitId}/jsa`, token, { satirlar }, ptwPermitSummarySchema),
    onSuccess: (_ozet, { permitId }) => tazele(permitId),
  });
}

/**
 * Talep formunun havuzları — marjı geçmeyen ekipman listede HİÇ yoktur.
 *
 * ✏️ **TUR 10 / K2:** kişi havuzları VARSAYILAN OLARAK iznin işyeriyle
 * sınırlıdır; `tumHolding` kutusu açıldığında tenant geneline döner (destek
 * ekibi çağırma hâli). Süzgeç sunucudadır — ekran ikinci filtre yazmaz (§2.16).
 */
export function useIzinSecenekleri(
  workplaceId: string | null,
  tumHolding = false,
): UseQueryResult<PtwPermitOptions> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-secenekler', workplaceId, tumHolding],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/ptw/permits/options?workplaceId=${workplaceId!}${tumHolding ? '&tumHolding=true' : ''}`,
        token,
        ptwPermitOptionsSchema,
        signal,
      ),
    enabled: workplaceId !== null,
  });
}

export function useIzinTalebiAc(): UseMutationResult<
  PtwPermitSummary,
  Error,
  PtwPermitCreateInput
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/ptw/permits', token, input, ptwPermitSummarySchema),
    onSuccess: (ozet) => tazele(ozet.id),
  });
}

/**
 * Tek tuşluk yaşam döngüsü işlemleri — hepsi AYNI sözleşmeden geçer (yanıt
 * iznin yeni özeti). Ayrı hook'lar yerine tek `islem` parametresi: her biri
 * için kopya hook yazmak, tazeleme listesini beş kez tekrar ettirirdi.
 */
export type PtwIzinIslemi =
  | 'submit'
  | 'cancel'
  | 'activate'
  | 'withdraw'
  | 'extension-request'
  | 'work-completed'
  | 'field-check';

export function useIzinIslemi(): UseMutationResult<
  PtwPermitSummary,
  Error,
  {
    permitId: string;
    islem: PtwIzinIslemi;
    govde?: PtwCancelInput | PtwWithdrawInput | PtwExtensionRequestInput;
  }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ permitId, islem, govde }) =>
      apiPost(`/api/ptw/permits/${permitId}/${islem}`, token, govde ?? {}, ptwPermitSummarySchema),
    onSuccess: (_ozet, { permitId }) => tazele(permitId),
  });
}

/** "Kararımı bekleyenler" — liste KİŞİSELDİR (yalnız izin veren; başkasında
 *  boş döner, fail-closed veriyle). */
export function useBekleyenIhlaller(): UseQueryResult<PtwViolationSummary[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-ihlaller'],
    queryFn: ({ signal }) => apiGet('/api/ptw/violations/pending', token, ihlallerSchema, signal),
  });
}

export function useIhlalKarari(): UseMutationResult<
  PtwPermitSummary,
  Error,
  { violationId: string; permitId: string; input: PtwViolationDecisionInput }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ violationId, input }) =>
      apiPost(`/api/ptw/violations/${violationId}/decision`, token, input, ptwPermitSummarySchema),
    onSuccess: (_ozet, { permitId }) => tazele(permitId),
  });
}

// ── F3·S5: SAHA YÜZEYİ + CACHE-AND-QUEUE ────────────────────────────────────

/** QR sayfasının salt-okunur durum kartı — oturumsuz erişim YOK (401). */
export function useQrDurum(permitId: string | null): UseQueryResult<PtwQrDurum> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-qr', permitId],
    queryFn: ({ signal }) =>
      apiGet(`/api/ptw/permits/${permitId!}/qr`, token, ptwQrDurumSchema, signal),
    enabled: permitId !== null,
  });
}

/**
 * ÇEVRİMDIŞI KUYRUKLANABİLEN saha adımı. Ağ varsa DOĞRUDAN gönderir; yoksa
 * (ya da istek ağ hatasıyla düşerse) kuyruğa alır ve `kuyruklandi` döner.
 *
 * Adres eşlemesi TEK YERDE (`kuyrukIstegi`): çevrimiçi ve kuyruklu yol aynı
 * uca gider — ikinci bir adres listesi zamanla ayrışırdı.
 */
export function useSahaIslemi(): UseMutationResult<
  { sonuc: 'gonderildi' | 'kuyruklandi' },
  Error,
  SahaKuyrukIslem
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    // TUZAK (F3·S5): TanStack Query VARSAYILAN olarak çevrimdışıyken
    // mutasyonu DURAKLATIR (`networkMode: 'online'`) — mutationFn hiç
    // koşmaz, düğme sonsuza dek "gönderiliyor" kalır ve KUYRUĞA HİÇBİR ŞEY
    // GİRMEZ. Kuyruğun bütün varlık sebebi çevrimdışı çalışmaktır; bu yüzden
    // `always`: bağlantı kararını BİZ veriyoruz (kuyruğa al ya da gönder).
    networkMode: 'always',
    mutationFn: async (islem) => {
      const kayit = {
        id: 'anlik',
        islem,
        cihazAt: new Date().toISOString(),
      };
      const { path, body } = kuyrukIstegi(kayit);
      if (!navigator.onLine) {
        kuyrugaAl(islem);
        return { sonuc: 'kuyruklandi' as const };
      }
      try {
        await apiPost(path, token, body, z.unknown());
        return { sonuc: 'gonderildi' as const };
      } catch (error) {
        // Ağ hatası kuyruğa düşer; SUNUCU HATASI (4xx/5xx) düşmez — kural
        // ihlalini kuyruğa saklamak, kullanıcıya "oldu" demenin sessiz hâli
        // olurdu (ApiError = sunucu cevap verdi demektir).
        if (error instanceof ApiError) throw error;
        kuyrugaAl(islem);
        return { sonuc: 'kuyruklandi' as const };
      }
    },
    // TAZELEME BEKLENMEZ (void): çevrimdışıyken invalidate → refetch zinciri
    // yeniden denemelerle uzar ve mutasyonu "pending" tutardı — saha düğmesi
    // kilitli kalırdı. Kuyruğa alma zaten bitmiştir; tazeleme en-iyi çabadır.
    onSuccess: (_sonuc, islem) => {
      void tazele(islem.permitId);
    },
  });
}

/**
 * ÇEVRİMİÇİ İSTEYEN saha adımı (kuyruk kapsamı DIŞI — bayat veriyle riskli
 * yönde işlem yapılmaz): askıdan dönüş · devir başlat/kabul · izolasyon
 * kaldırma · zorla kilit kaldırma · saha kontrolü. Ağ yoksa istek düşer ve
 * kullanıcı hatayı görür — sessizce kuyruğa alınmaz.
 */
export function useSahaCevrimiciIslemi(): UseMutationResult<
  PtwPermitSummary,
  Error,
  { permitId: string; path: string; govde?: unknown }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ path, govde }) => apiPost(path, token, govde ?? {}, ptwPermitSummarySchema),
    onSuccess: (_ozet, { permitId }) => tazele(permitId),
  });
}

/** K11 tarama — sonucun CÜMLESİ sunucudan gelir (bloklamaz, iz bırakır). */
export function useEkipmanTeyidi(): UseMutationResult<
  PtwEquipmentScanSonucu,
  Error,
  { permitId: string; kod: string }
> {
  const token = useAccessToken();
  const tazele = usePtwTazele();
  return useMutation({
    mutationFn: ({ permitId, kod }) =>
      apiPost(
        `/api/ptw/permits/${permitId}/equipment-scan`,
        token,
        { kod },
        ptwEquipmentScanSonucuSchema,
      ),
    onSuccess: (_sonuc, { permitId }) => tazele(permitId),
  });
}

/**
 * Kuyruk durumu + senkron. `senkronEt` her kaydı SIRAYLA gönderir; çatışanlar
 * (409/422/403) listeye düşer — sessiz kayıp yok.
 */
export function useSahaKuyrugu(): {
  kayitlar: KuyrukKaydi[];
  catismalar: SahaCatismasi[];
  senkron: () => Promise<SenkronSonucu>;
  catismalariTemizle: () => void;
  senkronKosuyor: boolean;
} {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  const [surum, setSurum] = useState(0);
  const [senkronKosuyor, setSenkronKosuyor] = useState(false);

  useEffect(() => kuyrugaAbone(() => setSurum((n) => n + 1)), []);

  const senkron = useCallback(async () => {
    setSenkronKosuyor(true);
    try {
      const sonuc = await senkronEt(async (path, body) => {
        const yanit = await fetch(path, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const govde = (await yanit.json().catch(() => null)) as { message?: string } | null;
        return {
          ok: yanit.ok,
          status: yanit.status,
          mesaj: govde?.message ?? `API ${yanit.status} döndü`,
        };
      });
      await queryClient.invalidateQueries({ queryKey: ['ptw-izinler'] });
      await queryClient.invalidateQueries({ queryKey: ['ptw-izin'] });
      return sonuc;
    } finally {
      setSenkronKosuyor(false);
    }
  }, [token, queryClient]);

  // `surum` bilinçli bağımlılık: depo değiştiğinde liste yeniden okunur.
  const kayitlar = useMemo(() => kuyruguOku(), [surum]);
  const catismalar = useMemo(() => catismalariOku(), [surum]);
  return { kayitlar, catismalar, senkron, catismalariTemizle, senkronKosuyor };
}

// ── F3·S6: PTW TÜR ŞABLONLARI + YAPILANDIRMA YÜZEYİ ─────────────────────────

const ptwTypeFormlariSchema = z.array(ptwTypeFormSchema);

/** ÖNİZLEME — talep açılmadan, seçilen türlerin şablon alanları. Yer tutucu
 *  bölge (S4) BUNUNLA dolar; kayıt yoktur, kullanıcı ne dolduracağını görür. */
export function useIzinTuruFormlari(
  izinTuruRefIds: readonly string[],
): UseQueryResult<PtwTypeForm[]> {
  const token = useAccessToken();
  const anahtar = [...izinTuruRefIds].sort().join(',');
  return useQuery({
    queryKey: ['ptw-tur-formlari', anahtar],
    queryFn: ({ signal }) =>
      apiGet(`/api/ptw/type-forms?izinTuruRefIds=${anahtar}`, token, ptwTypeFormlariSchema, signal),
    enabled: anahtar !== '',
  });
}

/** İZİN BAZLI — şablon + varsa DOLU KAYIT; şema kaydın KENDİ sürümünden. */
export function useIzinFormlari(permitId: string | null): UseQueryResult<PtwTypeForm[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-izin-formlari', permitId],
    queryFn: ({ signal }) =>
      apiGet(`/api/ptw/permits/${permitId!}/forms`, token, ptwTypeFormlariSchema, signal),
    enabled: permitId !== null,
  });
}

/** PTW yapılandırma yüzeyi — TEK okuma (S0-K3 "tek yapılandırma yüzeyi"). */
export function usePtwConfig(): UseQueryResult<PtwConfigOverview> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ptw-config'],
    queryFn: ({ signal }) => apiGet('/api/ptw/config', token, ptwConfigOverviewSchema, signal),
  });
}

/**
 * Yapılandırma yazımı — DÖRT uç, TEK hook: hepsi aynı yanıtı (tüm görünüm)
 * döndürür, dolayısıyla ekranın tazeleme yolu da tektir. Sunucu kuralları
 * (saha kontrol listesi boş olamaz → 422) ekranda TEKRARLANMAZ; hata mesajı
 * sunucudan gelir (§2.16).
 */
export function usePtwConfigYazma(): UseMutationResult<
  PtwConfigOverview,
  Error,
  { yol: 'type-gorev' | 'workplace-type' | 'authority' | 'qr-kapsam'; govde: unknown }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ yol, govde }) =>
      apiPut(`/api/ptw/config/${yol}`, token, govde, ptwConfigOverviewSchema),
    onSuccess: (veri) => {
      queryClient.setQueryData(['ptw-config'], veri);
      // Matris uygunluk motoruna girdi: değişince açık izinlerin uygunluk
      // önizlemesi de bayatlar.
      void queryClient.invalidateQueries({ queryKey: ['ptw-uygunluk'] });
    },
  });
}

/**
 * DİLİM 2 — KONTROL MATRİSİ HÜCRESİ (K-73a…d). Yanıt yapılandırma görünümünün
 * TAMAMIDIR (`usePtwConfigYazma` deseni); matris uygunluk motoruna girdi
 * olduğu için açık izinlerin uygunluğu, pano ve defter de bayatlar.
 */
export function usePtwKontrolMatrisiYaz(): UseMutationResult<
  PtwConfigOverview,
  Error,
  PtwKontrolMatrisYazma
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (govde) =>
      apiPut('/api/ptw/config/control-matrix', token, govde, ptwConfigOverviewSchema),
    onSuccess: (veri) => {
      queryClient.setQueryData(['ptw-config'], veri);
      void queryClient.invalidateQueries({ queryKey: ['ptw-uygunluk'] });
      void queryClient.invalidateQueries({ queryKey: ['ptw-onay-uygunluk'] });
      void queryClient.invalidateQueries({ queryKey: ['ptw-izin'] });
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['ayar-defteri'] });
    },
  });
}

/**
 * MADDE 248 / K-248 — MALİYET ÖZETİ. Aile açılmadan önce, TIKLAMA ANINDA
 * hesaplanır; önbelleğe alınmaz (her açma o anki sayıyı görür — kaydedilmez).
 */
export function usePtwAileMaliyeti(): UseMutationResult<
  PtwAileMaliyeti,
  Error,
  { aile: PtwMaliyetAilesi; workplaceId: string | null }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({ aile, workplaceId }) =>
      apiGet(
        `/api/ptw/config/control-matrix/maliyet?aile=${aile}` +
          (workplaceId ? `&workplaceId=${workplaceId}` : ''),
        token,
        ptwAileMaliyetiSchema,
      ),
  });
}

/** DİLİM 2 — AYAR DEFTERİ (K-73g); `anahtar` null = tüm anahtarlar. Sayfalı. */
export function useAyarDefteri(
  anahtar: string | null,
  sayfa: number,
): UseQueryResult<TenantSettingLogListesi> {
  const token = useAccessToken();
  const params = new URLSearchParams({ sayfa: String(sayfa) });
  if (anahtar) params.set('anahtar', anahtar);
  return useQuery({
    queryKey: ['ayar-defteri', anahtar, sayfa],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/tenant-settings/log?${params.toString()}`,
        token,
        tenantSettingLogListesiSchema,
        signal,
      ),
  });
}

// ─── Olay modülü (F3B·S2 — Kapsam §16.8/14) ─────────────────────────────────

const incidentsSchema = z.array(incidentSummarySchema);
const workHoursListSchema = z.array(workHoursRowSchema);

export function useOlaylar(): UseQueryResult<IncidentSummary[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['incidents', 'liste'],
    queryFn: ({ signal }) => apiGet('/api/incidents', token, incidentsSchema, signal),
  });
}

export function useOlayOptions(): UseQueryResult<IncidentOptions> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['incidents', 'options'],
    queryFn: ({ signal }) => apiGet('/api/incidents/options', token, incidentOptionsSchema, signal),
  });
}

export function useOlay(id: string | null): UseQueryResult<IncidentDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['incidents', 'detay', id],
    queryFn: ({ signal }) => apiGet(`/api/incidents/${id}`, token, incidentDetailSchema, signal),
    enabled: id !== null,
  });
}

function useOlayTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    void queryClient.invalidateQueries({ queryKey: ['zil'] });
  };
}

export function useOlayAc(): UseMutationResult<BildirimSonucu, Error, IncidentCreateInput> {
  const token = useAccessToken();
  const tazele = useOlayTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/incidents', token, input, bildirimSonucuSchema),
    onSuccess: tazele,
  });
}

/**
 * Durum/inceleme işlemleri + bağlı liste EKLEMELERİ — hepsi detay döner.
 * Düğme görünürlüğü SUNUCUDAN (`yetkiler`): ekran ikinci kural yazmaz,
 * görünmeyen düğmenin ucu da aynı kapıdan reddeder (S4 deseni).
 */
export function useOlayEylem(): UseMutationResult<
  IncidentDetail,
  Error,
  { id: string; eylem: string; govde?: unknown }
> {
  const token = useAccessToken();
  const tazele = useOlayTazele();
  return useMutation({
    mutationFn: ({ id, eylem, govde }) =>
      apiPost(`/api/incidents/${id}/${eylem}`, token, govde ?? {}, incidentDetailSchema),
    onSuccess: tazele,
  });
}

/**
 * ✏️ T2·0: `useOlayKapat` KALKTI — kapanış bir işlem değil, türetmedir
 * (`IncidentSummary.kapali`). Tür düzeltme AYRI hook: yanıt ENGELLEMEYEN
 * uyarı listesi taşır (K8 — aynı-akış değişiminde "içerik korunur").
 */
export function useOlayTurDuzelt(): UseMutationResult<
  IncidentOlayTuruUpdateResponse,
  Error,
  { id: string; olayTuruRefId: string }
> {
  const token = useAccessToken();
  const tazele = useOlayTazele();
  return useMutation({
    mutationFn: ({ id, olayTuruRefId }) =>
      apiPost(
        `/api/incidents/${id}/olay-turu`,
        token,
        { olayTuruRefId },
        incidentOlayTuruUpdateResponseSchema,
      ),
    onSuccess: tazele,
  });
}

/** QR üretme (F3B·S4 — madde 64): yetki `olay_inceleme` listesi + bahçe. */
export function useQrUret(): UseMutationResult<
  z.output<typeof incidentQrSonucSchema>,
  Error,
  z.infer<typeof incidentQrUretSchema>
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (input) => apiPost('/api/incidents/qr', token, input, incidentQrSonucSchema),
  });
}

const digerKuyruguSchema = z.array(digerKuyruguSatirSchema);

/** "Diğer" kuyruğu (T3 — F3B·S4): serbest metinler + sıklık. */
export function useDigerKuyrugu(): UseQueryResult<z.output<typeof digerKuyruguSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['incidents', 'diger-kuyrugu'],
    queryFn: ({ signal }) =>
      apiGet('/api/incidents/diger-kuyrugu', token, digerKuyruguSchema, signal),
  });
}

/** Kuyruktan kalıcı kalem — MEVCUT nüsha mekanizması, `olay_inceleme` kapısı. */
export function useDigerKalemEkle(): UseMutationResult<
  LookupItemDetail,
  Error,
  z.infer<typeof digerKuyrukKalemSchema>
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiPost('/api/incidents/diger-kuyrugu/kalem', token, input, lookupItemDetailSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
      void queryClient.invalidateQueries({ queryKey: ['lookup'] });
    },
  });
}

export function useOlaySatirSil(): UseMutationResult<
  IncidentDetail,
  Error,
  {
    id: string;
    liste: 'affected-persons' | 'damages' | 'causes' | 'kronoloji' | 'investigators' | 'injuries';
    satirId: string;
  }
> {
  const token = useAccessToken();
  const tazele = useOlayTazele();
  return useMutation({
    mutationFn: ({ id, liste, satirId }) =>
      apiDelete(`/api/incidents/${id}/${liste}/${satirId}`, token, incidentDetailSchema),
    onSuccess: tazele,
  });
}

/**
 * Sebep ağacı düğümünü düzenle/taşı (F4·B1 — O1). AYRI hook çünkü METOT
 * ayrı (PATCH); gövde ve yanıt `useOlayEylem` deseninin aynısıdır — detay
 * döner, düğme görünürlüğü sunucudan gelir.
 */
export function useOlaySebepGuncelle(): UseMutationResult<
  IncidentDetail,
  Error,
  { id: string; satirId: string; govde: IncidentCauseUpdateInput }
> {
  const token = useAccessToken();
  const tazele = useOlayTazele();
  return useMutation({
    mutationFn: ({ id, satirId, govde }) =>
      apiPatch(`/api/incidents/${id}/causes/${satirId}`, token, govde, incidentDetailSchema),
    onSuccess: tazele,
  });
}

/**
 * SAHA GÜNÜ — "bugün" TEK KAYNAKTAN (madde 302, 08.10.2026; Teknoloji §8).
 * Tarih alanlarının varsayılan değeri ve `max` sınırı tarayıcı gününden DEĞİL
 * buradan okunur (`lib/saha-gunu.ts useVarsayilanGun`). Uç `@Public`tir
 * (oturumsuz public bildirim sayfası da okur) — jeton gerekmez; yanıt yine
 * şemadan geçer (tip köprüsü). Bir dakika taze: açık kalan sekmede form
 * yeniden açılınca gün yeniden sorulur; gün değişimi sunucuda, tarayıcıda değil.
 */
export function useSahaGunu(): UseQueryResult<string> {
  return useQuery({
    queryKey: ['saha-gunu'],
    queryFn: async ({ signal }) => {
      const response = await fetch('/api/saha-gunu', { signal });
      if (!response.ok) throw new Error(`API ${response.status} döndü`);
      return sahaGunuYanitiSchema.parse(await response.json()).sahaGunu;
    },
    staleTime: 60_000,
  });
}

/**
 * Etkilenen kişi satırını güncelle (madde 309-e, 08.10.2026): basamak · kayıp
 * gün · tedavi notu. `useOlaySebepGuncelle` deseni (PATCH, detay döner).
 * ⚠️ Ölümlüye geçişte `kayipGun` GÖNDERİLMEZ — sunucu mevcut değeri aynı
 * işlemde NULL yapar; ekran yanıttaki satırdan "alan temizlendi" bilgisini okur.
 */
export function useOlayEtkilenenGuncelle(): UseMutationResult<
  IncidentDetail,
  Error,
  { id: string; satirId: string; govde: IncidentAffectedUpdateInput }
> {
  const token = useAccessToken();
  const tazele = useOlayTazele();
  return useMutation({
    mutationFn: ({ id, satirId, govde }) =>
      apiPatch(
        `/api/incidents/${id}/affected-persons/${satirId}`,
        token,
        govde,
        incidentDetailSchema,
      ),
    onSuccess: tazele,
  });
}

/** Seviye ölçeği yapılandırması (F3B·S3 — O6 + D9; yazma yalnız holding). */
export function useSeviyeConfig(): UseQueryResult<IncidentSeverityConfig> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['incident-severity'],
    queryFn: ({ signal }) =>
      apiGet('/api/incident-severity/config', token, incidentSeverityConfigSchema, signal),
  });
}

/** Tek hook, üç yol (PtwConfigYazma deseni): kademe güncelle · kademe sil
 *  (kullanılmışta 422 — ekran silme sunmaz, uç savunması) · kural satırları. */
export function useSeviyeConfigYazma(): UseMutationResult<
  IncidentSeverityConfig,
  Error,
  | { tur: 'level'; id: string; govde: SeverityLevelUpdateInput }
  | { tur: 'rules'; sinifRefId: string; govde: SeverityRulesUpdateInput }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (istek) =>
      istek.tur === 'level'
        ? apiPut(
            `/api/incident-severity/levels/${istek.id}`,
            token,
            istek.govde,
            incidentSeverityConfigSchema,
          )
        : apiPut(
            `/api/incident-severity/rules/${istek.sinifRefId}`,
            token,
            istek.govde,
            incidentSeverityConfigSchema,
          ),
    onSuccess: (veri) => {
      queryClient.setQueryData(['incident-severity'], veri);
      // Türetilen seviye rozetleri bayatlamasın.
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    },
  });
}

export function useCalismaSaatleri(): UseQueryResult<WorkHoursRow[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['work-hours'],
    queryFn: ({ signal }) => apiGet('/api/work-hours', token, workHoursListSchema, signal),
  });
}

export function useCalismaSaatiYaz(): UseMutationResult<
  WorkHoursRow[],
  Error,
  WorkHoursUpsertInput
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => apiPut('/api/work-hours', token, input, workHoursListSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-hours'] });
    },
  });
}

/** İçe aktarma (F3B·S5 — §16.8.1): dosya SUNUCUDA parse edilir; her satır
 *  `upsertWorkHours` tek kapısından geçer — kısmi başarı raporla döner. */
export function useCalismaSaatiImport(): UseMutationResult<WorkHoursImportReport, Error, File> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dosya) =>
      apiUpload('/api/work-hours/import', token, dosya, workHoursImportReportSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-hours'] });
    },
  });
}

// ─── Kaza dosyası ekleri (F3B·S5 — R13; aksiyon eki hook deseninin olay kolu) ─

const olayEkleriSchema = z.array(incidentAttachmentSchema);

export function useOlayEkleri(
  incidentId: string | null,
): UseQueryResult<IncidentAttachmentDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['incidents', 'ekler', incidentId],
    queryFn: ({ signal }) =>
      apiGet(`/api/incidents/${incidentId}/attachments`, token, olayEkleriSchema, signal),
    enabled: incidentId !== null,
  });
}

function useOlayEkTazele(): (incidentId: string) => void {
  const queryClient = useQueryClient();
  return (incidentId) => {
    void queryClient.invalidateQueries({ queryKey: ['incidents', 'ekler', incidentId] });
    // "Dosya eksik" işareti liste + detayda türetilir — ek değişince bayatlar.
    void queryClient.invalidateQueries({ queryKey: ['incidents', 'liste'] });
    void queryClient.invalidateQueries({ queryKey: ['incidents', 'detay', incidentId] });
  };
}

export function useOlayEkYukle(): UseMutationResult<
  IncidentAttachmentDetail,
  Error,
  {
    incidentId: string;
    dosya: File;
    ekTipi: IncidentEkTipi;
    /** Kanıt kategorisi (F4·B2·2) — opsiyonel; boş = kategorisiz (O6). */
    kanitKategorisi?: IncidentKanitKategorisi | null;
  }
> {
  const token = useAccessToken();
  const tazele = useOlayEkTazele();
  return useMutation({
    mutationFn: ({ incidentId, dosya, ekTipi, kanitKategorisi }) =>
      apiUpload(
        `/api/incidents/${incidentId}/attachments`,
        token,
        dosya,
        incidentAttachmentSchema,
        {
          ekTipi,
          ...(kanitKategorisi ? { kanitKategorisi } : {}),
        },
      ),
    onSuccess: (_veri, { incidentId }) => tazele(incidentId),
  });
}

export function useOlayEkSil(): UseMutationResult<
  { silindi: true },
  Error,
  { incidentId: string; attachmentId: string }
> {
  const token = useAccessToken();
  const tazele = useOlayEkTazele();
  return useMutation({
    mutationFn: ({ incidentId, attachmentId }) =>
      apiDelete(
        `/api/incidents/${incidentId}/attachments/${attachmentId}`,
        token,
        silmeSonucuSchema,
      ),
    onSuccess: (_veri, { incidentId }) => tazele(incidentId),
  });
}

export function useOlayEkIndir(): UseMutationResult<
  void,
  Error,
  { incidentId: string; attachmentId: string; dosyaAdi: string }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async ({ incidentId, attachmentId, dosyaAdi }) => {
      const { blob, dosyaAdi: sunucuAdi } = await apiDownload(
        `/api/incidents/${incidentId}/attachments/${attachmentId}/download`,
        token,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = sunucuAdi ?? dosyaAdi;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

// ── Eğitim modülü (F3B·S7 — Kapsam §16.10) ──────────────────────────────────

/**
 * Y1 (tur 5) — LİSTE SUNUCUDA SÜZÜLÜR VE SAYFALANIR (ekipman Y3 ikizi).
 * Süzgeç dizgisi `egitimSuzgecSorgusu`dan — çıktı ucu aynı dizgiyi kurar.
 */
export function useEgitimler(suzgec: EgitimSuzgeci): UseQueryResult<TrainingList> {
  const token = useAccessToken();
  const sorgu = egitimSuzgecSorgusu(suzgec, true);
  return useQuery({
    queryKey: ['trainings', 'liste', sorgu],
    queryFn: ({ signal }) => apiGet(`/api/trainings${sorgu}`, token, trainingListSchema, signal),
  });
}

/**
 * Y4 (tur 5) — eğitim kayıtlarının toplu yüklenmesi (motorun 6. müşterisi).
 * Rapor satır düzeyindedir; oturum bağlantısı `trainingSessionId` ile döner.
 */
export function useEgitimYukle(): UseMutationResult<TrainingImportReport, Error, { dosya: File }> {
  const token = useAccessToken();
  const tazele = useEgitimTazele();
  return useMutation({
    mutationFn: ({ dosya }) =>
      apiUpload('/api/trainings/import', token, dosya, trainingImportReportSchema),
    onSuccess: tazele,
  });
}

/**
 * SÖ-1 (02.11.2026) — SİSTEM ÖNCESİ YÜKLEME, ADIM 1: önizleme. Dosya sunucuda
 * ayrıştırılır, benzersiz konular + önerilen tür + sayılar döner; **hiçbir kayda
 * dokunmaz** (`import/persons/preview` emsali) — bu yüzden liste tazelenmez.
 */
export function useEgitimDevirOnizle(): UseMutationResult<
  TrainingImportPreview,
  Error,
  { dosya: File }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({ dosya }) =>
      apiUpload('/api/trainings/import/preview', token, dosya, trainingImportPreviewSchema),
  });
}

/**
 * SÖ-1 — ADIM 2: yükleme. Aynı uç, `kip=sistem_oncesi`; konu → tür eşlemesi
 * multipart metin alanında JSON olarak gider (doğrulama SUNUCUDA).
 */
export function useEgitimDevirYukle(): UseMutationResult<
  TrainingImportReport,
  Error,
  { dosya: File; konuEslemesi: { metin: string; egitimTuruId: string | null }[] }
> {
  const token = useAccessToken();
  const tazele = useEgitimTazele();
  return useMutation({
    mutationFn: ({ dosya, konuEslemesi }) =>
      apiUpload(
        '/api/trainings/import?kip=sistem_oncesi',
        token,
        dosya,
        trainingImportReportSchema,
        {
          konuEslemesi: JSON.stringify(konuEslemesi),
        },
      ),
    onSuccess: tazele,
  });
}

export function useEgitimOptions(): UseQueryResult<z.output<typeof trainingOptionsSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['trainings', 'options'],
    queryFn: ({ signal }) => apiGet('/api/trainings/options', token, trainingOptionsSchema, signal),
  });
}

export function useEgitim(
  id: string | null,
): UseQueryResult<z.output<typeof trainingDetailSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['trainings', 'detay', id],
    queryFn: ({ signal }) => apiGet(`/api/trainings/${id}`, token, trainingDetailSchema, signal),
    enabled: id !== null,
  });
}

function useEgitimTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['trainings'] });
    // Yetkinlik doğduğu/düştüğü için kişi kartı da bayatlar (tek yazıcı).
    void queryClient.invalidateQueries({ queryKey: ['persons'] });
  };
}

export function useEgitimAc(): UseMutationResult<
  z.output<typeof trainingDetailSchema>,
  Error,
  z.input<typeof trainingCreateSchema>
> {
  const token = useAccessToken();
  const tazele = useEgitimTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/trainings', token, input, trainingDetailSchema),
    onSuccess: tazele,
  });
}

/**
 * Kayıt üstündeki tüm yazma işlemleri — hepsi DETAY döner (olay `useOlayEylem`
 * deseni): düğme görünürlüğü SUNUCUDAN (`yetkiler`), ekran ikinci kural
 * yazmaz. `yol` uç sonekidir (participants · konular · qr yok — QR ayrı).
 */
export function useEgitimEylem(): UseMutationResult<
  z.output<typeof trainingDetailSchema>,
  Error,
  { id: string; yol: string; govde?: unknown; metot?: 'post' | 'patch' | 'delete' }
> {
  const token = useAccessToken();
  const tazele = useEgitimTazele();
  return useMutation({
    mutationFn: ({ id, yol, govde, metot }) => {
      const url = yol === '' ? `/api/trainings/${id}` : `/api/trainings/${id}/${yol}`;
      if (metot === 'patch') return apiPatch(url, token, govde ?? {}, trainingDetailSchema);
      if (metot === 'delete') return apiDelete(url, token, trainingDetailSchema);
      return apiPost(url, token, govde ?? {}, trainingDetailSchema);
    },
    onSuccess: tazele,
  });
}

/** Değerlendirme QR'ı (§16.10/9-b) — katılımcı listesi BOŞKEN de çalışır. */
export function useEgitimQrUret(): UseMutationResult<
  z.output<typeof trainingQrSonucSchema>,
  Error,
  { id: string }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({ id }) => apiPost(`/api/trainings/${id}/qr`, token, {}, trainingQrSonucSchema),
  });
}

/** Kayıt eki (T1 — imzalı katılım formunun taraması). */
export function useEgitimEkYukle(): UseMutationResult<unknown, Error, { id: string; dosya: File }> {
  const token = useAccessToken();
  const tazele = useEgitimTazele();
  return useMutation({
    mutationFn: ({ id, dosya }) =>
      apiUpload(`/api/trainings/${id}/attachments`, token, dosya, z.unknown()),
    onSuccess: tazele,
  });
}

export function useEgitimEkSil(): UseMutationResult<unknown, Error, { id: string; ekId: string }> {
  const token = useAccessToken();
  const tazele = useEgitimTazele();
  return useMutation({
    mutationFn: ({ id, ekId }) =>
      apiDelete(`/api/trainings/${id}/attachments/${ekId}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

// ── Sağlık gözetimi (F3B·S8 — Kapsam §16.11) ────────────────────────────────
//
// ⚠️ Bu uçların GUARD'ı DARDIR (§16.11/10): izleyici ve saha sorumlusu 403
// alır. Ekran ikinci bir yetki kuralı YAZMAZ — menü görünürlüğü
// `my-authorization`dan sadeleşir, karar her istekte sunucudadır (§2.16/B3).

const healthOpinionsSchema = z.array(healthOpinionSummarySchema);

/**
 * Kişi kartının *Hekim kanaati ve kısıtlar* bölümü.
 *
 * ⚠️ **SAĞLIK KURALI (madde 291 — yetki artçısı 2, 30.09.2026): SAĞLIK UCU,
 * SAĞLIK OKUMA LİSTESİNDE OLMAYAN ŞAPKADA ASLA ÇAĞRILMAZ.** Koruma kancanın
 * İÇİNDEDİR (`ikincilOkuma.saglikKanaati` — `SAGLIK_OKUMA_SAPKALARI`nın
 * kendisi). Önceki hâlde ekran yetkisiz şapkada kancaya `null` veriyor, kanca
 * kişi süzgeci olmadan `GET health-opinions`ı çağırıyordu: izleyici, alan
 * sorumlusu ve çalışan kişi kartını her açtığında sağlık ucuna bir istek
 * gidiyor ve 403 dönüyordu (persona yoklaması kişi kartına genişleyince
 * ölçüldü). Veri sızmıyordu; çağrının kendisi kuralın ihlaliydi.
 */
export function useSaglikKanaatleri(
  personId: string,
): UseQueryResult<z.output<typeof healthOpinionsSchema>> {
  const token = useAccessToken();
  const kapi = useIkincilOkuma('saglikKanaati');
  return useQuery({
    queryKey: ['health-opinions', 'liste', personId],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/health-opinions?personId=${encodeURIComponent(personId)}`,
        token,
        healthOpinionsSchema,
        signal,
      ),
    enabled: kapi.acik,
  });
}

export function useSaglikOptions(): UseQueryResult<z.output<typeof healthOpinionOptionsSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['health-opinions', 'options'],
    queryFn: ({ signal }) =>
      apiGet('/api/health-opinions/options', token, healthOpinionOptionsSchema, signal),
  });
}

export function useSaglikKanaati(
  id: string | null,
): UseQueryResult<z.output<typeof healthOpinionDetailSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['health-opinions', 'detay', id],
    queryFn: ({ signal }) =>
      apiGet(`/api/health-opinions/${id}`, token, healthOpinionDetailSchema, signal),
    enabled: id !== null,
  });
}

function useSaglikTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['health-opinions'] });
    // Kanaat izin uygunluğunu besler (§16.11/11) — izin ekranları bayatlar.
    void queryClient.invalidateQueries({ queryKey: ['ptw'] });
  };
}

const kanaatIdSchema = z.object({ id: z.uuid() });

export function useSaglikKanaatKaydet(): UseMutationResult<
  z.output<typeof kanaatIdSchema>,
  Error,
  { id?: string; govde: z.input<typeof healthOpinionCreateSchema> }
> {
  const token = useAccessToken();
  const tazele = useSaglikTazele();
  return useMutation({
    mutationFn: ({ id, govde }) =>
      id
        ? apiPatch(`/api/health-opinions/${id}`, token, govde, kanaatIdSchema)
        : apiPost('/api/health-opinions', token, govde, kanaatIdSchema),
    onSuccess: tazele,
  });
}

export function useSaglikKanaatSil(): UseMutationResult<unknown, Error, { id: string }> {
  const token = useAccessToken();
  const tazele = useSaglikTazele();
  return useMutation({
    mutationFn: ({ id }) => apiDelete(`/api/health-opinions/${id}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

/** İmzalı Ek-2'nin taranmış kopyası — OPSİYONEL ek (zorunlu DEĞİL). */
export function useSaglikEkYukle(): UseMutationResult<unknown, Error, { id: string; dosya: File }> {
  const token = useAccessToken();
  const tazele = useSaglikTazele();
  return useMutation({
    mutationFn: ({ id, dosya }) =>
      apiUpload(`/api/health-opinions/${id}/attachments`, token, dosya, z.unknown()),
    onSuccess: tazele,
  });
}

export function useSaglikEkSil(): UseMutationResult<unknown, Error, { id: string; ekId: string }> {
  const token = useAccessToken();
  const tazele = useSaglikTazele();
  return useMutation({
    mutationFn: ({ id, ekId }) =>
      apiDelete(`/api/health-opinions/${id}/attachments/${ekId}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

// ── Risk değerlendirme (F3B·S9 — Kapsam §16.12) ─────────────────────────────
//
// Okuma GENEL GET kuralına tabidir (izleyici DAHİL) — sağlık kanaatinin dar
// listesi buraya UZANMAZ: risk maddesi kişisel veri taşımaz. Yazma kapısı
// tenant `risk_degerlendirme` listesi + bahçedir ve **sunucudadır**; ekran
// düğmeyi `yetkiler` alanından açar (S4 deseni — ikinci kural yazılmaz).

/** R7 — liste sunucuda sayfalanır ve aranır (tur 9/10 deseni). */
export interface RiskListeSuzgeci {
  workplaceId?: string | null;
  q?: string;
  durum?: 'aktif' | 'arsivlenmis' | null;
  sayfa?: number;
}

export function useRiskDegerlendirmeleri(
  suzgec: RiskListeSuzgeci = {},
): UseQueryResult<z.output<typeof riskAssessmentListSchema>> {
  const token = useAccessToken();
  const parametreler = new URLSearchParams();
  if (suzgec.workplaceId) parametreler.set('workplaceId', suzgec.workplaceId);
  if (suzgec.q?.trim()) parametreler.set('q', suzgec.q.trim());
  if (suzgec.durum) parametreler.set('durum', suzgec.durum);
  if (suzgec.sayfa && suzgec.sayfa > 1) parametreler.set('sayfa', String(suzgec.sayfa));
  const sorgu = parametreler.toString();
  return useQuery({
    queryKey: ['risk-assessments', 'liste', sorgu],
    queryFn: ({ signal }) =>
      apiGet(
        sorgu ? `/api/risk-assessments?${sorgu}` : '/api/risk-assessments',
        token,
        riskAssessmentListSchema,
        signal,
      ),
  });
}

export function useRiskOptions(): UseQueryResult<z.output<typeof riskAssessmentOptionsSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['risk-assessments', 'options'],
    queryFn: ({ signal }) =>
      apiGet('/api/risk-assessments/options', token, riskAssessmentOptionsSchema, signal),
  });
}

/** R7 — madde listesi de sunucuda sayfalanır/süzülür (asıl ölçek riski). */
export interface RiskMaddeSuzgeciUi {
  q?: string;
  seviye?: string | null;
  /** ✏️ §16.12/16-j — `eksik` = tamamlanmamış maddeler (skor/risk eksik). */
  durum?: string | null;
  sayfa?: number;
}

export function useRiskDegerlendirmesi(
  id: string | null,
  maddeSuzgeci: RiskMaddeSuzgeciUi = {},
): UseQueryResult<z.output<typeof riskAssessmentDetailSchema>> {
  const token = useAccessToken();
  const parametreler = new URLSearchParams();
  if (maddeSuzgeci.q?.trim()) parametreler.set('maddeQ', maddeSuzgeci.q.trim());
  if (maddeSuzgeci.seviye) parametreler.set('maddeSeviye', maddeSuzgeci.seviye);
  if (maddeSuzgeci.durum) parametreler.set('maddeDurum', maddeSuzgeci.durum);
  if (maddeSuzgeci.sayfa && maddeSuzgeci.sayfa > 1) {
    parametreler.set('maddeSayfa', String(maddeSuzgeci.sayfa));
  }
  const sorgu = parametreler.toString();
  return useQuery({
    queryKey: ['risk-assessments', 'detay', id, sorgu],
    queryFn: ({ signal }) =>
      apiGet(
        sorgu ? `/api/risk-assessments/${id}?${sorgu}` : `/api/risk-assessments/${id}`,
        token,
        riskAssessmentDetailSchema,
        signal,
      ),
    enabled: id !== null,
  });
}

/** Tenant'ın VARSAYILAN metodolojisi + o yöntemin bant tablosu. */
export function useRiskBantlari(): UseQueryResult<z.output<typeof riskBandListSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['risk-assessments', 'bantlar'],
    queryFn: ({ signal }) => apiGet('/api/risk-bands', token, riskBandListSchema, signal),
  });
}

/**
 * ⚠️ **BELİRLİ BİR YÖNTEMİN BANT TABLOSU (tur 11 / R2).** Eşik artık bant
 * tablosundan TÜRETİLDİĞİ için tablo **düzenlenebilir olmak zorundadır** —
 * `PUT /api/risk-bands` F3B·S9'dan beri vardı ve **çağıranı yoktu**
 * (h-3 sınıfı: uç var, ekran yok). Yapılandırma ekranı artık onu çağırır.
 */
export function useRiskBantTablosu(
  metodoloji: string,
  options?: { enabled?: boolean },
): UseQueryResult<z.output<typeof riskBandListSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['risk-assessments', 'bantlar', metodoloji],
    queryFn: ({ signal }) =>
      apiGet(`/api/risk-bands/${metodoloji}`, token, riskBandListSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

export function useRiskBantYaz(): UseMutationResult<
  GuncellemeSonucu,
  Error,
  z.input<typeof riskBandWriteSchema>
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (govde) => apiPut('/api/risk-bands', token, govde, guncellemeSonucuSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['risk-assessments'] });
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
  });
}

/** HEDEF SORGU 1 — olay detayındaki panel (§16.12/12). */
export function useOlayRiskMaddeleri(
  incidentId: string | null,
): UseQueryResult<z.output<typeof riskEslesmeSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['risk-assessments', 'olay', incidentId],
    queryFn: ({ signal }) =>
      apiGet(`/api/incidents/${incidentId}/risk-items`, token, riskEslesmeSchema, signal),
    enabled: incidentId !== null,
  });
}

/** HEDEF SORGU 2 — ekipman kartındaki panel. */
export function useEkipmanRiskMaddeleri(
  equipmentId: string | null,
): UseQueryResult<z.output<typeof riskEslesmeSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['risk-assessments', 'ekipman', equipmentId],
    queryFn: ({ signal }) =>
      apiGet(`/api/equipment/${equipmentId}/risk-items`, token, riskEslesmeSchema, signal),
    enabled: equipmentId !== null,
  });
}

function useRiskTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['risk-assessments'] });
    // Risk maddesinden açılan DÖF aksiyon listelerinde görünür (§16.12/1).
    void queryClient.invalidateQueries({ queryKey: ['actions'] });
  };
}

const riskIdSchema = z.object({ id: z.uuid() });

export function useRiskDegerlendirmeKaydet(): UseMutationResult<
  GuncellemeSonucu | z.output<typeof riskIdSchema>,
  Error,
  { id?: string; govde: Record<string, unknown> }
> {
  const token = useAccessToken();
  const tazele = useRiskTazele();
  return useMutation({
    mutationFn: ({ id, govde }): Promise<GuncellemeSonucu | z.output<typeof riskIdSchema>> =>
      id
        ? apiPatch(`/api/risk-assessments/${id}`, token, govde, guncellemeSonucuSchema)
        : apiPost('/api/risk-assessments', token, govde, riskIdSchema),
    onSuccess: tazele,
  });
}

export function useRiskDegerlendirmeSil(): UseMutationResult<unknown, Error, { id: string }> {
  const token = useAccessToken();
  const tazele = useRiskTazele();
  return useMutation({
    mutationFn: ({ id }) => apiDelete(`/api/risk-assessments/${id}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

export function useRiskMaddesiKaydet(): UseMutationResult<
  GuncellemeSonucu | z.output<typeof riskIdSchema>,
  Error,
  { id: string; itemId?: string; govde: Record<string, unknown> }
> {
  const token = useAccessToken();
  const tazele = useRiskTazele();
  return useMutation({
    mutationFn: ({
      id,
      itemId,
      govde,
    }): Promise<GuncellemeSonucu | z.output<typeof riskIdSchema>> =>
      itemId
        ? apiPatch(
            `/api/risk-assessments/${id}/items/${itemId}`,
            token,
            govde,
            guncellemeSonucuSchema,
          )
        : apiPost(`/api/risk-assessments/${id}/items`, token, govde, riskIdSchema),
    onSuccess: tazele,
  });
}

export function useRiskMaddesiSil(): UseMutationResult<
  unknown,
  Error,
  { id: string; itemId: string }
> {
  const token = useAccessToken();
  const tazele = useRiskTazele();
  return useMutation({
    mutationFn: ({ id, itemId }) =>
      apiDelete(`/api/risk-assessments/${id}/items/${itemId}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

/** Ekip üyeliği KATILIM KAYDIDIR — yetki vermez (R6). */
export function useRiskEkipUyesiEkle(): UseMutationResult<
  unknown,
  Error,
  { id: string; personId: string; gorevMetni?: string | null }
> {
  const token = useAccessToken();
  const tazele = useRiskTazele();
  return useMutation({
    mutationFn: ({ id, personId, gorevMetni }) =>
      apiPost(
        `/api/risk-assessments/${id}/members`,
        token,
        { personId, gorevMetni: gorevMetni ?? null },
        riskIdSchema,
      ),
    onSuccess: tazele,
  });
}

export function useRiskEkipUyesiCikar(): UseMutationResult<
  unknown,
  Error,
  { id: string; memberId: string }
> {
  const token = useAccessToken();
  const tazele = useRiskTazele();
  return useMutation({
    mutationFn: ({ id, memberId }) =>
      apiDelete(`/api/risk-assessments/${id}/members/${memberId}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

/** SÜRÜM DONDURMA (R1) — R5 kapısı sunucuda; uyarılar yanıtla döner. */
export function useRiskSurumDondur(): UseMutationResult<
  { id: string; surumNo: number; uyarilar: string[] },
  Error,
  { id: string; not?: string | null }
> {
  const token = useAccessToken();
  const tazele = useRiskTazele();
  return useMutation({
    mutationFn: ({ id, not }) =>
      apiPost(
        `/api/risk-assessments/${id}/freeze`,
        token,
        { not: not ?? null },
        z.object({ id: z.uuid(), surumNo: z.number().int(), uyarilar: z.array(z.string()) }),
      ),
    onSuccess: tazele,
  });
}

// ── EKSİK KAYIT LİSTELERİ (F4·S1 — Kapsam §22.2/9 detay yüzeyi) ─────────────
//
// Günlük özet SAYI gönderir (Y1); SATIRI bu uçlar açar. Ayrı bir "eksikler"
// panosu AÇILMADI (açık madde A3 — 2. oturum): mevcut ekranlara SÜZGEÇ
// DEĞERİ olarak bağlanır. Yetki kaynak modülünkiyle aynıdır ve SUNUCUDADIR;
// ekran ikinci kural yazmaz (§2.16/B3).

const eksikKisilerSchema = z.array(eksikKisiSchema);
const eksikIsyerleriSchema = z.array(eksikIsyeriSchema);

/** Temel İSG eğitimi kaydı HİÇ olmayan kişiler (eğitim okuma kuralı). */
export function useEksikTemelIsg(
  aktif: boolean,
): UseQueryResult<z.output<typeof eksikKisilerSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['trainings', 'eksik-temel-isg'],
    queryFn: ({ signal }) =>
      apiGet('/api/trainings/eksik-temel-isg', token, eksikKisilerSchema, signal),
    enabled: aktif,
  });
}

/**
 * P1 — SAYFALI SAĞLIK LİSTESİ (tur 7). Süzgeç ve sayfalama **SUNUCUDA**;
 * ekran ikinci bir süzgeç yazmaz ve **eşiği kendi hesaplamaz** (P2 —
 * `uyariEsigiGun` yanıttan gelir; ekranda `30` sabiti YAŞAMAZ).
 */
export function useSaglikListesi(suzgec: {
  workplaceId: string | null;
  vade: 'dolmus' | 'yaklasan' | null;
  sayfa: number;
}): UseQueryResult<z.output<typeof healthOpinionListSchema>> {
  const token = useAccessToken();
  const sorgu = saglikSuzgecSorgusu(suzgec);
  return useQuery({
    queryKey: ['health-opinions', 'sayfali', sorgu],
    queryFn: ({ signal }) =>
      apiGet(`/api/health-opinions/liste${sorgu}`, token, healthOpinionListSchema, signal),
  });
}

/**
 * Süzgeç dizgisi TEK FONKSİYONDAN — ekran ve ÇIKTI aynı sorguyu kurar
 * (ekipman Y5 dersi: *"çıktı ekranın sorgusunu tekrar koşar"*).
 */
export function saglikSuzgecSorgusu(suzgec: {
  workplaceId: string | null;
  vade: 'dolmus' | 'yaklasan' | null;
  sayfa?: number;
}): string {
  const p = new URLSearchParams();
  if (suzgec.workplaceId) p.set('workplaceId', suzgec.workplaceId);
  if (suzgec.vade) p.set('vade', suzgec.vade);
  if (suzgec.sayfa !== undefined) p.set('sayfa', String(suzgec.sayfa));
  const q = p.toString();
  return q ? `?${q}` : '';
}

/** Sağlık gözetimi listesi → Excel (P4). Kısıt sütunu ÇIKTIYA GİRMEZ. */
export function useSaglikListesiIndir(): UseMutationResult<
  void,
  Error,
  { workplaceId: string | null; vade: 'dolmus' | 'yaklasan' | null }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async (suzgec) => {
      const { blob, dosyaAdi } = await apiDownload(
        `/api/health-opinions/export.xlsx${saglikSuzgecSorgusu(suzgec)}`,
        token,
      );
      indirmeyiBaslat(blob, dosyaAdi ?? 'saglik-gozetimi.xlsx');
    },
  });
}

/**
 * İBYS düzeninde dışa aktarma (J) — gönderim DEĞİL aktarma.
 *
 * ⚠️ **P3: HATA ARTIK GÖRÜNÜR.** Eskiden ekran `if (!yanit.ok) return`
 * diyordu: yetkisiz ya da geçersiz süzgeçli bir istek **sessizce hiçbir şey
 * yapmıyordu** ve kullanıcı düğmenin bozuk olduğunu sanıyordu (§2.40'ın
 * *"sessizce düşer"* kolu). Mutasyon hatayı yükseltir, ekran onu yazar.
 */
export function useIbysDisaAktar(): UseMutationResult<void, Error, { workplaceId: string | null }> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async ({ workplaceId }) => {
      const sorgu = workplaceId ? `?workplaceId=${encodeURIComponent(workplaceId)}` : '';
      const { blob, dosyaAdi } = await apiDownload(`/api/health-opinions/export${sorgu}`, token);
      indirmeyiBaslat(blob, dosyaAdi ?? 'ibys-muayene.csv');
    },
  });
}

/** Kanaat raporunu ÜRET (P5) — sağlık konteynerine ek olarak yazılır. */
export function useKanaatBelgesiUret(
  opinionId: string,
): UseMutationResult<{ id: string; dosyaAdi: string }, Error, void> {
  const token = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost(
        `/api/health-opinions/${opinionId}/belge`,
        token,
        {},
        z.object({ id: z.uuid(), dosyaAdi: z.string() }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health-opinion', opinionId] });
    },
  });
}

export function indirmeyiBaslat(blob: Blob, dosyaAdi: string): void {
  const url = URL.createObjectURL(blob);
  const bag = document.createElement('a');
  bag.href = url;
  bag.download = dosyaAdi;
  document.body.appendChild(bag);
  bag.click();
  bag.remove();
  URL.revokeObjectURL(url);
}

/**
 * P6 (tur 7) — geçmiş muayene durumunun toplu aktarımı (motorun 7. müşterisi).
 * Rapor satır düzeyindedir; belge AKTARILMAZ (kayıt "belgesiz" doğar).
 */
export function useSaglikKanaatYukle(): UseMutationResult<
  z.output<typeof healthOpinionImportReportSchema>,
  Error,
  { dosya: File }
> {
  const token = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dosya }) =>
      apiUpload('/api/health-opinions/import', token, dosya, healthOpinionImportReportSchema),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health-opinions'] });
    },
  });
}

/** Sağlık kanaati HİÇ olmayan kişiler — DAR okuma listesi (§16.11/10). */
export function useEksikSaglikKanaati(
  aktif: boolean,
): UseQueryResult<z.output<typeof eksikKisilerSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['health-opinions', 'eksik'],
    queryFn: ({ signal }) =>
      apiGet('/api/health-opinions/eksik', token, eksikKisilerSchema, signal),
    enabled: aktif,
  });
}

/**
 * KİŞİNİN KENDİ SAĞLIK KAYDI — **YALNIZ İKİ TARİH** (§16.11/10; tur 7 / U1).
 *
 * ⚠️ Uç F4·S1'den beri vardı ve **hiçbir ekran çağırmıyordu**: kişiye vade
 * bildirimi gidiyor, tıklayacağı yüzey 403 veriyordu. Şema `strict` — karar ·
 * kısıt · hekim · tetkik alanları cevapta **boş değil, YOK** (kısıtlama API
 * kenarında, ekran gizlemesiyle değil).
 */
export function useKendiSaglikKaydim(): UseQueryResult<z.output<typeof kendiSaglikVadesiSchema>> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['health-opinions', 'kendi'],
    queryFn: ({ signal }) =>
      apiGet('/api/health-opinions/kendi', token, kendiSaglikVadesiSchema, signal),
    // Kişi dosyasına bağlı olmayan hesapta 403 döner — ekran BÖLÜMÜ ÇİZMEZ
    // (§2.40: çalışmayacak kontrolü göstermektense göstermemek).
    retry: false,
  });
}

/**
 * KİŞİNİN İBYS ÇALIŞMA PROFİLİ (tur 7 / U2; §16.11/9-C).
 *
 * ⚠️ **İKİ UÇ, SIFIR WEB ÇAĞIRANI (17 gün).** Alanlar F3B·S8'de açıldı,
 * İBYS CSV'sinin son 11 sütununu onlar besler — ve **hiçbir ekran onları
 * yazmıyordu**, yani her dışa aktarmada o sütunlar **daima boş** çıkıyordu.
 *
 * ⚠️ **KOD LİSTELERİ İNMEZ, ALANLAR SERBEST METİNDİR** (J/C kararı): ISCO ve
 * Bakanlık sınıf listeleri entegratörlük turunda (Faz 5) iner; buraya
 * enum/lookup BAĞLANMAZ ve **değer UYDURULMAZ** (Yönerge §5).
 */
export function useCalismaProfili(
  personId: string,
): UseQueryResult<z.output<typeof personWorkProfileDetailSchema>> {
  const token = useAccessToken();
  // Sağlık kuralı (madde 291): kapı sağlık kanaatinin kapısıyla AYNI — sağlık
  // okuma listesinde olmayan şapkada uç çağrılmaz (koruma kancanın içinde).
  const kapi = useIkincilOkuma('saglikKanaati');
  return useQuery({
    queryKey: ['person-work-profile', personId],
    queryFn: ({ signal }) =>
      apiGet(`/api/person-work-profile/${personId}`, token, personWorkProfileDetailSchema, signal),
    enabled: kapi.acik,
  });
}

export function useCalismaProfiliYaz(
  personId: string,
): UseMutationResult<{ personId: string }, Error, z.input<typeof personWorkProfileSchema>> {
  const token = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (govde) =>
      apiPut(
        `/api/person-work-profile/${personId}`,
        token,
        govde,
        z.object({ personId: z.uuid() }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person-work-profile', personId] });
    },
  });
}

/** Yayınlanmış risk değerlendirmesi olmayan işyerleri (R1 — sürüm ölçütü). */
export function useEksikRiskDegerlendirmesi(): UseQueryResult<
  z.output<typeof eksikIsyerleriSchema>
> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['risk-assessments', 'eksik'],
    queryFn: ({ signal }) =>
      apiGet('/api/risk-assessments/eksik', token, eksikIsyerleriSchema, signal),
  });
}

// ── BEŞİNCİ MOTORUN YÜZEYİ (F4·S5·B2 — Kapsam §22.6a/T8-T9 + §20.5/T10) ─────
//
// ⚠️ **GÖREV KAPATMA MUTASYONU YOKTUR ve yazılmaz** — öyle bir uç da yok:
// kapanış kontrol kaydının YAN ETKİSİDİR (§22.6a kilidi).

/** Görev listesinin süzgeç ekseni — P4'ün "hatırlanan ama görünür" tarafı. */
export interface GorevSuzgeci {
  durum: 'hepsi' | 'bekliyor' | 'yapildi' | 'kacirildi' | 'gecersiz';
  ekipmanId: string | null;
  sayfa: number;
}

export const GOREV_SUZGEC_VARSAYILANI: GorevSuzgeci = {
  durum: 'bekliyor',
  ekipmanId: null,
  sayfa: 1,
};

/** Varsayılandan sapan süzgeç sayısı — göstergenin TEK kaynağı. */
export function etkinGorevSuzgeci(suzgec: GorevSuzgeci): number {
  let sayi = 0;
  if (suzgec.durum !== GOREV_SUZGEC_VARSAYILANI.durum) sayi += 1;
  if (suzgec.ekipmanId !== null) sayi += 1;
  return sayi;
}

export function useGorevler(suzgec: GorevSuzgeci): UseQueryResult<TaskOccurrenceList> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['gorevler', 'liste', suzgec.durum, suzgec.ekipmanId, suzgec.sayfa],
    queryFn: ({ signal }) => {
      const parametreler = new URLSearchParams({
        durum: suzgec.durum,
        sayfa: String(suzgec.sayfa),
      });
      if (suzgec.ekipmanId) parametreler.set('ekipmanId', suzgec.ekipmanId);
      return apiGet(
        `/api/tasks/occurrences?${parametreler.toString()}`,
        token,
        taskOccurrenceListSchema,
        signal,
      );
    },
  });
}

export function useGorev(
  id: string,
  secenek: { enabled?: boolean } = {},
): UseQueryResult<TaskOccurrenceDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['gorevler', 'detay', id],
    enabled: secenek.enabled ?? true,
    queryFn: ({ signal }) =>
      apiGet(`/api/tasks/occurrences/${id}`, token, taskOccurrenceDetailSchema, signal),
  });
}

export function useGorevTanimlari(askida?: boolean): UseQueryResult<TaskAssignmentSummary[]> {
  const token = useAccessToken();
  // T15 (F4·S6) — askı SÜZGECİ SUNUCUDA (§2.16/B3: ekran ikinci filtre yazmaz).
  const qs = askida === undefined ? '' : `?askida=${askida ? 'true' : 'false'}`;
  return useQuery({
    queryKey: ['gorevler', 'tanimlar', askida ?? 'hepsi'],
    queryFn: ({ signal }) =>
      apiGet(`/api/tasks/assignments${qs}`, token, z.array(taskAssignmentSummarySchema), signal),
  });
}

/**
 * GÖREV ATAMA UYARISI — atama anındaki önizleme (Kapsam §22.6a). Nesne ve
 * hedef seçilince sunucunun yüklemi sorulur; tanım listesi aynı yüklemi satır
 * satır taşır. `girdi` eksikse sorgu gönderilmez.
 */
export interface GorevHedefSorgusu {
  ekipmanId: string | null;
  isyeriId: string | null;
  hedefTipi: 'kisi' | 'kume';
  hedefPersonId: string | null;
  kumeKaynagi: 'operator_listesi' | 'sapka_bahce' | null;
  kumeSapkalar: readonly string[];
}

export function useGorevHedefDurumu(
  girdi: GorevHedefSorgusu | null,
): UseQueryResult<GorevHedefDurumu> {
  const token = useAccessToken();
  const sorgu = new URLSearchParams();
  if (girdi) {
    if (girdi.ekipmanId) sorgu.set('ekipmanId', girdi.ekipmanId);
    if (girdi.isyeriId) sorgu.set('isyeriId', girdi.isyeriId);
    sorgu.set('hedefTipi', girdi.hedefTipi);
    if (girdi.hedefPersonId) sorgu.set('hedefPersonId', girdi.hedefPersonId);
    if (girdi.kumeKaynagi) sorgu.set('kumeKaynagi', girdi.kumeKaynagi);
    if (girdi.kumeSapkalar.length > 0) sorgu.set('kumeSapkalar', girdi.kumeSapkalar.join(','));
  }
  return useQuery({
    queryKey: ['gorevler', 'hedef-durumu', sorgu.toString()],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/tasks/assignments/hedef-durumu?${sorgu.toString()}`,
        token,
        gorevHedefDurumuSchema,
        signal,
      ),
    enabled: girdi !== null,
  });
}

export function useGorevSecenekleri(): UseQueryResult<TaskOptions> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['gorevler', 'secenekler'],
    queryFn: ({ signal }) => apiGet('/api/tasks/options', token, taskOptionsSchema, signal),
  });
}

/**
 * DASHBOARD *"SÜRESİ GEÇENLER"* BLOĞU (T8).
 *
 * ⚠️ Bu bir **DURUM ÖZETİDİR** (konu ekseni), **İşlerim'in kuyruğu değil**
 * (kimlik ekseni) — `useIslerim`e KATILMAZ. Birleşirse ya blok kişiselleşir
 * (G7 ihlali) ya İşlerim kişisel olmaktan çıkar (IA §3/8).
 */
export function useSuresiGecenler(): UseQueryResult<TaskOverdueSummary> {
  const token = useAccessToken();
  // Çalışan · hekim · YSP'de blok ÇAĞRILMAZ (kendi gecikmiş görevleri İşlerim'de).
  const kapi = useIkincilOkuma('gecikmisGorev');
  return useQuery({
    queryKey: ['gorevler', 'suresi-gecenler'],
    queryFn: ({ signal }) => apiGet('/api/tasks/overdue', token, taskOverdueSummarySchema, signal),
    enabled: kapi.acik,
  });
}

function useGorevTazele(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['gorevler'] });
    await queryClient.invalidateQueries({ queryKey: ['ekipman-durum'] });
    // Madde 290: tanım silinince / kapatılınca açık görevleri `gecersiz`
    // kapanır — Genel bakış takvimi de aynı anda eskir.
    await queryClient.invalidateQueries({ queryKey: ['dashboard', 'operasyonel'] });
  };
}

export function useGorevTanimiAc(): UseMutationResult<
  TaskAssignmentSummary,
  Error,
  TaskAssignmentCreateInput
> {
  const token = useAccessToken();
  const tazele = useGorevTazele();
  return useMutation({
    mutationFn: (input) =>
      apiPost('/api/tasks/assignments', token, input, taskAssignmentSummarySchema),
    onSuccess: tazele,
  });
}

export function useGorevTanimiGuncelle(): UseMutationResult<
  TaskAssignmentSummary,
  Error,
  { id: string; input: TaskAssignmentUpdateInput }
> {
  const token = useAccessToken();
  const tazele = useGorevTazele();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiPatch(`/api/tasks/assignments/${id}`, token, input, taskAssignmentSummarySchema),
    onSuccess: tazele,
  });
}

/** Silme GÜNCEL LİSTEYİ döner (204 değil) — ortak istemci her yanıtı
 *  şemadan geçirir ve boş gövde o sözleşmeyi bozardı. */
export function useGorevTanimiSil(): UseMutationResult<TaskAssignmentSummary[], Error, string> {
  const token = useAccessToken();
  const tazele = useGorevTazele();
  return useMutation({
    mutationFn: (id) =>
      apiDelete(`/api/tasks/assignments/${id}`, token, z.array(taskAssignmentSummarySchema)),
    onSuccess: tazele,
  });
}

// ── EKİPMAN DURUMU (T10/#5 · #6) ────────────────────────────────────────────

export function useEkipmanDurumu(equipmentId: string): UseQueryResult<EquipmentDurumGecmisi> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ekipman-durum', equipmentId],
    queryFn: ({ signal }) =>
      apiGet(`/api/equipment/${equipmentId}/durum`, token, equipmentDurumGecmisiSchema, signal),
  });
}

export function useEkipmanDurumGecisi(): UseMutationResult<
  EquipmentDurumGecmisi,
  Error,
  { equipmentId: string; input: EquipmentDurumGecisInput }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ equipmentId, input }) =>
      apiPost(`/api/equipment/${equipmentId}/durum`, token, input, equipmentDurumGecmisiSchema),
    onSuccess: async () => {
      // Pasife alma AÇIK GÖREVLERİ `gecersiz` kapatır (Z5) — görev listesi ve
      // dashboard bloğu aynı anda eskir; tek yerde tazelenir.
      await queryClient.invalidateQueries({ queryKey: ['ekipman-durum'] });
      await queryClient.invalidateQueries({ queryKey: ['gorevler'] });
      await queryClient.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
}

// ── TUR 4 / K1 — EKİPMAN KONTROL GEÇMİŞİ (§16.5) ───────────────────────────
//
// ⚠️ `equipment.sonKontrol` BU KAYITLARDAN TÜRETİLİR — her yazma sonrası
// ekipman listesi de eskir (rozet ve "sonraki kontrol" oradan okunur).

export function useEkipmanKontrolleri(
  equipmentId: string,
): UseQueryResult<EquipmentInspectionDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['ekipman-kontroller', equipmentId],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/equipment/${equipmentId}/inspections`,
        token,
        z.array(equipmentInspectionSchema),
        signal,
      ),
    enabled: equipmentId !== '',
  });
}

function kontrolTazele(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return (async () => {
    await queryClient.invalidateQueries({ queryKey: ['ekipman-kontroller'] });
    // `sonKontrol` / `sonrakiKontrol` TÜRETİLMİŞTİR: kayıt değişince ekipman
    // listesi ve durum ekranı da eskir — tek yerde tazelenir.
    await queryClient.invalidateQueries({ queryKey: ['equipment'] });
  })();
}

export function useKontrolKaydiEkle(): UseMutationResult<
  EquipmentInspectionDetail,
  Error,
  { equipmentId: string; input: EquipmentInspectionCreateInput }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ equipmentId, input }) =>
      apiPost(`/api/equipment/${equipmentId}/inspections`, token, input, equipmentInspectionSchema),
    onSuccess: () => kontrolTazele(queryClient),
  });
}

export function useKontrolKaydiSil(): UseMutationResult<SilmeSonucu, Error, string> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inspectionId) =>
      apiDelete(`/api/equipment-inspections/${inspectionId}`, token, silmeSonucuSchema),
    onSuccess: () => kontrolTazele(queryClient),
  });
}

export function useKontrolRaporuYukle(): UseMutationResult<
  EquipmentInspectionDetail,
  Error,
  { inspectionId: string; dosya: File }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ inspectionId, dosya }) =>
      apiUpload(
        `/api/equipment-inspections/${inspectionId}/rapor`,
        token,
        dosya,
        equipmentInspectionSchema,
      ),
    onSuccess: () => kontrolTazele(queryClient),
  });
}

// ── F4·S11·B — YASAL UYGUNLUK / YÜKÜMLÜLÜK TAKVİMİ (§2.37) ─────────────────

/**
 * ⚠️ **EKRAN İKİNCİ KURAL YAZMAZ** (§2.16): doğum kararı, periyot, kademe ve
 * durum rozeti **sunucuda** çözülür; buradaki tek iş yanıtı şemadan geçirmek.
 * *"İşyerinin sınıfı çok tehlikeliyse..."* diye dallanan bir ekran, aynı
 * kuralı ikinci kez tanımlar ve iki tanım zamanla ayrışır.
 */
export function useYukumlulukTakvimi(workplaceId: string | null): UseQueryResult<YukumlulukTakvim> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['yukumluluk', 'takvim', workplaceId],
    enabled: workplaceId !== null,
    queryFn: ({ signal }) =>
      apiGet(`/api/obligations/workplaces/${workplaceId!}`, token, yukumlulukTakvimSchema, signal),
  });
}

/**
 * **İ5 — ELLE İŞARET.** Uç takvimin tamamını döner (işaret doğum kararını
 * değiştirmese de `isaretSorgulanmali` alanını etkiler); yanıt doğrudan
 * önbelleğe yazılır, ikinci istek atılmaz.
 */
export function useYukumlulukIsaret(): UseMutationResult<
  YukumlulukTakvim,
  Error,
  { workplaceId: string; kalemId: string; elleIsaret: boolean }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, kalemId, elleIsaret }) =>
      apiPut(
        `/api/obligations/workplaces/${workplaceId}/items/${kalemId}/isaret`,
        token,
        { elleIsaret },
        yukumlulukTakvimSchema,
      ),
    onSuccess: (takvim) => {
      queryClient.setQueryData(['yukumluluk', 'takvim', takvim.workplaceId], takvim);
    },
  });
}

// ── TUR 8 / B — İKİ MOD · ŞERH · GÖZDEN GEÇİRME ─────────────────────────────
//
// ⚠️ **DÖRDÜ DE GÜNCEL TAKVİMİ DÖNER** (`useYukumlulukIsaret` sözleşmesi):
// önbelleğe doğrudan yazılır ve ekran **yeniden istek atmadan** tazelenir.
// Yol testinde `page.reload()` görmenin bir İTİRAF olmasının sebebi budur
// (Yönerge §6/9-a): kullanıcı yenilemez.

/** B1 — kalemi `elle` ↔ `bagli` moda alma. */
export function useYukumlulukMod(): UseMutationResult<
  YukumlulukTakvim,
  Error,
  { workplaceId: string; kalemId: string; mod: 'elle' | 'bagli' }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, kalemId, mod }) =>
      apiPut(
        `/api/obligations/workplaces/${workplaceId}/items/${kalemId}/mod`,
        token,
        { mod },
        yukumlulukTakvimSchema,
      ),
    onSuccess: (takvim) => {
      queryClient.setQueryData(['yukumluluk', 'takvim', takvim.workplaceId], takvim);
    },
  });
}

/**
 * B2 — elle moddaki durum işareti (`durum: null` işareti KALDIRIR).
 * ✏️ **Gezinti artçısı 2 / B4.2:** gövde YALNIZ durum taşır — gerekçe alanı
 * kalktı; açıklama ayrı uçtan yazılan nottadır (`useYukumlulukNot`).
 */
export function useYukumlulukDurum(): UseMutationResult<
  YukumlulukTakvim,
  Error,
  {
    workplaceId: string;
    kalemId: string;
    durum: 'uygun' | 'uygun_degil' | 'kapsam_disi' | null;
  }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, kalemId, durum }) =>
      apiPut(
        `/api/obligations/workplaces/${workplaceId}/items/${kalemId}/durum`,
        token,
        { durum },
        yukumlulukTakvimSchema,
      ),
    onSuccess: (takvim) => {
      queryClient.setQueryData(['yukumluluk', 'takvim', takvim.workplaceId], takvim);
    },
  });
}

/**
 * ✏️ **Gezinti artçısı 2 / B4.2 — NOT** (serbest metin; boş metin notu
 * kaldırır). ⚠️ Durumdan AYRI yazılır: eski ekranda gerekçe yalnız durum
 * seçilirken gönderiliyordu ve seçimden sonra yazılan metin kaydedilmiyordu.
 */
export function useYukumlulukNot(): UseMutationResult<
  YukumlulukTakvim,
  Error,
  { workplaceId: string; kalemId: string; notu: string | null }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, kalemId, notu }) =>
      apiPut(
        `/api/obligations/workplaces/${workplaceId}/items/${kalemId}/not`,
        token,
        { notu },
        yukumlulukTakvimSchema,
      ),
    onSuccess: (takvim) => {
      queryClient.setQueryData(['yukumluluk', 'takvim', takvim.workplaceId], takvim);
    },
  });
}

/** B3 — şerh düşme (yalnız bağlı modda; türetileni EZMEZ, yanına yazılır). */
export function useYukumlulukSerh(): UseMutationResult<
  YukumlulukTakvim,
  Error,
  {
    workplaceId: string;
    kalemId: string;
    durum: 'uygun' | 'uygun_degil' | 'kapsam_disi';
    gerekce: string;
  }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, kalemId, durum, gerekce }) =>
      apiPost(
        `/api/obligations/workplaces/${workplaceId}/items/${kalemId}/serh`,
        token,
        { durum, gerekce },
        yukumlulukTakvimSchema,
      ),
    onSuccess: (takvim) => {
      queryClient.setQueryData(['yukumluluk', 'takvim', takvim.workplaceId], takvim);
    },
  });
}

/** B3 — şerhi kaldırma (soft-delete; tarihçe kalır). */
export function useYukumlulukSerhKaldir(): UseMutationResult<
  YukumlulukTakvim,
  Error,
  { workplaceId: string; serhId: string }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, serhId }) =>
      apiDelete(
        `/api/obligations/workplaces/${workplaceId}/serhler/${serhId}`,
        token,
        yukumlulukTakvimSchema,
      ),
    onSuccess: (takvim) => {
      queryClient.setQueryData(['yukumluluk', 'takvim', takvim.workplaceId], takvim);
    },
  });
}

/** B4 — gözden geçirme kaydı; o dönemin şerhlerini DÜŞÜRÜR. */
export function useYukumlulukGozdenGecirme(): UseMutationResult<
  YukumlulukTakvim,
  Error,
  { workplaceId: string; tarih: string; aciklama?: string | null }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, tarih, aciklama }) =>
      apiPost(
        `/api/obligations/workplaces/${workplaceId}/gozden-gecirme`,
        token,
        { tarih, aciklama: aciklama ?? null },
        yukumlulukTakvimSchema,
      ),
    onSuccess: (takvim) => {
      queryClient.setQueryData(['yukumluluk', 'takvim', takvim.workplaceId], takvim);
    },
  });
}

// ── TUR 8 / C2 · C3 · D2 — KÜTÜPHANE KURALI · MÜŞTERİ KALEMİ · KANIT EKİ ───

const yukumlulukKalemListSchema = z.array(yukumlulukKalemSchema);

/**
 * ⚠️ **BU UÇ ON GÜN BOYUNCA ÇAĞRILMADI ve HER ÇAĞRIDA 500 VERİYORDU** (H1).
 * Kütüphane kural ekranı (C2) onun **ilk tüketicisidir** — *"uç var" cümlesi
 * "yol var" cümlesi değildir* (Yönerge §6/7).
 *
 * ⚠️ **GİZLİLER DE GELİR:** bu bir YAPILANDIRMA listesidir, takvim değil.
 * Gizli kalemin geri açılabilmesi için görünmesi gerekir.
 */
export function useYukumlulukKalemleri(): UseQueryResult<YukumlulukKalem[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['yukumluluk', 'kalemler'],
    queryFn: ({ signal }) => apiGet('/api/obligations', token, yukumlulukKalemListSchema, signal),
  });
}

/** C2 — nüshanın KURAL alanları (kimlik alanları şemada YOK — 422). */
export function useYukumlulukKalemGuncelle(): UseMutationResult<
  YukumlulukKalem,
  Error,
  { id: string; yama: YukumlulukKalemGuncelle }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, yama }) =>
      apiPatch(`/api/obligations/${id}`, token, yama, yukumlulukKalemSchema),
    onSuccess: () => kutuphaneTazele(queryClient),
  });
}

/** §19/9 — nüsha SİLİNMEZ, GİZLENİR. Ayrı uç, ayrı şapka (holding). */
export function useYukumlulukGizle(): UseMutationResult<
  YukumlulukKalem,
  Error,
  { id: string; gizli: boolean }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, gizli }) =>
      apiPatch(`/api/obligations/${id}/gizli`, token, { gizli }, yukumlulukKalemSchema),
    onSuccess: () => kutuphaneTazele(queryClient),
  });
}

/** C3 — müşterinin kendi kalemi (`kaynak` GİRDİDE YOK: `ic_prosedur`). */
export function useYukumlulukKalemAc(): UseMutationResult<
  YukumlulukKalem,
  Error,
  YukumlulukKalemOlustur
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (govde) => apiPost('/api/obligations', token, govde, yukumlulukKalemSchema),
    onSuccess: () => kutuphaneTazele(queryClient),
  });
}

/**
 * ⚠️ **KÜTÜPHANE DEĞİŞİNCE TAKVİM DE BAYATLAR** — periyot/eşik/gizlilik
 * doğum kararını ve vadeyi değiştirir. İki anahtar birlikte tazelenir; yalnız
 * kütüphaneyi tazeleyen kod, ekranda **eski takvimi** bırakır.
 */
function kutuphaneTazele(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['yukumluluk'] });
}

/**
 * **D2 — KANIT EKİ.** ⚠️ Yanıt takvim DEĞİLDİR (yükleme tek kaleme dokunur);
 * bu yüzden önbelleğe doğrudan yazılmaz, takvim **geçersizleştirilir**.
 */
export function useYukumlulukEkYukle(): UseMutationResult<
  { id: string },
  Error,
  { workplaceId: string; kalemId: string; dosya: File }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, kalemId, dosya }) =>
      apiUpload(
        `/api/obligations/workplaces/${workplaceId}/items/${kalemId}/attachments`,
        token,
        dosya,
        z.object({ id: z.uuid() }),
      ),
    onSuccess: () => kutuphaneTazele(queryClient),
  });
}

export function useYukumlulukEkSil(): UseMutationResult<
  { silindi: boolean },
  Error,
  { workplaceId: string; ekId: string }
> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workplaceId, ekId }) =>
      apiDelete(
        `/api/obligations/workplaces/${workplaceId}/attachments/${ekId}`,
        token,
        z.object({ silindi: z.boolean() }),
      ),
    onSuccess: () => kutuphaneTazele(queryClient),
  });
}

// ── TUR 4½ / AŞAMA B — GÖREVLENDİRME (§25; kurallar `.claude/rules/gorevlendirme.md`) ──
//
// ⚠️ **BU HOOK'LAR YETKİ TAŞIMAZ.** Görevlendirme bir NİTELİK kaydıdır;
// şapka/bahçe hook'larıyla (`useAppUsers` ailesi) hiçbir yerde birleşmez —
// `gorevlendirme.ts`in `roles.ts`ten import etmemesi kuralının ekran kolu.

const gorevlendirmeListSchema = z.array(gorevlendirmeSchema);

/**
 * Kişinin görevlendirmeleri — **kaldırılanlar DAHİL** (§25/1: *"olay
 * tarihinde ekipte kim vardı"* sorusu bu satırlardan cevaplanır).
 */
export function useGorevlendirmeler(personId: string): UseQueryResult<Gorevlendirme[]> {
  const token = useAccessToken();
  // MADDE 291 — kişi kartını her iç şapka açar, görevlendirme bölümünü yalnız
  // görevlendirme okuma listesi görür; listede olmayan şapkada uç ÇAĞRILMAZ
  // (bayrak ucun `@RequireRole` sabitinden — `ikincilOkuma.gorevlendirme`).
  const kapi = useIkincilOkuma('gorevlendirme');
  return useQuery({
    queryKey: ['gorevlendirmeler', personId],
    queryFn: ({ signal }) =>
      apiGet(`/api/persons/${personId}/designations`, token, gorevlendirmeListSchema, signal),
    enabled: kapi.acik,
  });
}

/**
 * G6 — kişi uygunluk paneli. **TAM LİSTE, eksikler değil.**
 * ⚠️ Panel OKUR, saklamaz: damga her istekte sunucuda türetilir (§25/5).
 */
export function useKisiUygunluk(personId: string): UseQueryResult<KisiUygunlukPaneli> {
  const token = useAccessToken();
  // MADDE 295 — koruma kancanın İÇİNDE: panel görünmüyorsa uç ÇAĞRILMAZ.
  const gorunur = useUygunlukPaneliGorunur(personId);
  return useQuery({
    queryKey: ['kisi-uygunluk', personId],
    queryFn: ({ signal }) =>
      apiGet(`/api/persons/${personId}/uygunluk`, token, kisiUygunlukPaneliSchema, signal),
    enabled: gorunur,
  });
}

/**
 * MADDE 295 — UYGUNLUK PANELİ BU KARTTA ÇİZİLİR Mİ (ve uç çağrılır mı — ikisi
 * aynı cevap). İki kol, sunucudaki yüklemin ikizi (`uygunlukPaneliErisimi`):
 *   · ŞAPKA : `ikincilOkuma.uygunlukPaneli` (panel okuma listesi — sunucudan)
 *   · KENDİ : kart kullanıcının KENDİ kişisiyse (`my-authorization.personId`)
 * Çalışan başkasının kartında paneli görmez ve uç ÇAĞRILMAZ (403 de almaz).
 * Ekran şapka dizgesi yazmaz; bahçe kararı uçtadır.
 */
export function useUygunlukPaneliGorunur(personId: string): boolean {
  const sapkaKolu = useIkincilOkuma('uygunlukPaneli');
  const authz = useMyAuthorization();
  const kendiKarti = authz.data?.personId != null && authz.data.personId === personId;
  return sapkaKolu.acik || kendiKarti;
}

export function useGorevlendirmeEkle(
  personId: string,
): UseMutationResult<Gorevlendirme, Error, GorevlendirmeOlustur> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiPost(`/api/persons/${personId}/designations`, token, input, gorevlendirmeSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gorevlendirmeler', personId] });
      // ⚠️ PANEL DE TAZELENİR: yeni görevlendirme yeni ŞART doğurur ve panel
      // aynı anda değişir; tazelemezsek kullanıcı damgayı görüp gerekçesini
      // göremezdi.
      void queryClient.invalidateQueries({ queryKey: ['kisi-uygunluk', personId] });
      void queryClient.invalidateQueries({ queryKey: ['gorevlendirme-listesi'] });
      // G3.1 — kurul üyeliği görevlendirmedir: kurul sayfası da aynı kayıttan okur.
      void queryClient.invalidateQueries({ queryKey: ['kurul'] });
    },
  });
}

/** ⚠️ **KALDIRMA SİLME DEĞİLDİR** — gövdedeki tarih yazılır, satır yaşar. */
export function useGorevlendirmeKaldir(
  personId: string,
): UseMutationResult<{ ok: true }, Error, { id: string; kaldirmaTarihi: string }> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, kaldirmaTarihi }) =>
      apiDelete(
        `/api/persons/${personId}/designations/${id}`,
        token,
        z.object({ ok: z.literal(true) }),
        { kaldirmaTarihi },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gorevlendirmeler', personId] });
      void queryClient.invalidateQueries({ queryKey: ['kisi-uygunluk', personId] });
      void queryClient.invalidateQueries({ queryKey: ['gorevlendirme-listesi'] });
    },
  });
}

export interface GorevlendirmeListeSuzgeci {
  workplaceId: string | null;
  gorevlendirmeTuruId: string | null;
  yalnizEksik: boolean;
  sayfa: number;
}

export const BOS_GOREVLENDIRME_LISTE_SUZGECI: GorevlendirmeListeSuzgeci = {
  workplaceId: null,
  gorevlendirmeTuruId: null,
  yalnizEksik: false,
  sayfa: 1,
};

export function etkinGorevlendirmeSuzgeciSayisi(suzgec: GorevlendirmeListeSuzgeci): number {
  return (
    (suzgec.workplaceId ? 1 : 0) +
    (suzgec.gorevlendirmeTuruId ? 1 : 0) +
    (suzgec.yalnizEksik ? 1 : 0)
  );
}

/** B3 — tür bazlı liste (*"bu işyerinde ilkyardımcılar kimler"*). */
export function useGorevlendirmeListesi(
  suzgec: GorevlendirmeListeSuzgeci,
): UseQueryResult<GorevlendirmeListesi> {
  const token = useAccessToken();
  const sorgu = new URLSearchParams();
  if (suzgec.workplaceId) sorgu.set('workplaceId', suzgec.workplaceId);
  if (suzgec.gorevlendirmeTuruId) sorgu.set('gorevlendirmeTuruId', suzgec.gorevlendirmeTuruId);
  if (suzgec.yalnizEksik) sorgu.set('eksik', 'true');
  sorgu.set('sayfa', String(suzgec.sayfa));
  const dizge = sorgu.toString();
  return useQuery({
    queryKey: ['gorevlendirme-listesi', dizge],
    queryFn: ({ signal }) =>
      apiGet(`/api/designations?${dizge}`, token, gorevlendirmeListesiSchema, signal),
  });
}

/**
 * B4 — **SÖZLÜK KALEMİNİN KURALINI YAZAR** (`PATCH /api/lookup/items/:id`).
 *
 * ⚠️ **YENİ UÇ AÇILMADI:** kural `lookup_item.kurallar`ta yaşar ve o ucun
 * *"kurallar düzenlemesi TAM SERBEST"* kuralı (§19/7) zaten geçerlidir.
 * Görevlendirme türüne özel bir yazma yolu açmak, aynı alanı **iki kapıdan**
 * yazılabilir yapardı.
 */
export function useLookupKuralYaz(
  listeKodu: string,
): UseMutationResult<LookupItemDetail, Error, { id: string; kurallar: Record<string, unknown> }> {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, kurallar }) =>
      apiPatch(`/api/lookup/items/${id}`, token, { kurallar }, lookupItemDetailSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lookup', listeKodu] });
      // Kural değişince türetilmiş damgalar da değişir (§25/5).
      void queryClient.invalidateQueries({ queryKey: ['gorevlendirme-listesi'] });
      void queryClient.invalidateQueries({ queryKey: ['kisi-uygunluk'] });
      void queryClient.invalidateQueries({ queryKey: ['gorevlendirmeler'] });
      // K7 (tur 5): eğitim türü periyodu değişince doğmuş yetkinlikler
      // sunucuda tazelendi — kişi kartı rozetleri de bayatlar.
      void queryClient.invalidateQueries({ queryKey: ['competencies'] });
      void queryClient.invalidateQueries({ queryKey: ['trainings'] });
      // 5½: zorunlu sayı kuralı değişince işyeri türetmeleri de bayatlar.
      void queryClient.invalidateQueries({ queryKey: ['zorunlu-gorevlendirmeler'] });
    },
  });
}

// ── 5½ / A1 — GENEL SÖZLÜK YÖNETİMİ (Kapsam §19/16) ─────────────────────────
// `POST lookup/:listeKodu` F1·S2'den beri vardı; İLK ÇAĞIRANI bu ekrandır
// (*"31 liste, 0 ekleme yüzeyi"* — tur 5 keşfi 0b-i). Yeni uç YOK.

function useSozlukTazele(listeKodu: string): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['lookup', listeKodu] });
    void queryClient.invalidateQueries({ queryKey: ['lookup'] });
    void queryClient.invalidateQueries({ queryKey: ['incidents', 'diger-kuyrugu'] });
    void queryClient.invalidateQueries({ queryKey: ['gorevlendirme-listesi'] });
    void queryClient.invalidateQueries({ queryKey: ['kisi-uygunluk'] });
    void queryClient.invalidateQueries({ queryKey: ['zorunlu-gorevlendirmeler'] });
  };
}

/** Kalem ekleme — müşteri kalemi (kütüphane kalemi API'den açılamaz, §19/6). */
export function useLookupKalemEkle(
  listeKodu: string,
): UseMutationResult<
  LookupItemDetail,
  Error,
  { ad: string; anaKalemId?: string | null; kurallar?: Record<string, unknown> | null }
> {
  const token = useAccessToken();
  const tazele = useSozlukTazele(listeKodu);
  return useMutation({
    mutationFn: (input) =>
      apiPost(`/api/lookup/${listeKodu}`, token, input, lookupItemDetailSchema),
    onSuccess: tazele,
  });
}

/** Ad düzeltme (yalnız müşteri kalemi — nüshada 422) · gizle/göster. */
export function useLookupKalemGuncelle(
  listeKodu: string,
): UseMutationResult<
  LookupItemDetail,
  Error,
  { id: string; ad?: string; gizli?: boolean; anaKalemId?: string | null }
> {
  const token = useAccessToken();
  const tazele = useSozlukTazele(listeKodu);
  return useMutation({
    mutationFn: ({ id, ...govde }) =>
      apiPatch(`/api/lookup/items/${id}`, token, govde, lookupItemDetailSchema),
    onSuccess: tazele,
  });
}

/** Müşteri kalemi silme (nüsha 422 — gizlenir). */
export function useLookupKalemSil(
  listeKodu: string,
): UseMutationResult<SilmeSonucu, Error, { id: string }> {
  const token = useAccessToken();
  const tazele = useSozlukTazele(listeKodu);
  return useMutation({
    mutationFn: ({ id }) => apiDelete(`/api/lookup/items/${id}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

/** 5½ / B3 — işyeri düzeyi zorunlu görevlendirme türetmesi (§25/12). */
export function useIsyeriZorunluGorevlendirmeler(
  workplaceId: string | null,
): UseQueryResult<IsyeriZorunluGorevlendirmeler> {
  const token = useAccessToken();
  // Okuyamayan şapkada hücre çizilmez ve uç ÇAĞRILMAZ (işyeri satırı başına bir 403'tü).
  const kapi = useIkincilOkuma('zorunluGorevlendirme');
  return useQuery({
    queryKey: ['zorunlu-gorevlendirmeler', workplaceId],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/workplaces/${workplaceId}/zorunlu-gorevlendirmeler`,
        token,
        isyeriZorunluGorevlendirmelerSchema,
        signal,
      ),
    enabled: workplaceId !== null && kapi.acik,
  });
}

// ═══ YÜKLENİCİLER — SERTLEŞTİRME TUR 6 (Kapsam §5.13/14) ═══
//
// ⚠️ Saha girişi SUNUCUDAN gelir (`sahaGirisi` — tek yüklem); ekran tarih
// hesaplamaz. Yetki bayrakları da sunucudan (`yetkiler`) — rol kopyası yok.

import type {
  contractorCreateSchema,
  contractorDocumentCreateSchema,
  contractorUpdateSchema,
} from '@isg/validation';

export type ContractorCreateGirdisi = z.input<typeof contractorCreateSchema>;
export type ContractorUpdateGirdisi = z.input<typeof contractorUpdateSchema>;
export type ContractorDocumentCreateGirdisi = z.input<typeof contractorDocumentCreateSchema>;
const contractorEkleriSchema = z.array(contractorDocumentAttachmentSchema);
const contractorPersonelSchema = z.array(contractorPersonelSatiriSchema);
const contractorEkipmanSchema = z.array(contractorEkipmanSatiriSchema);

export interface YukleniciSuzgeci {
  arama: string;
  sahaGirisi: 'gecerli' | 'gecersiz' | null;
  sozlesme: 'sabit' | 'suren' | 'bitmis' | null;
  sayfa: number;
}

export const BOS_YUKLENICI_SUZGECI: YukleniciSuzgeci = {
  arama: '',
  sahaGirisi: null,
  sozlesme: null,
  sayfa: 1,
};

export function etkinYukleniciSuzgeciSayisi(suzgec: YukleniciSuzgeci): number {
  return [suzgec.arama.trim() || null, suzgec.sahaGirisi, suzgec.sozlesme].filter(
    (deger) => deger !== null,
  ).length;
}

/** Liste sorgusu ile çıktı sorgusu AYNI yerden kurulur (P4 — ekipman deseni). */
export function yukleniciSuzgecSorgusu(suzgec: YukleniciSuzgeci, sayfali: boolean): string {
  const parametreler = new URLSearchParams();
  if (suzgec.arama.trim()) parametreler.set('arama', suzgec.arama.trim());
  if (suzgec.sahaGirisi) parametreler.set('sahaGirisi', suzgec.sahaGirisi);
  if (suzgec.sozlesme) parametreler.set('sozlesme', suzgec.sozlesme);
  if (sayfali) parametreler.set('sayfa', String(suzgec.sayfa));
  const sorgu = parametreler.toString();
  return sorgu ? `?${sorgu}` : '';
}

export function useYukleniciListesi(suzgec: YukleniciSuzgeci): UseQueryResult<ContractorList> {
  const token = useAccessToken();
  return useQuery({
    queryKey: [
      'contractors',
      'liste',
      suzgec.arama,
      suzgec.sahaGirisi,
      suzgec.sozlesme,
      suzgec.sayfa,
    ],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/contractors/liste${yukleniciSuzgecSorgusu(suzgec, true)}`,
        token,
        contractorListSchema,
        signal,
      ),
  });
}

export function useYuklenici(id: string): UseQueryResult<ContractorDetail> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['contractors', id],
    refetchOnMount: 'always', // saha girişi türetmesi başkasının kararıyla değişir
    queryFn: ({ signal }) =>
      apiGet(`/api/contractors/${id}`, token, contractorDetailSchema, signal),
    enabled: id.length > 0,
  });
}

export function useYukleniciPersoneli(id: string): UseQueryResult<ContractorPersonelSatiri[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['contractors', id, 'personel'],
    queryFn: ({ signal }) =>
      apiGet(`/api/contractors/${id}/personel`, token, contractorPersonelSchema, signal),
    enabled: id.length > 0,
  });
}

export function useYukleniciEkipmani(id: string): UseQueryResult<ContractorEkipmanSatiri[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['contractors', id, 'ekipman'],
    queryFn: ({ signal }) =>
      apiGet(`/api/contractors/${id}/ekipman`, token, contractorEkipmanSchema, signal),
    enabled: id.length > 0,
  });
}

function useYukleniciTazele(): (contractorId?: string) => void {
  const queryClient = useQueryClient();
  return (contractorId) => {
    void queryClient.invalidateQueries({ queryKey: ['contractors'] });
    if (contractorId) {
      void queryClient.invalidateQueries({ queryKey: ['contractor-documents', contractorId] });
    }
  };
}

export function useYukleniciAc(): UseMutationResult<
  ContractorDetail,
  Error,
  ContractorCreateGirdisi
> {
  const token = useAccessToken();
  const tazele = useYukleniciTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/contractors', token, input, contractorDetailSchema),
    onSuccess: () => tazele(),
  });
}

export function useYukleniciGuncelle(): UseMutationResult<
  ContractorDetail,
  Error,
  { id: string; input: ContractorUpdateGirdisi }
> {
  const token = useAccessToken();
  const tazele = useYukleniciTazele();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiPatch(`/api/contractors/${id}`, token, input, contractorDetailSchema),
    onSuccess: (_s, { id }) => tazele(id),
  });
}

// ═══ YÜKLENİCİ EVRAK PAKETİ (K-102a…k — 17.09.2026; Kapsam §5.13/15) ═══

export type EvrakPaketi = z.output<typeof evrakPaketleriSchema>[number];
const kimlikSchema = z.object({ id: z.uuid() });

/** Paket değişince yol planı · saha girişi · pano sayıları birlikte eskir. */
function useEvrakPaketiTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['evrak-paketleri'] });
    void queryClient.invalidateQueries({ queryKey: ['contractors'] });
    void queryClient.invalidateQueries({ queryKey: ['contractor-documents'] });
    void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
  };
}

export function useEvrakPaketleri(options?: { enabled?: boolean }): UseQueryResult<EvrakPaketi[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['evrak-paketleri'],
    queryFn: ({ signal }) =>
      apiGet('/api/contractor-document-packages', token, evrakPaketleriSchema, signal),
    enabled: options?.enabled ?? true,
  });
}

export function useEvrakPaketiAc(): UseMutationResult<
  { id: string },
  Error,
  { ad: string; evrakTipiIds: string[] }
> {
  const token = useAccessToken();
  const tazele = useEvrakPaketiTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/contractor-document-packages', token, input, kimlikSchema),
    onSuccess: () => tazele(),
  });
}

export function useEvrakPaketiGuncelle(): UseMutationResult<
  { id: string },
  Error,
  { id: string; input: { ad?: string; evrakTipiIds?: string[] } }
> {
  const token = useAccessToken();
  const tazele = useEvrakPaketiTazele();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiPatch(`/api/contractor-document-packages/${id}`, token, input, kimlikSchema),
    onSuccess: () => tazele(),
  });
}

export function useEvrakPaketiSil(): UseMutationResult<{ id: string }, Error, string> {
  const token = useAccessToken();
  const tazele = useEvrakPaketiTazele();
  return useMutation({
    mutationFn: (id) => apiDelete(`/api/contractor-document-packages/${id}`, token, kimlikSchema),
    onSuccess: () => tazele(),
  });
}

/** K-102e — içerik düzenlemesinin maliyet özeti (salt okuma; değiştirmeden önce). */
export function useEvrakPaketiOnizleme(): UseMutationResult<
  EvrakPaketiMaliyetOzeti,
  Error,
  { id: string; evrakTipiIds: string[] }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({ id, evrakTipiIds }) =>
      apiPost(
        `/api/contractor-document-packages/${id}/onizleme`,
        token,
        { evrakTipiIds },
        evrakPaketiMaliyetOzetiSchema,
      ),
  });
}

/** K-102f — yol planı: kart ve portal AYNI uçtan. */
export function useYukleniciEvrakBeklentisi(contractorId: string): UseQueryResult<EvrakBeklentisi> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['contractors', contractorId, 'evrak-beklentisi'],
    // Durum başkasının kararıyla (onay/red) değişir — her monte oluşta tazelenir.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      apiGet(
        `/api/contractors/${contractorId}/evrak-beklentisi`,
        token,
        evrakBeklentisiSchema,
        signal,
      ),
    enabled: contractorId.length > 0,
  });
}

/** K-102e — paket değişiminin maliyet özeti (yeni paket seçilince). */
export function useYukleniciPaketOnizleme(
  contractorId: string,
  evrakPaketiId: string | null,
): UseQueryResult<EvrakPaketiMaliyetOzeti> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['contractors', contractorId, 'paket-onizleme', evrakPaketiId],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/contractors/${contractorId}/document-package/onizleme?evrakPaketiId=${evrakPaketiId}`,
        token,
        evrakPaketiMaliyetOzetiSchema,
        signal,
      ),
    enabled: contractorId.length > 0 && evrakPaketiId !== null,
    staleTime: 0,
  });
}

export function useYukleniciPaketAta(): UseMutationResult<
  EvrakBeklentisi,
  Error,
  { contractorId: string; evrakPaketiId: string }
> {
  const token = useAccessToken();
  const tazele = useEvrakPaketiTazele();
  return useMutation({
    mutationFn: ({ contractorId, evrakPaketiId }) =>
      apiPut(
        `/api/contractors/${contractorId}/document-package`,
        token,
        { evrakPaketiId },
        evrakBeklentisiSchema,
      ),
    onSuccess: () => tazele(),
  });
}

export function useYukleniciEvrakEkiEkle(): UseMutationResult<
  EvrakBeklentisi,
  Error,
  { contractorId: string; evrakTipiId: string }
> {
  const token = useAccessToken();
  const tazele = useEvrakPaketiTazele();
  return useMutation({
    mutationFn: ({ contractorId, evrakTipiId }) =>
      apiPost(
        `/api/contractors/${contractorId}/document-extras`,
        token,
        { evrakTipiId },
        evrakBeklentisiSchema,
      ),
    onSuccess: () => tazele(),
  });
}

export function useYukleniciEvrakEkiKaldir(): UseMutationResult<
  EvrakBeklentisi,
  Error,
  { contractorId: string; ekId: string }
> {
  const token = useAccessToken();
  const tazele = useEvrakPaketiTazele();
  return useMutation({
    mutationFn: ({ contractorId, ekId }) =>
      apiDelete(
        `/api/contractors/${contractorId}/document-extras/${ekId}`,
        token,
        evrakBeklentisiSchema,
      ),
    onSuccess: () => tazele(),
  });
}

/** Firmanın evrak listesi — detay ve portal AYNI uçtan (temsilci kendi firması). */
export function useYukleniciEvraklari(
  contractorId: string,
): UseQueryResult<ContractorDocumentDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['contractor-documents', contractorId],
    // Karar BAŞKA bir kullanıcıdan gelir (onaycı); 30 sn'lik genel staleTime,
    // Personel'den Firmam'a dönen temsilciye bayat durum gösterirdi → her
    // monte oluşta tazelenir (kullanıcı yenilemez — a-1).
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      apiGet(
        `/api/contractors/${contractorId}/documents`,
        token,
        contractorDocumentsSchema,
        signal,
      ),
    enabled: contractorId.length > 0,
  });
}

export function useEvrakAc(): UseMutationResult<
  ContractorDocumentDetail,
  Error,
  { contractorId: string; input: ContractorDocumentCreateGirdisi }
> {
  const token = useAccessToken();
  const tazele = useYukleniciTazele();
  return useMutation({
    mutationFn: ({ contractorId, input }) =>
      apiPost(
        `/api/contractors/${contractorId}/documents`,
        token,
        input,
        contractorDocumentDetailSchema,
      ),
    onSuccess: (_s, { contractorId }) => tazele(contractorId),
  });
}

/** Onay ve red TEK mutation — iki karar akışı yazılmaz. */
export function useEvrakKarari(): UseMutationResult<
  ContractorDocumentDetail,
  Error,
  {
    contractorId: string;
    documentId: string;
    karar: { sonuc: 'onaylandi' } | { sonuc: 'reddedildi'; gerekce: string; hassasVeri: boolean };
  }
> {
  const token = useAccessToken();
  const tazele = useYukleniciTazele();
  return useMutation({
    mutationFn: ({ documentId, karar }) =>
      karar.sonuc === 'onaylandi'
        ? apiPost(
            `/api/contractor-documents/${documentId}/approve`,
            token,
            {},
            contractorDocumentDetailSchema,
          )
        : apiPost(
            `/api/contractor-documents/${documentId}/reject`,
            token,
            { gerekce: karar.gerekce, hassasVeri: karar.hassasVeri },
            contractorDocumentDetailSchema,
          ),
    onSuccess: (_s, { contractorId }) => tazele(contractorId),
  });
}

export function useEvrakEkleri(
  documentId: string,
): UseQueryResult<ContractorDocumentAttachmentDetail[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['contractor-document-attachments', documentId],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/contractor-documents/${documentId}/attachments`,
        token,
        contractorEkleriSchema,
        signal,
      ),
    enabled: documentId.length > 0,
  });
}

function useEvrakEkTazele(): (documentId: string, contractorId: string) => void {
  const queryClient = useQueryClient();
  return (documentId, contractorId) => {
    void queryClient.invalidateQueries({
      queryKey: ['contractor-document-attachments', documentId],
    });
    void queryClient.invalidateQueries({ queryKey: ['contractor-documents', contractorId] });
  };
}

export function useEvrakEkYukle(): UseMutationResult<
  ContractorDocumentAttachmentDetail,
  Error,
  { contractorId: string; documentId: string; dosya: File }
> {
  const token = useAccessToken();
  const tazele = useEvrakEkTazele();
  return useMutation({
    mutationFn: ({ documentId, dosya }) =>
      apiUpload(
        `/api/contractor-documents/${documentId}/attachments`,
        token,
        dosya,
        contractorDocumentAttachmentSchema,
      ),
    onSuccess: (_s, { documentId, contractorId }) => tazele(documentId, contractorId),
  });
}

export function useEvrakEkSil(): UseMutationResult<
  { silindi: true },
  Error,
  { contractorId: string; documentId: string; attachmentId: string }
> {
  const token = useAccessToken();
  const tazele = useEvrakEkTazele();
  return useMutation({
    mutationFn: ({ documentId, attachmentId }) =>
      apiDelete(
        `/api/contractor-documents/${documentId}/attachments/${attachmentId}`,
        token,
        silmeSonucuSchema,
      ),
    onSuccess: (_s, { documentId, contractorId }) => tazele(documentId, contractorId),
  });
}

export function useEvrakEkIndir(): UseMutationResult<
  void,
  Error,
  { documentId: string; attachmentId: string; dosyaAdi: string }
> {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async ({ documentId, attachmentId, dosyaAdi }) => {
      const { blob, dosyaAdi: sunucuAdi } = await apiDownload(
        `/api/contractor-documents/${documentId}/attachments/${attachmentId}/download`,
        token,
      );
      const url = URL.createObjectURL(blob);
      const bag = document.createElement('a');
      bag.href = url;
      bag.download = sunucuAdi ?? dosyaAdi;
      document.body.appendChild(bag);
      bag.click();
      bag.remove();
      URL.revokeObjectURL(url);
    },
  });
}

// ═══ DASHBOARD (TUR 12 — Kapsam §26) ══════════════════════════════════════
//
// ⚠️ **EKRAN HİÇBİR SAYIYI KENDİ HESAPLAMAZ** (K6): oran da, çalışan başına
// metrik de, etiket de SUNUCUDAN gelir. Buraya bir `pay / payda` çarpımı
// yazmak, kuralı üçüncü bir yere daha kopyalamak olurdu — ve ekrandaki kopya
// sunucudakiyle ayrıştığında fark **hiç görünmez.**

/**
 * Kapsam seçenekleri + çarpanın VARSAYILANI. ⚠️ Seçenekler sunucudan gelir:
 * istemci bahçeyi yeniden türetmez (§2.16/B3).
 */
export function useDashboardSecenekleri(): UseQueryResult<DashboardSecenekleri> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['dashboard', 'options'],
    queryFn: ({ signal }) =>
      apiGet('/api/dashboard/options', token, dashboardSecenekleriSchema, signal),
  });
}

export interface DashboardSuzgeci {
  kapsamTipi: DashboardKapsamTipi;
  kapsamId: string | null;
  donem: DashboardDonemi;
  carpan: KpiCarpan;
}

function dashboardSorgusu(suzgec: DashboardSuzgeci): string {
  const params = new URLSearchParams({
    kapsamTipi: suzgec.kapsamTipi,
    donem: suzgec.donem,
    carpan: String(suzgec.carpan),
  });
  if (suzgec.kapsamId) params.set('kapsamId', suzgec.kapsamId);
  return params.toString();
}

export function dashboardKpiYolu(suzgec: DashboardSuzgeci): string {
  return `/api/dashboard/kpi?${dashboardSorgusu(suzgec)}`;
}

/**
 * K10 — ÇIKTI YOLU **EKRANDAKİ SÜZGECİN AYNISIYLA** kurulur (P4): dosya,
 * ekranda okunan kapsam · dönem · **çarpan** üçlüsünü taşır ve antetine
 * basar. ⚠️ İndirmenin kendisi `useCiktiIndir`dedir — ikinci bir indirme
 * mutasyonu yazılmaz.
 */
export function dashboardCiktiYolu(suzgec: DashboardSuzgeci, bicim: 'export' | 'pdf'): string {
  return `/api/dashboard/${bicim}?${dashboardSorgusu(suzgec)}`;
}

/** ⚠️ Süzgeç `queryKey`in İÇİNDEDİR: çarpan değişince oran YENİDEN OKUNUR
 *  (K1 — çarpan bir gösterim ayarı değil, sorgunun parametresidir). */
export function useDashboardKpi(suzgec: DashboardSuzgeci): UseQueryResult<DashboardKpi> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['dashboard', 'kpi', suzgec],
    queryFn: ({ signal }) => apiGet(dashboardKpiYolu(suzgec), token, dashboardKpiSchema, signal),
  });
}

/** G1.1 — kıyas: aynı süzgeç, bir kademe aşağı okunur (ikinci kapsam seçici yok). */
export function useDashboardKiyas(suzgec: DashboardSuzgeci): UseQueryResult<DashboardKiyas> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['dashboard', 'kiyas', suzgec],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/dashboard/kiyas?${dashboardSorgusu(suzgec)}`,
        token,
        dashboardKiyasSchema,
        signal,
      ),
  });
}

/** ⚠️ Kapsamı BAHÇEDİR — (A) bloğunun süzgeci burada koşmaz (§26.6). */
export function useDashboardOperasyonel(): UseQueryResult<DashboardOperasyonel> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['dashboard', 'operasyonel'],
    queryFn: ({ signal }) =>
      apiGet('/api/dashboard/operasyonel', token, dashboardOperasyonelSchema, signal),
  });
}

// ═══ SAHA TURU / DENETİM (Kapsam §27 — 05.09.2026) ═══════════════════════════
//
// KAP BELİRLER: form gönderimi bulgu doğurmaz, tur kapanışı doğurur. Kancalar
// yalnız taşır; yetki bayrakları SUNUCUDAN gelir (`yetkiler`), ekran kural
// türetmez (§2.40).

export interface SahaTuruListeSuzgeci {
  durum?: 'acik' | 'kapali';
  tip?: 'gozlem' | 'denetim' | 'tatbikat';
  workplaceId?: string;
  sayfa: number;
}
export const BOS_SAHA_TURU_SUZGECI: SahaTuruListeSuzgeci = { sayfa: 1 };
export function etkinSahaTuruSuzgeciSayisi(s: SahaTuruListeSuzgeci): number {
  return [s.durum, s.tip, s.workplaceId].filter(Boolean).length;
}
function sahaTuruSorgusu(s: SahaTuruListeSuzgeci, sayfali: boolean): string {
  const sorgu = new URLSearchParams();
  if (s.durum) sorgu.set('durum', s.durum);
  if (s.tip) sorgu.set('tip', s.tip);
  if (s.workplaceId) sorgu.set('workplaceId', s.workplaceId);
  if (sayfali) sorgu.set('sayfa', String(s.sayfa));
  return sorgu.toString();
}
/** Excel yolu — ekranla AYNI süzgeç serileştiricisi (P4 cümlesi). */
export function sahaTuruListesiCiktiYolu(s: SahaTuruListeSuzgeci): string {
  const qs = sahaTuruSorgusu(s, false);
  return `/api/site-tours/export${qs ? `?${qs}` : ''}`;
}

export function useSahaTurlari(s: SahaTuruListeSuzgeci): UseQueryResult<SahaTuruListesi> {
  const token = useAccessToken();
  const qs = sahaTuruSorgusu(s, true);
  return useQuery({
    queryKey: ['saha-turlari', qs],
    queryFn: ({ signal }) => apiGet(`/api/site-tours?${qs}`, token, sahaTuruListesiSchema, signal),
  });
}

export function useSahaTuruSecenekleri(): UseQueryResult<SahaTuruSecenekleri> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['saha-turu-secenekler'],
    queryFn: ({ signal }) =>
      apiGet('/api/site-tours/options', token, sahaTuruSecenekleriSchema, signal),
  });
}

export function useSahaTuru(id: string | undefined): UseQueryResult<SahaTuruDetay> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['saha-turu', id],
    queryFn: ({ signal }) => apiGet(`/api/site-tours/${id}`, token, sahaTuruDetaySchema, signal),
    enabled: Boolean(id),
  });
}

export function useSahaTuruGozlemler(
  id: string | undefined,
  sayfa: number,
  sayfaBoyutu = 50,
): UseQueryResult<GozlemListesi> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['saha-turu-gozlemler', id, sayfa, sayfaBoyutu],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/site-tours/${id}/observations?sayfa=${sayfa}&sayfaBoyutu=${sayfaBoyutu}`,
        token,
        gozlemListesiSchema,
        signal,
      ),
    enabled: Boolean(id),
  });
}

export function useEkipAdaylari(id: string | undefined, q: string): UseQueryResult<EkipAdayi[]> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['saha-turu-ekip-adaylari', id, q],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/site-tours/${id}/member-candidates?q=${encodeURIComponent(q)}`,
        token,
        ekipAdaylariSchema,
        signal,
      ),
    enabled: Boolean(id),
  });
}

function useSahaTuruTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['saha-turu'] });
    void queryClient.invalidateQueries({ queryKey: ['saha-turu'] });
    void queryClient.invalidateQueries({ queryKey: ['saha-turu-gozlemler'] });
    // Kapanış uygunsuzluk doğurur — o listeler ve zil de tazelenir.
    void queryClient.invalidateQueries({ queryKey: ['nonconformities'] });
    void queryClient.invalidateQueries({ queryKey: ['zil'] });
  };
}

export function useSahaTuruAc(): UseMutationResult<SahaTuruDetay, Error, SahaTuruOlustur> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/site-tours', token, input, sahaTuruDetaySchema),
    onSuccess: tazele,
  });
}

export function useSahaTuruEkipEkle(): UseMutationResult<
  SahaTuruDetay,
  Error,
  { id: string; personId: string }
> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  return useMutation({
    mutationFn: ({ id, personId }) =>
      apiPost(`/api/site-tours/${id}/members`, token, { personId }, sahaTuruDetaySchema),
    onSuccess: tazele,
  });
}

export function useSahaTuruEkipCikar(): UseMutationResult<
  { silindi: true },
  Error,
  { id: string; memberId: string }
> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  return useMutation({
    mutationFn: ({ id, memberId }) =>
      apiDelete(`/api/site-tours/${id}/members/${memberId}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

export function useGozlemEkle(): UseMutationResult<
  Gozlem,
  Error,
  { id: string; girdi: GozlemOlustur }
> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  return useMutation({
    mutationFn: ({ id, girdi }) =>
      apiPost(`/api/site-tours/${id}/observations`, token, girdi, gozlemSchema),
    onSuccess: tazele,
  });
}

export function useGozlemSil(): UseMutationResult<
  { silindi: true },
  Error,
  { id: string; observationId: string }
> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  return useMutation({
    mutationFn: ({ id, observationId }) =>
      apiDelete(`/api/site-tours/${id}/observations/${observationId}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

export function useSahaTuruKapat(): UseMutationResult<
  SahaTuruDetay,
  Error,
  { id: string; secim: SahaTuruKapat }
> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  return useMutation({
    mutationFn: ({ id, secim }) =>
      apiPost(`/api/site-tours/${id}/close`, token, secim, sahaTuruDetaySchema),
    onSuccess: tazele,
  });
}

export function useGozlemFotografYukle(): UseMutationResult<
  Gozlem,
  Error,
  { id: string; observationId: string; dosya: File }
> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  return useMutation({
    mutationFn: ({ id, observationId, dosya }) =>
      apiUpload(
        `/api/site-tours/${id}/observations/${observationId}/attachments`,
        token,
        dosya,
        gozlemSchema,
      ),
    onSuccess: tazele,
  });
}

export function useGozlemFotografSil(): UseMutationResult<
  { silindi: true },
  Error,
  { id: string; observationId: string; attachmentId: string }
> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  return useMutation({
    mutationFn: ({ id, observationId, attachmentId }) =>
      apiDelete(
        `/api/site-tours/${id}/observations/${observationId}/attachments/${attachmentId}`,
        token,
        silmeSonucuSchema,
      ),
    onSuccess: tazele,
  });
}

/** Fotoğraf indirme — public URL YOK; baytlar API'den (§22.4/6). */
export function useGozlemFotografIndir(): (
  id: string,
  observationId: string,
  attachmentId: string,
) => Promise<{ blob: Blob; dosyaAdi: string | null }> {
  const token = useAccessToken();
  return (id, observationId, attachmentId) =>
    apiDownload(
      `/api/site-tours/${id}/observations/${observationId}/attachments/${attachmentId}/download`,
      token,
    );
}

/** ACİL DURUM / E5 — tur eki (dış tatbikat raporu) yükleme; tutanak PDF ile yan yana. */
export function useSahaTuruEkYukle(): UseMutationResult<
  SahaTuruDetay,
  Error,
  { id: string; dosya: File }
> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dosya }) =>
      apiUpload(`/api/site-tours/${id}/attachments`, token, dosya, sahaTuruDetaySchema),
    onSuccess: () => {
      tazele();
      void queryClient.invalidateQueries({ queryKey: ['acil-durum'] });
    },
  });
}

export function useSahaTuruEkSil(): UseMutationResult<
  { silindi: true },
  Error,
  { id: string; attachmentId: string; gerekce?: string }
> {
  const token = useAccessToken();
  const tazele = useSahaTuruTazele();
  return useMutation({
    // Madde 230 — kapalı turda holding yön. GEREKÇEYLE siler (gövde; sunucu 422'ler).
    mutationFn: ({ id, attachmentId, gerekce }) =>
      apiDelete(
        `/api/site-tours/${id}/attachments/${attachmentId}`,
        token,
        silmeSonucuSchema,
        gerekce ? { gerekce } : {},
      ),
    onSuccess: tazele,
  });
}

export function useSahaTuruEkIndir(): (
  id: string,
  attachmentId: string,
) => Promise<{ blob: Blob; dosyaAdi: string | null }> {
  const token = useAccessToken();
  return (id, attachmentId) =>
    apiDownload(`/api/site-tours/${id}/attachments/${attachmentId}/download`, token);
}

// ═══ ACİL DURUM (Kapsam §29 — 06.09.2026) ═══════════════════════════════════
// ACİL DURUM MODÜLÜ HİÇBİR ŞEYİ İKİNCİ KEZ TUTMAZ. Sunucu verisi yalnız
// TanStack Query'de; ekran ikinci filtre/kural yazmaz — vadeler sunucudan.

export type { AcilDurumListeSuzgeci };
export const BOS_ACIL_DURUM_SUZGECI: AcilDurumListeSuzgeci = { sayfa: 1, sayfaBoyutu: 25 };
function acilDurumSorgusu(s: AcilDurumListeSuzgeci): string {
  const sorgu = new URLSearchParams();
  if (s.workplaceId) sorgu.set('workplaceId', s.workplaceId);
  sorgu.set('sayfa', String(s.sayfa));
  sorgu.set('sayfaBoyutu', String(s.sayfaBoyutu));
  return sorgu.toString();
}

export function useAcilDurumListesi(s: AcilDurumListeSuzgeci): UseQueryResult<AcilDurumListesi> {
  const token = useAccessToken();
  const qs = acilDurumSorgusu(s);
  return useQuery({
    queryKey: ['acil-durum', 'liste', qs],
    queryFn: ({ signal }) => apiGet(`/api/emergency?${qs}`, token, acilDurumListesiSchema, signal),
  });
}

export function useAcilDurumOzeti(workplaceId: string | undefined): UseQueryResult<AcilDurumOzeti> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['acil-durum', 'isyeri', workplaceId],
    enabled: Boolean(workplaceId),
    queryFn: ({ signal }) =>
      apiGet(`/api/emergency/workplaces/${workplaceId}`, token, acilDurumOzetiSchema, signal),
  });
}

function useAcilDurumTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['acil-durum'] });
    // Belge yüklemesi #17 görev dönemini kapatır — görev ekranları da tazelenir.
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    void queryClient.invalidateQueries({ queryKey: ['yukumluluk'] });
  };
}

/** E1 — kontrollü belge yükleme: multipart (dosya + tip + yürürlük + açıklama). */
export function useKontrolluBelgeYukle(): UseMutationResult<
  AcilDurumOzeti,
  Error,
  { workplaceId: string; dosya: File } & KontrolluBelgeOlustur
> {
  const token = useAccessToken();
  const tazele = useAcilDurumTazele();
  return useMutation({
    mutationFn: ({ workplaceId, dosya, tip, yururlukTarihi, aciklama }) =>
      apiUpload(
        `/api/emergency/workplaces/${workplaceId}/documents`,
        token,
        dosya,
        acilDurumOzetiSchema,
        { tip, yururlukTarihi, ...(aciklama ? { aciklama } : {}) },
      ),
    onSuccess: tazele,
  });
}

export function useKontrolluBelgeSil(): UseMutationResult<
  { silindi: true },
  Error,
  { id: string }
> {
  const token = useAccessToken();
  const tazele = useAcilDurumTazele();
  return useMutation({
    mutationFn: ({ id }) => apiDelete(`/api/emergency/documents/${id}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

export function useKontrolluBelgeIndir(): (
  id: string,
) => Promise<{ blob: Blob; dosyaAdi: string | null }> {
  const token = useAccessToken();
  return (id) => apiDownload(`/api/emergency/documents/${id}/download`, token);
}

export function useAcilIletisimEkle(): UseMutationResult<
  AcilIletisim,
  Error,
  { workplaceId: string } & AcilIletisimOlustur
> {
  const token = useAccessToken();
  const tazele = useAcilDurumTazele();
  return useMutation({
    mutationFn: ({ workplaceId, ...govde }) =>
      apiPost(
        `/api/emergency/workplaces/${workplaceId}/contacts`,
        token,
        govde,
        acilIletisimSchema,
      ),
    onSuccess: tazele,
  });
}

export function useAcilIletisimGuncelle(): UseMutationResult<
  AcilIletisim,
  Error,
  { id: string } & AcilIletisimGuncelle
> {
  const token = useAccessToken();
  const tazele = useAcilDurumTazele();
  return useMutation({
    mutationFn: ({ id, ...govde }) =>
      apiPatch(`/api/emergency/contacts/${id}`, token, govde, acilIletisimSchema),
    onSuccess: tazele,
  });
}

export function useAcilIletisimSil(): UseMutationResult<{ silindi: true }, Error, { id: string }> {
  const token = useAccessToken();
  const tazele = useAcilDurumTazele();
  return useMutation({
    mutationFn: ({ id }) => apiDelete(`/api/emergency/contacts/${id}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

// ═══ İSG KURULU (Kapsam §28 — 05.09.2026) ═══════════════════════════════════
// ÜYELİK GÖREVLENDİRMEDİR, TOPLANTI KAYITTIR, KARAR DÖF'TÜR. Sunucu verisi
// yalnız TanStack Query'de; ekran ikinci filtre/kural yazmaz.

export type { KurulListeSuzgeci, ToplantiListeSuzgeci };
export const BOS_KURUL_SUZGECI: KurulListeSuzgeci = { sayfa: 1, sayfaBoyutu: 25 };
export function etkinKurulSuzgeciSayisi(s: KurulListeSuzgeci): number {
  return [s.durum, s.workplaceId].filter(Boolean).length;
}
function kurulSorgusu(s: KurulListeSuzgeci): string {
  const sorgu = new URLSearchParams();
  if (s.durum) sorgu.set('durum', s.durum);
  if (s.workplaceId) sorgu.set('workplaceId', s.workplaceId);
  sorgu.set('sayfa', String(s.sayfa));
  return sorgu.toString();
}

export function useKurullar(s: KurulListeSuzgeci): UseQueryResult<KurulListesi> {
  const token = useAccessToken();
  const qs = kurulSorgusu(s);
  return useQuery({
    queryKey: ['kurullar', qs],
    queryFn: ({ signal }) => apiGet(`/api/committees?${qs}`, token, kurulListesiSchema, signal),
  });
}

export function useKurulSecenekleri(): UseQueryResult<KurulSecenekleri> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['kurul-secenekler'],
    queryFn: ({ signal }) =>
      apiGet('/api/committees/options', token, kurulSecenekleriSchema, signal),
  });
}

export function useKurul(id: string | undefined): UseQueryResult<KurulDetay> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['kurul', id],
    queryFn: ({ signal }) => apiGet(`/api/committees/${id}`, token, kurulDetaySchema, signal),
    enabled: Boolean(id),
  });
}

export const BOS_TOPLANTI_SUZGECI: ToplantiListeSuzgeci = { sayfa: 1, sayfaBoyutu: 25 };
export function etkinToplantiSuzgeciSayisi(s: ToplantiListeSuzgeci): number {
  return [s.tip, s.durum].filter(Boolean).length;
}
function toplantiSorgusu(s: ToplantiListeSuzgeci, sayfali: boolean): string {
  const sorgu = new URLSearchParams();
  if (s.tip) sorgu.set('tip', s.tip);
  if (s.durum) sorgu.set('durum', s.durum);
  if (sayfali) sorgu.set('sayfa', String(s.sayfa));
  return sorgu.toString();
}
/** Excel yolu — ekranla AYNI süzgeç serileştiricisi (P4 cümlesi). */
export function toplantiListesiCiktiYolu(kurulId: string, s: ToplantiListeSuzgeci): string {
  const qs = toplantiSorgusu(s, false);
  return `/api/committees/${kurulId}/meetings/export${qs ? `?${qs}` : ''}`;
}

export function useKurulToplantilari(
  kurulId: string | undefined,
  s: ToplantiListeSuzgeci,
): UseQueryResult<ToplantiListesi> {
  const token = useAccessToken();
  const qs = toplantiSorgusu(s, true);
  return useQuery({
    queryKey: ['kurul-toplantilari', kurulId, qs],
    queryFn: ({ signal }) =>
      apiGet(`/api/committees/${kurulId}/meetings?${qs}`, token, toplantiListesiSchema, signal),
    enabled: Boolean(kurulId),
  });
}

export function useKurulToplantisi(id: string | undefined): UseQueryResult<ToplantiDetay> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['kurul-toplantisi', id],
    queryFn: ({ signal }) =>
      apiGet(`/api/committee-meetings/${id}`, token, toplantiDetaySchema, signal),
    enabled: Boolean(id),
  });
}

/** C6 — "Kurul gündemine ekle" düğmesinin TEK yüklemi (görünürlük + engel SUNUCUDAN). */
export function useGundemSecenekleri(
  workplaceId: string | undefined,
): UseQueryResult<GundemSecenekleri> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['kurul-gundem-secenekleri', workplaceId],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/committees/gundem-secenekleri?workplaceId=${workplaceId}`,
        token,
        gundemSecenekleriSchema,
        signal,
      ),
    enabled: Boolean(workplaceId),
  });
}

function useKurulTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['kurullar'] });
    void queryClient.invalidateQueries({ queryKey: ['kurul'] });
    void queryClient.invalidateQueries({ queryKey: ['kurul-secenekler'] });
    void queryClient.invalidateQueries({ queryKey: ['kurul-toplantilari'] });
    void queryClient.invalidateQueries({ queryKey: ['kurul-toplantisi'] });
    void queryClient.invalidateQueries({ queryKey: ['kurul-gundem-secenekleri'] });
    // Karar DÖF doğurur, kapanış görev kapatır — o listeler ve zil de tazelenir.
    void queryClient.invalidateQueries({ queryKey: ['actions'] });
    void queryClient.invalidateQueries({ queryKey: ['zil'] });
  };
}

export function useKurulKur(): UseMutationResult<KurulDetay, Error, KurulOlustur> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: (input) => apiPost('/api/committees', token, input, kurulDetaySchema),
    onSuccess: tazele,
  });
}

export function useKurulGuncelle(): UseMutationResult<
  KurulDetay,
  Error,
  { id: string; yama: KurulGuncelle }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ id, yama }) => apiPatch(`/api/committees/${id}`, token, yama, kurulDetaySchema),
    onSuccess: tazele,
  });
}

export function useToplantiPlanla(): UseMutationResult<
  ToplantiDetay,
  Error,
  { kurulId: string; girdi: ToplantiPlanla }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ kurulId, girdi }) =>
      apiPost(`/api/committees/${kurulId}/meetings`, token, girdi, toplantiDetaySchema),
    onSuccess: tazele,
  });
}

export function useKatilimYaz(): UseMutationResult<
  ToplantiDetay,
  Error,
  { id: string; katilanDesignationIds: string[] }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ id, katilanDesignationIds }) =>
      apiPut(
        `/api/committee-meetings/${id}/attendance`,
        token,
        { katilanDesignationIds },
        toplantiDetaySchema,
      ),
    onSuccess: tazele,
  });
}

export function useGundemMaddesiEkle(): UseMutationResult<
  ToplantiDetay,
  Error,
  { id: string; madde: GundemMaddesiOlustur }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ id, madde }) =>
      apiPost(`/api/committee-meetings/${id}/agenda-items`, token, madde, toplantiDetaySchema),
    onSuccess: tazele,
  });
}

export function useGundemMaddesiSil(): UseMutationResult<
  { silindi: true },
  Error,
  { id: string; itemId: string }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ id, itemId }) =>
      apiDelete(`/api/committee-meetings/${id}/agenda-items/${itemId}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

export function useKararYaz(): UseMutationResult<
  ToplantiDetay,
  Error,
  { id: string; karar: KararOlustur }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ id, karar }) =>
      apiPost(`/api/committee-meetings/${id}/decisions`, token, karar, toplantiDetaySchema),
    onSuccess: tazele,
  });
}

export function useKararDuzelt(): UseMutationResult<
  ToplantiDetay,
  Error,
  { kararId: string; yama: KararDuzelt }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ kararId, yama }) =>
      apiPatch(`/api/committee-decisions/${kararId}`, token, yama, toplantiDetaySchema),
    onSuccess: tazele,
  });
}

export function useKararDofAc(): UseMutationResult<
  ToplantiDetay,
  Error,
  { kararId: string; girdi: KararDofAc }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ kararId, girdi }) =>
      apiPost(`/api/committee-decisions/${kararId}/action`, token, girdi, toplantiDetaySchema),
    onSuccess: tazele,
  });
}

export function useToplantiKapat(): UseMutationResult<
  ToplantiDetay,
  Error,
  { id: string; girdi: ToplantiKapat }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ id, girdi }) =>
      apiPost(`/api/committee-meetings/${id}/close`, token, girdi, toplantiDetaySchema),
    onSuccess: tazele,
  });
}

export function useToplantiNushaYukle(): UseMutationResult<
  ToplantiDetay,
  Error,
  { id: string; dosya: File }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    mutationFn: ({ id, dosya }) =>
      apiUpload(`/api/committee-meetings/${id}/attachments`, token, dosya, toplantiDetaySchema),
    onSuccess: tazele,
  });
}

export function useToplantiNushaSil(): UseMutationResult<
  { silindi: true },
  Error,
  { id: string; attachmentId: string; gerekce?: string }
> {
  const token = useAccessToken();
  const tazele = useKurulTazele();
  return useMutation({
    // Madde 230 — kapalı toplantıda holding yön. GEREKÇEYLE siler (gövde; sunucu 422'ler).
    mutationFn: ({ id, attachmentId, gerekce }) =>
      apiDelete(
        `/api/committee-meetings/${id}/attachments/${attachmentId}`,
        token,
        silmeSonucuSchema,
        gerekce ? { gerekce } : {},
      ),
    onSuccess: tazele,
  });
}

// ═══ KKD ZİMMETİ (Kapsam §30 — 07.09.2026) ═══════════════════════════════════
// KKD ZİMMETİ BİR TESLİM KAYDIDIR — ÖMÜR, STOK VE UYGUNLUK DEĞİL. Sunucu
// verisi yalnız TanStack Query'de; ekran ikinci filtre/kural yazmaz.

/** Kişi kartı bölümünün TEK okuyucusu — `yetkiler.yazabilir` sunucudan (§2.40). */
export function useKkdZimmetleri(personId: string): UseQueryResult<KisiKkdZimmetleri> {
  const token = useAccessToken();
  return useQuery({
    queryKey: ['kkd', personId],
    queryFn: ({ signal }) =>
      apiGet(`/api/persons/${personId}/ppe-issues`, token, kisiKkdZimmetleriSchema, signal),
  });
}

function useKkdTazele(personId: string): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['kkd', personId] });
    void queryClient.invalidateQueries({ queryKey: ['kkd-liste'] });
  };
}

export function useKkdZimmetEkle(
  personId: string,
): UseMutationResult<KkdZimmet, Error, KkdZimmetOlustur> {
  const token = useAccessToken();
  const tazele = useKkdTazele(personId);
  return useMutation({
    mutationFn: (govde) =>
      apiPost(`/api/persons/${personId}/ppe-issues`, token, govde, kkdZimmetSchema),
    onSuccess: tazele,
  });
}

/** D9 — iptal (gerekçe zorunlu). İmzalı satırda sunucu 409 verir; ekran o satırda düğmeyi ÇİZMEZ. */
export function useKkdZimmetIptal(
  personId: string,
): UseMutationResult<{ silindi: true }, Error, { id: string; gerekce: string }> {
  const token = useAccessToken();
  const tazele = useKkdTazele(personId);
  return useMutation({
    mutationFn: ({ id, gerekce }) =>
      apiDelete(`/api/persons/${personId}/ppe-issues/${id}`, token, silmeSonucuSchema, { gerekce }),
    onSuccess: tazele,
  });
}

/** Z6 — imzalı nüsha: TEK dosya, seçili satırların hepsine (virgülle). */
export function useKkdImzaliNushaYukle(
  personId: string,
): UseMutationResult<KisiKkdZimmetleri, Error, { dosya: File; issueIds: readonly string[] }> {
  const token = useAccessToken();
  const tazele = useKkdTazele(personId);
  return useMutation({
    mutationFn: ({ dosya, issueIds }) =>
      apiUpload(
        `/api/persons/${personId}/ppe-issues/signed-copies`,
        token,
        dosya,
        kisiKkdZimmetleriSchema,
        { issueIds: issueIds.join(',') },
      ),
    onSuccess: tazele,
  });
}

export function useKkdImzaliNushaSil(
  personId: string,
): UseMutationResult<{ silindi: true }, Error, { id: string }> {
  const token = useAccessToken();
  const tazele = useKkdTazele(personId);
  return useMutation({
    mutationFn: ({ id }) =>
      apiDelete(`/api/persons/${personId}/ppe-issues/${id}/signed-copy`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

export function useKkdImzaliNushaIndir(
  personId: string,
): (id: string) => Promise<{ blob: Blob; dosyaAdi: string | null }> {
  const token = useAccessToken();
  return (id) =>
    apiDownload(`/api/persons/${personId}/ppe-issues/${id}/signed-copy/download`, token);
}

export interface KkdZimmetListeSuzgeci {
  workplaceId: string | null;
  kalemId: string | null;
  ara: string;
  sayfa: number;
}

export const BOS_KKD_LISTE_SUZGECI: KkdZimmetListeSuzgeci = {
  workplaceId: null,
  kalemId: null,
  ara: '',
  sayfa: 1,
};

export function etkinKkdSuzgeciSayisi(s: KkdZimmetListeSuzgeci): number {
  return (s.workplaceId ? 1 : 0) + (s.kalemId ? 1 : 0) + (s.ara.trim() ? 1 : 0);
}

/** Çalışanlar sekmesi — *"kimde ne var"* (P4: sunucuda süzülür ve sayfalanır). */
export function useKkdZimmetListesi(s: KkdZimmetListeSuzgeci): UseQueryResult<KkdZimmetListesi> {
  const token = useAccessToken();
  const sorgu = new URLSearchParams();
  if (s.workplaceId) sorgu.set('workplaceId', s.workplaceId);
  if (s.kalemId) sorgu.set('kalemId', s.kalemId);
  if (s.ara.trim()) sorgu.set('ara', s.ara.trim());
  sorgu.set('sayfa', String(s.sayfa));
  const dizge = sorgu.toString();
  return useQuery({
    queryKey: ['kkd-liste', dizge],
    queryFn: ({ signal }) =>
      apiGet(`/api/ppe-issues?${dizge}`, token, kkdZimmetListesiSchema, signal),
  });
}

// ═══ DOKÜMAN YÖNETİMİ — V1 SADE RAF (Kapsam §31; 07.09.2026) ══════════════
// RAF, KONTROLLÜ BELGE ÇEKİRDEĞİNİN İKİNCİ MÜŞTERİSİDİR. Sunucu verisi yalnız
// burada; ekran ikinci süzgeç/yetki yazmaz (`silinebilir` · `yuklenebilirIsyerleri`
// sunucudan — §2.40).

export interface RafListeSuzgeciUi {
  sekme: RafSekmesi;
  workplaceId: string | null;
  ara: string;
  sayfa: number;
}

export function bosRafSuzgeci(sekme: RafSekmesi): RafListeSuzgeciUi {
  return { sekme, workplaceId: null, ara: '', sayfa: 1 };
}

export function etkinRafSuzgeciSayisi(s: RafListeSuzgeciUi): number {
  return (s.workplaceId ? 1 : 0) + (s.ara.trim() ? 1 : 0);
}

/** Sekme başına liste (P4: sunucuda süzülür ve sayfalanır; tanınmayan değer 400). */
export function useRafBelgeleri(s: RafListeSuzgeciUi): UseQueryResult<RafListesi> {
  const token = useAccessToken();
  const sorgu = new URLSearchParams({ sekme: s.sekme });
  if (s.workplaceId) sorgu.set('workplaceId', s.workplaceId);
  if (s.ara.trim()) sorgu.set('ara', s.ara.trim());
  sorgu.set('sayfa', String(s.sayfa));
  const dizge = sorgu.toString();
  return useQuery({
    queryKey: ['dokumanlar', dizge],
    queryFn: ({ signal }) => apiGet(`/api/documents?${dizge}`, token, rafListesiSchema, signal),
  });
}

function useRafTazele(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['dokumanlar'] });
    // Tek kayıt iki yüzey: Plan sekmesi acil durum belgesini de gösterir — o sayfa da tazelenir.
    void queryClient.invalidateQueries({ queryKey: ['acil-durum'] });
  };
}

/** Raf belgesi yükleme — multipart (dosya + tip + işyeri); yalnız PDF (sunucu 422). */
export function useRafBelgesiYukle(): UseMutationResult<
  RafBelgesi,
  Error,
  {
    dosya: File;
    tip: RafSekmesi;
    workplaceId: string;
    ad?: string;
    kimyasalAdi?: string;
    gbfDuzenlemeTarihi?: string;
  }
> {
  const token = useAccessToken();
  const tazele = useRafTazele();
  return useMutation({
    mutationFn: ({ dosya, tip, workplaceId, ad, kimyasalAdi, gbfDuzenlemeTarihi }) =>
      apiUpload('/api/documents', token, dosya, rafBelgesiSchema, {
        tip,
        workplaceId,
        // D2-3: yalnız Diğer sekmesinde serbest ad.
        ...(ad !== undefined ? { ad } : {}),
        // P2: yalnız GBF sekmesinde — kimyasal adı zorunlu, düzenleme tarihi opsiyonel.
        ...(kimyasalAdi !== undefined ? { kimyasalAdi } : {}),
        ...(gbfDuzenlemeTarihi ? { gbfDuzenlemeTarihi } : {}),
      }),
    onSuccess: tazele,
  });
}

export function useRafBelgesiSil(): UseMutationResult<{ silindi: true }, Error, { id: string }> {
  const token = useAccessToken();
  const tazele = useRafTazele();
  return useMutation({
    mutationFn: ({ id }) => apiDelete(`/api/documents/${id}`, token, silmeSonucuSchema),
    onSuccess: tazele,
  });
}

/** İndirme — raf VE acil durum belgesi aynı uçtan (tek kayıt iki yüzey). */
export function useRafBelgesiIndir(): (
  id: string,
) => Promise<{ blob: Blob; dosyaAdi: string | null }> {
  const token = useAccessToken();
  return (id) => apiDownload(`/api/documents/${id}/download`, token);
}
